import { i18n } from '../i18n.js';
import { getFoundry, setFoundry } from './foundry-instance.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { showAchievementBanner } from './banner.js';
import { MoonfallDiscoveryPalette } from '../discovery-palette.js';
import { MoonfallCloud } from '../convex-client.js';
import { creationRefusalLine } from './service-pause.js';
/**
 * Moon Foundry - Infinite Craft Style
 * Simple drag-and-drop merging without physics library
 */

import { AssetPreloader } from './asset-preloader.js';
import { CardDrag } from './card-drag.js';
import { Cards } from './cards.js';
import { CardTilt } from './card-tilt.js';
import { Confirm } from './confirm-dialog.js';
import { Firebase } from '../firebase-loader.js';
import { FoundryInventoryWarm } from './foundry-inventory-warm.js';
import { FoundryLifecycle } from './foundry-lifecycle.js';
import { FoundrySurface } from './foundry-surface.js';
import { OrientationGuard } from './orientation-guard.js';
import { Pagination } from './pagination.js';
import { SoundEffects } from './sound-effects.js';
import { Stardust } from './stardust.js';
import { announceStardust, lastKnownStardust, onStardustAnnounced } from './stardust-chip.js';
import { StellarAssets } from './stellar-assets.js';
import { StellarFirstBannerDelivery } from './stellar-first-banner-delivery.js';
import { TouchPreview } from './touch-preview.js';
import { cssLengthPx, uiCssPx, uiPx } from './ui-scale.js';
let FoundryWorkspaceReleasePromise = {};

const ART_DISMISS_LATCH_MS = 350;
const formatStardust = (value) => Stardust?.format?.(value) ?? String(value);

/**
 * The same viewport gate mobile.css uses, so copy and layout can never disagree
 * about what "a phone" is. Viewport-driven rather than pointer-driven for the
 * same reason the stylesheet is: a fine-pointer emulator at phone size must get
 * the phone treatment.
 */
function isCompactFoundry() {
    try {
        return window.matchMedia?.(
            '(orientation: portrait) and (max-width: 768px), '
            + '(orientation: landscape) and (max-height: 560px)'
        )?.matches === true;
    } catch (_) {
        return false;
    }
}

function formatEtaMinutes(minutes) {
    if (!Number.isFinite(minutes) || minutes <= 0) return null;
    if (minutes < 60) return uiText('common.minutesShort', `${minutes} min`, { count: minutes });
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (remainder <= 0) return uiText('common.hoursShort', `${hours}h`, { count: hours });
    return uiText('common.hoursMinutesShort', `${hours}h ${remainder}m`, { hours, minutes: remainder });
}

function formatEtaWindow(minMs, maxMs) {
    if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) return null;
    const minMinutes = Math.max(1, Math.round(Math.max(45_000, minMs) / 60_000));
    const maxMinutes = Math.max(minMinutes, Math.round(Math.max(45_000, maxMs) / 60_000));
    if (maxMinutes <= 1) {
        return uiText('common.aboutDuration', 'about {{duration}}', {
            duration: uiText('common.oneMinute', '1 min'),
        });
    }
    if (maxMinutes < 60) {
        if (minMinutes >= maxMinutes) {
            return uiText('common.aboutDuration', 'about {{duration}}', {
                duration: formatEtaMinutes(maxMinutes),
            });
        }
        return uiText('common.aboutDuration', 'about {{duration}}', {
            duration: uiText('common.minutesRangeShort', '{{min}}-{{max}} min', {
                min: minMinutes,
                max: maxMinutes,
            }),
        });
    }
    if (minMinutes >= maxMinutes) {
        return uiText('common.aboutDuration', 'about {{duration}}', {
            duration: formatEtaMinutes(maxMinutes),
        });
    }
    return uiText('common.aboutRange', 'about {{min}}–{{max}}', {
        min: formatEtaMinutes(minMinutes),
        max: formatEtaMinutes(maxMinutes),
    });
}

// The forging chip's countdown ticks twice a second so it never skips one,
// and a deadline a later poll moves by less than this is the same deadline:
// the network, not news, so the count never steps back a second.
const FORGE_TIMER_TICK_MS = 500;
const FORGE_TIMER_JITTER_MS = 1500;

/** m:ss, digits only: it reads the same in every language. */
function formatForgeCountdown(remainingMs) {
    const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/* The chip's count says it is an estimate: a bare "0:41" read as a
   stopwatch (owner, 28 Oct 2026: "Should be more clear moon fuse timer is
   eta"). */
function forgeEtaText(time) {
    return uiText('foundry.forgeTimer.eta', 'ETA {{time}}', { time });
}

function buildFoundryEtaLabel(data) {
    const eta = formatEtaWindow(data?.estimatedWaitMinMs, data?.estimatedWaitMaxMs);
    if (data?.queueStatus === 'queued') {
        return eta
            ? uiText('foundry.queue.stirsWithEta', 'The forge stirs. {{eta}}...', { eta })
            : uiText('foundry.queue.stirs', 'The forge stirs...');
    }
    if (data?.queueStatus === 'running') {
        return eta
            ? uiText('foundry.queue.blazesWithEta', 'The forge blazes. {{eta}}...', { eta })
            : uiText('foundry.queue.blazes', 'The forge blazes...');
    }
    return null;
}

function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    const text = translated && translated !== key ? translated : fallback;
    /* BOTH brace styles, double FIRST.
       The copy uses `{{name}}` (as ui.json writes it) and, in a few
       places, single `{name}`. A helper that knows only one leaves the
       other on screen whenever i18n has not resolved. Order matters:
       substituting the single form first turns `{{count}}` into `{3}`,
       which is exactly how starter-reward.js was rendering it. */
    return Object.entries(replacements || {}).reduce(
        (result, [name, value]) => String(result)
            .split(`{{${name}}}`).join(String(value))
            .split(`{${name}}`).join(String(value)),
        String(text),
    );
}

function normalizeFoundryArtSource(rawSource) {
    if (typeof rawSource !== 'string') return '';
    const source = rawSource.trim();
    if (!source) return '';
    try {
        const url = new URL(source, window.location?.href || 'http://localhost/');
        if (!['http:', 'https:', 'data:', 'blob:'].includes(url.protocol)) return '';
        let pathname = url.pathname;
        for (let count = 0; count < 4; count += 1) {
            const decoded = decodeURIComponent(pathname);
            if (decoded === pathname) break;
            pathname = decoded;
        }
        if (pathname === '/assets/art_placeholder.webp') return '';
        return source;
    } catch (_) {
        return '';
    }
}

function loadFoundryArtImage(image, rawSource, profile = {}) {
    if (!image) return false;
    Cards?.disposeCardArtImage?.(image);
    window.MoonfallRetryingImage?.dispose?.(image, { clearSource: true });
    delete image.dataset.foundryExactSource;
    const source = normalizeFoundryArtSource(rawSource);
    if (!source) {
        image.removeAttribute('src');
        image.classList.add('card-art-unavailable');
        return false;
    }

    image.dataset.foundryExactSource = source;
    const cards = Cards;
    if (typeof cards?.loadCardArtImage === 'function') {
        void cards.loadCardArtImage(image, source, profile, { directArtOnly: true });
        return true;
    }
    if (typeof window.MoonfallRetryingImage?.load === 'function') {
        window.MoonfallRetryingImage.load(image, source);
        return true;
    }

    image.setAttribute('src', source);
    if (typeof cards?.enhanceCardArt === 'function') {
        void cards.enhanceCardArt(image, source, profile);
    }
    return false;
}

function disposeFoundryArtImages(root) {
    if (!root) return;
    root.querySelectorAll?.('img, canvas.card-art-paint').forEach((paint) => {
        Cards?.disposeCardArtImage?.(paint);
        if (paint.tagName === 'IMG') window.MoonfallRetryingImage?.dispose?.(paint);
    });
}

async function waitForBoundedCardWarm(warm) {
    if (!warm || typeof warm.then !== 'function') return;
    const gateWarm = AssetPreloader?.gateWarm;
    if (typeof gateWarm !== 'function') {
        // The decode stays best-effort when the bounded gate is unavailable;
        // synchronous final-size settlement remains authoritative.
        void Promise.resolve(warm).catch(() => undefined);
        return;
    }
    try {
        await gateWarm(warm);
    } catch (_) {
        // Final-size raster settlement below decides whether reveal is safe.
    }
}

function awaitFoundryRequestStage(value, signal) {
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (callback, result) => {
            if (settled) return;
            settled = true;
            signal?.removeEventListener?.('abort', onAbort);
            callback(result);
        };
        // The abort's own reason when it is ours, so a timeout reaches the
        // caller as a timeout and not as the player leaving.
        const onAbort = () => {
            finish(reject, signal?.reason?.name === 'AbortError' ? signal.reason : createFoundryAbortError());
        };
        if (signal?.aborted) {
            /* Still LISTEN to the promise we were handed. A stage reached after
               the abort — the fetch stage resolved, the timeout landed, and the
               executor continued into `response.json()` — hands over a promise
               that rejects natively on the aborted request. Returning without
               observing it left that rejection to nobody: seen on production
               as two uncaught "AbortError: signal is aborted without reason"
               from a hidden, frozen tab whose request timeout and response
               were delivered together on resume. */
            Promise.resolve(value).then(() => undefined, () => undefined);
            onAbort();
            return;
        }
        signal?.addEventListener?.('abort', onAbort, { once: true });
        Promise.resolve(value).then(
            (result) => finish(resolve, result),
            (error) => finish(reject, error),
        );
    });
}

function createFoundryAbortError(message = 'Foundry request aborted', { timedOut = false } = {}) {
    const error = /** @type {Error & { timedOut?: boolean }} */ (new Error(message));
    error.name = 'AbortError';
    if (timedOut) error.timedOut = true;
    return error;
}

/** A bounded read that ran out of time, as opposed to one cancelled. */
function isFoundryTimeout(error) {
    return error?.name === 'AbortError' && error.timedOut === true;
}

function hasDecodedStandaloneArt(image) {
    return Number(image?.naturalWidth) > 0 && Number(image?.naturalHeight) > 0;
}

function formatCooldownDuration(ms) {
    const durationMs = Number(ms);
    if (!Number.isFinite(durationMs) || durationMs <= 0) return '';
    const minutes = Math.ceil(durationMs / 60_000);
    if (minutes <= 1) return uiText('common.underMinute', 'under a minute');
    if (minutes < 60) {
        return uiText('common.minutesShort', '{{count}} min', { count: minutes });
    }
    const hours = Math.ceil(minutes / 60);
    if (hours < 24) {
        return uiText('common.hoursShort', '{{count}}h', { count: hours });
    }
    const days = Math.ceil(hours / 24);
    return uiText('common.daysShort', '{{count}}d', { count: days });
}

function buildCreationCooldownMessage(data) {
    const base = data?.error || uiText(
        'foundry.creationRateLimited',
        'Too many failed AI creations. Please wait before starting another creation.'
    );
    const wait = formatCooldownDuration(data?.retryAfterMs);
    if (!wait) return base;
    return uiText('foundry.creationRateLimitedWithWait', `${base} Try again in ${wait}.`, {
        message: base,
        wait,
    });
}

function hasRecoverableFoundryProgress(data) {
    if (!data || typeof data !== 'object') return false;
    if (data.status === 'failed') return false;
    if (data.status === 'complete' || data.resolvedCardId) return true;
    if (data.queueStatus === 'queued' || data.queueStatus === 'running') return true;
    if (typeof data.name === 'string' && data.name && data.name !== 'Unknown') return true;
    if (typeof data.selectedArtIndex === 'number' || Boolean(data.imageKey)) return true;
    const variations = Array.isArray(data.artVariations) ? data.artVariations : [];
    return variations.some((art) => (
        art?.status === 'complete' && Boolean(normalizeFoundryArtSource(art?.url))
    ));
}

/* The Collection presents pages it read in the last minute from its page
   cache. Found 25 September 2026: a card discovered here was missing from its
   next Cards view until a revalidation morphed it in. Each point at which a
   card lands in the player's inventory announces it; the Collection drops
   every page read before (collection.js setupHoldingsFollow). */
function announceHoldingsChanged() {
    window.dispatchEvent(new CustomEvent('moonfall:holdings-changed', { detail: { source: 'foundry' } }));
}

const FOUNDRY_CRASH_JOURNAL_KEY = 'moonfall_foundry_crash_journal_v1';
const FOUNDRY_CRASH_JOURNAL_TTL_MS = 24 * 60 * 60 * 1000;
const FOUNDRY_WORKSPACE_LEASE_KEY = 'moonfall_foundry_workspace_lease_v1';
const FOUNDRY_WORKSPACE_LEASE_RENEW_MS = 15_000;
const MAX_FOUNDRY_WORKSPACE_MIGRATION_PAGES = 32;
const MAX_FOUNDRY_RECOVERY_PAGES_PER_PASS = 4;
const FOUNDRY_RECOVERY_PAGE_TIMEOUT_MS = 8_000;
// The hidden tab's unseen preparation shares the surface's first-view cap.
const HIDDEN_FIRST_VIEW_CAP_MS = 4_000;
/* THE INVENTORY IS EVERY CARD THE PLAYER OWNS (read audit R7 and R19, 26
   September 2026). A tab switch or a page return re-read it in full every
   time; now only a read older than this, or one a Stardust or holdings
   announcement made stale, is repeated. The lobby's warm of the same list
   lives as long (FoundryInventoryWarm.WARM_TTL_MS). */
const INVENTORY_FRESH_MS = 20_000;
/* How long a tab entered while the lobby's list is still on its way waits for
   it before reading its own: the tab hand-over's cap (foundry-tabs.js), where
   the tab is shown with its list loading anyway. That list has been out since
   the tile was touched, so it usually lands first; one that stalls is bounded
   only by its own 8 s abort, and the tab never waits on it that long. */
const INVENTORY_ADOPT_WAIT_MS = 1_500;
/* How long the canvas read unseen while Moon Dream shows (peekCanvas) is
   trusted as the server's answer at the first press on Moon Fuse: the
   inventory's own window. Older, the press waits for its own open, and intent
   on the tab reads it again. */
const CANVAS_PEEK_FRESH_MS = 20_000;
/* How long a projection waits for the state of the discoveries it paints
   (applyWorkspaceSnapshot): the readiness cap docs/asset-loading.md models on
   the mulligan reveal. A slower read paints the discovery as underway and its
   poll takes the answer up. */
const DISCOVERY_FIRST_READ_CAP_MS = 1_500;
const MAX_FOUNDRY_WORKSPACE_ELEMENTS = 150;

function finiteLayoutNumber(value, fallback = 0) {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
}

function calculateInventoryPageCapacity({
    width,
    height,
    paddingInline = 0,
    paddingBlock = 0,
    columnGap = 0,
    rowGap = 0,
    itemMinWidth = 1,
    itemHeight = 1,
} = {}) {
    const innerWidth = Math.max(0, finiteLayoutNumber(width) - finiteLayoutNumber(paddingInline));
    const innerHeight = Math.max(0, finiteLayoutNumber(height) - finiteLayoutNumber(paddingBlock));
    const minWidth = Math.max(1, finiteLayoutNumber(itemMinWidth, 1));
    const minHeight = Math.max(1, finiteLayoutNumber(itemHeight, 1));
    const horizontalGap = Math.max(0, finiteLayoutNumber(columnGap));
    const verticalGap = Math.max(0, finiteLayoutNumber(rowGap));
    if (innerWidth < 1 || innerHeight < 1) return 0;
    const columns = Math.max(1, Math.floor((innerWidth + horizontalGap) / (minWidth + horizontalGap)));
    const rows = Math.max(1, Math.floor((innerHeight + verticalGap) / (minHeight + verticalGap)));
    return columns * rows;
}

const CANVAS_CHIP_GAP_PX = 4;
// A chip's nearest free spot is local, so its candidates come from the chips
// nearest it; the cap keeps a 150-chip canvas from sorting ~90k spots per chip.
const CANVAS_SPOT_NEIGHBOURS = 24;

function canvasSpotIsClear({ w, h }, spotX, spotY, placed, margin) {
    return placed.every((other) => (
        spotX + w + margin <= other.x || other.x + other.w + margin <= spotX
        || spotY + h + margin <= other.y || other.y + other.h + margin <= spotY
    ));
}

/**
 * Where a w x h chip at (x, y) can rest without overlapping any `placed` chip
 * inside a width x height canvas: its own spot when that overlaps nothing,
 * else the nearest spot that keeps CANVAS_CHIP_GAP_PX from every chip, else
 * null. A nearest free spot has each coordinate at the chip's own, at a canvas
 * edge, or flush against a neighbour, so those are the only candidates.
 */
function findFreeCanvasSpot({ x, y, w, h }, placed, width, height) {
    const clearOf = (spotX, spotY, margin) => canvasSpotIsClear({ w, h }, spotX, spotY, placed, margin);
    if (clearOf(x, y, 0)) return { x, y };
    const maxX = Math.max(0, width - w);
    const maxY = Math.max(0, height - h);
    const xs = new Set([x, 0, maxX]);
    const ys = new Set([y, 0, maxY]);
    placed
        .map((other) => ({ other, distance: Math.hypot(other.x - x, other.y - y) }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, CANVAS_SPOT_NEIGHBOURS)
        .forEach(({ other }) => {
            xs.add(other.x + other.w + CANVAS_CHIP_GAP_PX);
            xs.add(other.x - w - CANVAS_CHIP_GAP_PX);
            ys.add(other.y + other.h + CANVAS_CHIP_GAP_PX);
            ys.add(other.y - h - CANVAS_CHIP_GAP_PX);
        });
    const spots = [];
    xs.forEach((spotX) => {
        if (spotX < 0 || spotX > maxX) return;
        ys.forEach((spotY) => {
            if (spotY < 0 || spotY > maxY) return;
            spots.push({ x: spotX, y: spotY, distance: Math.hypot(spotX - x, spotY - y) });
        });
    });
    spots.sort((a, b) => a.distance - b.distance);
    const spot = spots.find((candidate) => clearOf(candidate.x, candidate.y, CANVAS_CHIP_GAP_PX));
    return spot ? { x: spot.x, y: spot.y } : null;
}

/**
 * The top-most, then left-most spot where a w x h chip overlaps no `placed`
 * chip: first-fit, the way a crowded shelf is tidied. Null when none is free.
 */
function findFirstFreeCanvasSpot(chip, placed, width, height) {
    const maxX = width - chip.w;
    const maxY = height - chip.h;
    const ys = [...new Set([0, ...placed.map((other) => other.y + other.h + CANVAS_CHIP_GAP_PX)])]
        .filter((spotY) => spotY <= maxY)
        .sort((a, b) => a - b);
    for (const spotY of ys) {
        // Only chips level with this row can block it, and a free spot starts
        // at the canvas edge or just past one of them.
        const level = placed.filter((other) => spotY + chip.h + CANVAS_CHIP_GAP_PX > other.y
            && other.y + other.h + CANVAS_CHIP_GAP_PX > spotY);
        const spotX = [0, ...level.map((other) => other.x + other.w + CANVAS_CHIP_GAP_PX)]
            .filter((candidate) => candidate <= maxX)
            .sort((a, b) => a - b)
            .find((candidate) => canvasSpotIsClear(chip, candidate, spotY, level, CANVAS_CHIP_GAP_PX));
        if (spotX !== undefined) return { x: spotX, y: spotY };
    }
    return null;
}

function createFoundryLeaseToken() {
    if (typeof window?.crypto?.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function buildFoundryAccountStorageKey(user) {
    const uid = String(user?.uid || '').trim();
    if (!uid) return '';
    // This partition key is needed synchronously during first paint. Four
    // independently seeded 32-bit lanes keep it opaque while making the
    // collision risk negligible for the handful of accounts on a device.
    const hashes = [0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35];
    for (let index = 0; index < uid.length; index += 1) {
        const code = uid.charCodeAt(index);
        for (let lane = 0; lane < hashes.length; lane += 1) {
            hashes[lane] ^= code;
            hashes[lane] = Math.imul(hashes[lane], 0x01000193) >>> 0;
        }
    }
    const fingerprint = hashes
        .map((hash) => hash.toString(16).padStart(8, '0'))
        .join('');
    return `account_v2_${fingerprint}`;
}

function createFoundryCanvasElementId() {
    if (typeof window?.crypto?.randomUUID === 'function') {
        return `element_${window.crypto.randomUUID()}`;
    }
    return `element_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

class MoonFoundry {
    constructor({ root = document, user = null, onClose = null, viewState = null } = {}) {
        this.root = root;
        this.user = user;
        this.onClose = typeof onClose === 'function' ? onClose : null;
        this.destroyed = false;
        this.destroying = false;
        this.eventController = new window.AbortController();
        this.authRetryTimer = null;
        this.authUnsubscribe = null;
        this.authAttemptGeneration = 0;
        this.authRequestControllers = new Set();
        this.accountBoundaryGeneration = 0;
        this.surfaceTimers = new Set();
        this.playArea = this.getElementById('play-area');
        this.canvasElements = this.getElementById('canvas-elements');
        this.elementList = this.getElementById('element-list');
        this.searchInput = this.getElementById('search-input');
        this.stardustCountEl = this.getElementById('stardust-count');
        this.fuseCostEl = this.getElementById('moon-fuse-cost');
        this.trashZone = this.getElementById('trash-zone');
        this.lobbyBtn = this.getElementById('foundry-lobby-btn');
        this.workspaceNotice = this.getElementById('foundry-workspace-notice');
        this.workspaceNoticeText = this.getElementById('foundry-workspace-notice-text');
        this.workspaceTakeOverBtn = this.getElementById('foundry-workspace-takeover');

        this.elements = []; // Elements on canvas
        this.forgeCosts = null;
        this.canvasMutationVersion = 0;
        this.inventory = []; // All available elements
        this.inventoryById = new Map();
        this.firstDiscoveredCardIds = new Set();
        this.cardDetailCache = new Map();
        this.draggedEl = null;
        this.pendingCanvasDrag = null; // touch press awaiting movement before it becomes a drag
        this.dragPointerId = null;
        this.dragPointerCaptureEl = null;
        this.offsetX = 0;
        this.offsetY = 0;
        this.pendingForges = new Map();
        this.materializedOperationIds = new Set();
        this.forgePollTimers = new Map();
        this.forgeTimerInterval = null;
        this.artQueue = [];
        // Operations whose chooser the participant explicitly closed. Their
        // canvas chip stays awaiting-art-selection and a tap reopens it, but
        // polling must not force the modal back open.
        this.dismissedArtOperations = new Set();
        this.activeArtModal = false;
        this.activeArtModalOperationId = null;
        this.activeArtModalComplete = null;
        this.artModalReconcileInFlight = null;
        this.artModalReconcileLocked = false;
        this.artModalReconcileOperationId = null;
        this.artModalReconcileTimer = null;
        this.artSelectionRequestControllers = new Map();
        this.artDismissLatchUntil = 0;
        this.artModalInertRecords = [];
        this.artModalPreviousFocus = null;
        this.artModalFocusOperationId = null;
        this.awaitingArtSelectionA11y = new WeakMap();
        this.pendingMergePairs = new Set();
        this.artReadySoundPlayed = new Set();
        this.lastCreationCooldownNoticeAt = 0;
        this.hoverTimers = new Map();
        // Per-chip "open the enlarged card" callback, so a tap can reach the
        // same popup the hover path builds instead of duplicating it. Weak, so
        // a removed chip needs no teardown to stop being referenced.
        this.chipPreviewOpeners = new WeakMap();
        this.hoverDelayMs = 600;
        this.sidebarDrag = null;
        this.inventoryPageSize = 200;
        this.inventoryRenderedPageSize = null;
        this.inventoryPage = 1;
        this.inventoryScrollTop = 0;
        this.inventoryCacheKey = '';
        this.filteredInventory = [];
        this.inventoryVersion = 0;
        // Set while the PAGE is hidden, so the return can re-read (see
        // resumeAfterPageHidden). A tab switch uses its own lifecycle path.
        this.hiddenWhileOpen = false;
        this.inventoryLoaded = false;
        this.inventorySearch = '';
        /* A COLLECTION TOO LARGE TO LIST WHOLE (card-scale, 21 October 2026):
           the server answers the inventory a page at a time, sorted and
           searched by name as this surface does it (`mode: 'paged'`). Then
           the list on hand is one page, and search and the pager ask again. */
        this.inventoryPaged = false;
        this.inventoryPagedInfo = null;
        this.inventorySearchTimer = null;
        this.inventoryResizeRead = null;
        this.inventoryIndexWaits = 0;
        this.hasRestoredViewState = false;
        this.inventoryLoadSeq = 0;
        this.operationRequestTimeoutMs = 15_000;
        this.surfaceRequestControllers = new Set();
        this.sidebarPrev = this.getElementById('sidebar-prev');
        this.sidebarNext = this.getElementById('sidebar-next');
        this.sidebarPageInfo = this.getElementById('sidebar-page-info');
        this.workspacePersistDelayMs = 600;
        this.workspaceSaveTimer = null;
        this.workspaceSaveInFlight = null;
        this.workspaceSaveInFlightHash = '';
        this.workspaceSaveInFlightNoticeOwners = [];
        this.workspacePendingSaveRequest = null;
        this.workspaceSaveDrainPromise = null;
        this.workspaceSaveRequestControllers = new Set();
        this.workspaceLeaseRequestControllers = new Set();
        this.workspaceSaveBlockedByConflict = false;
        this.workspaceOpenInFlight = null;
        this.workspaceTakeOverInFlight = false;
        this.workspaceOpenRetryTimer = null;
        this.workspaceRenewTimer = null;
        this.workspaceRecoveryTimer = null;
        this.workspaceRecoveryInFlight = null;
        this.workspaceRecoveryRequestControllers = new Set();
        this.workspaceRecoveryOverflow = false;
        this.workspaceNoticeReasons = new Map();
        this.workspaceRevision = 0;
        this.workspaceUpdatedAt = 0;
        this.workspaceAuthorityGeneration = 0;
        this.workspaceProjectionGeneration = 0;
        this.workspaceRecoveryState = null;
        this.workspaceMode = 'read_only';
        this.workspaceLeaseToken = this.getOrCreateWorkspaceLeaseToken();
        this.workspaceLeaseExpiresAt = 0;
        this.workspaceCanvasWidth = 0;
        this.workspaceCanvasHeight = 0;
        this.canvasChipSeparationPending = false;
        this.lastSavedWorkspaceHash = '';
        this.workspacePersistenceSuspended = false;
        this.workspaceLoaded = false;
        this.workspaceProjected = false;
        this.accountStorageKey = '';
        this.surfaceActive = this.readFuseSurfaceActive();
        this.surfacePausedAt = this.surfaceActive ? null : Date.now();
        this.deferredAchievementBanners = [];
        this.deferredUserNotices = [];
        this.achievementConsumePending = false;
        this.achievementConsumeInFlight = null;
        this.unsubscribeSurfaceLifecycle = FoundryLifecycle?.subscribe?.((active) => {
            this.handleSurfaceVisibility(active && this.readFuseSurfaceActive());
        }) || null;
        this.unsubscribeTabLifecycle = FoundryLifecycle?.subscribeTab?.(() => {
            this.handleSurfaceVisibility(this.readFuseSurfaceActive());
        }) || null;

        /* THE FIRST VIEW. FoundrySurface keeps the surface unpainted until
           this settles (bounded there), so the frame the player first sees
           already carries the inventory, the balance, the price and the
           canvas. It used to show at once and then fill in over ~800 ms:
           24 rows popping in, "Loading fusion prices..." becoming "50 ✦ /
           discovery", the pill jumping 0 -> 5K (snappy meter, 2026-09-24). */
        this.firstView = { settled: false, resolve: null, promise: null };
        this.firstView.promise = new Promise((resolve) => { this.firstView.resolve = resolve; });
        this.firstProjection = { settled: false, resolve: null, promise: null };
        this.firstProjection.promise = new Promise((resolve) => {
            this.firstProjection.resolve = resolve;
        });
        this.inventorySignature = '';
        this.hiddenPrepareRequested = false;
        this.hiddenFirstViewStarted = false;
        this.entryPreparing = false;
        /* The canvas as the server answered a read made unseen while Moon
           Dream shows (peekCanvas), and that read while it is out: `read`
           settles with its answer, `whole` once its lease is handed back. */
        this.canvasPeek = null;
        this.canvasPeekInFlight = null;
        this.hiddenReadControllers = new Set();
        /* The inventory on hand (inventoryNeedsRead): when it was read, in
           which language, and how many announcements that may have moved it
           (`inventoryChanges`) had been heard when it was requested. */
        this.inventoryReadAt = null;
        this.inventoryReadLang = null;
        this.inventoryChanges = 0;
        this.inventoryReadChanges = 0;
        /* What is on the wire for it (readInventoryForSurface): this
           instance's own requests, counted, and the lobby's list being taken
           over, which makes no request and can end with nothing. */
        this.inventoryReadsInFlight = 0;
        this.inventoryAdoption = null;

        // What this surface already knows goes on the first frame: the figure
        // the lobby chip holds (the read below confirms it) and prices warmed
        // ahead of the press. Unknown prices print their localized loading
        // line rather than the template's English.
        const knownBalance = lastKnownStardust();
        if (knownBalance !== null) {
            this.updateStardustDisplay(knownBalance);
        } else if (this.stardustCountEl) {
            // Unknown looks unknown (the Collection's rule): the template's
            // "0" read as a real balance until the read corrected it.
            this.stardustCountEl.removeAttribute('data-count-value');
            this.stardustCountEl.textContent = '\u2014';
        }
        const warmedCosts = FoundryInventoryWarm?.peekConfig?.();
        if (warmedCosts) this.forgeCosts = warmedCosts;
        this.renderForgeCosts();

        this.captureWorkspaceCanvasMetrics();
        this.restoreViewState(viewState);
        this.init();
    }

    /** Resolves once the first view's reads have landed (or failed, or the instance was torn down). */
    whenFirstViewReady() {
        return this.firstView.promise;
    }

    isFirstViewReady() {
        return this.firstView.settled;
    }

    settleFirstView() {
        if (this.firstView.settled) return;
        this.firstView.settled = true;
        this.firstView.resolve();
    }

    settleFirstProjection() {
        if (this.firstProjection.settled) return;
        this.firstProjection.settled = true;
        this.firstProjection.resolve();
    }

    /**
     * The surface was laid out and read while unpainted; the body class that
     * presents it can still nudge the chrome it sits in. Re-measure in the
     * same task that reveals it, so the first painted frame is the final one.
     */
    refitToPresentedLayout() {
        if (this.destroyed) return;
        this.reprojectCanvasElementsToCurrentSize();
        if (!this.inventoryLoaded || !this.inventoryRenderedPageSize || !this.isInventoryPanelActive()) return;
        if (this.measureInventoryPageCapacity() === this.inventoryRenderedPageSize) return;
        this.renderSidebar({ preservePage: true, preserveLogicalOffset: true });
    }

    /**
     * THE HIDDEN TAB'S FIRST VIEW. Opened on Moon Dream, the surface has this
     * tab read the small things it shows — the balance and the prices — and
     * lay them out unseen (`data-foundry-preparing`, foundry.css). One read
     * each, bounded, never repeated. Not the inventory: it is every card the
     * player owns, read here on every open on Moon Dream and mostly thrown
     * away (read audit R7, 26 September 2026: "it's fine to load things, but
     * not a players entire library"). It is read on the player's intent to
     * enter this tab — a hover, focus or touch on it (warmEntryInventory) —
     * or by the press itself, beside the canvas. A list the lobby already
     * read on the tile's own intent (FoundryInventoryWarm) is adopted and laid
     * out here, since no request is made for it: otherwise that read was
     * thrown away — the first balance any tab announces drops it — and the
     * first switch paid for the list again beside the canvas (Chrome, iPhone
     * 13 landscape, EU 4G: 0.72 s became 1.07 s). And the canvas, read as
     * the server holds it and its lease handed straight back (peekCanvas), so
     * the first press can show the server's answer instead of waiting for it.
     */
    prepareFirstView() {
        if (this.destroyed || this.surfaceActive || this.hiddenPrepareRequested) return;
        this.hiddenPrepareRequested = true;
        this.markPanelPreparing(true);
        if (this.user) void this.readHiddenFirstView();
    }

    async readHiddenFirstView() {
        if (!this.hiddenPrepareRequested || this.hiddenFirstViewStarted) return;
        if (this.destroyed || this.surfaceActive || !this.user) return;
        this.hiddenFirstViewStarted = true;
        let capTimer = null;
        try {
            await Promise.race([
                Promise.allSettled([
                    this.loadStardust({ whileHidden: true }),
                    this.loadForgeCosts({ whileHidden: true }),
                    this.loadInventory({
                        preserveView: this.hasRestoredViewState,
                        showLoading: false,
                        whileHidden: true,
                        warmOnly: true,
                    }),
                    // While the panel is laid out: the read carries the canvas's real size.
                    this.peekCanvas(),
                ]),
                new Promise((resolve) => { capTimer = setTimeout(resolve, HIDDEN_FIRST_VIEW_CAP_MS); }),
            ]);
        } finally {
            clearTimeout(capTimer);
            if (!this.surfaceActive) this.markPanelPreparing(false);
        }
    }

    /**
     * THE CANVAS, READ UNSEEN (snappy round 15, 28 September 2026). Opened on
     * Moon Dream, the first press on Moon Fuse waited for the workspace read
     * the press sent — 0.61-0.89 s on every seat against the baseline's
     * 0.10 s, for an empty canvas — because only the server's answer is shown
     * as final (a device-local "last seen empty" hint showed another device's
     * chips popping in after the switch).
     *
     * No deployed Convex function reads the canvas without its lease (the
     * GDPR export of the whole account is the only query that returns it), so
     * this is the workspace open itself, and the lease is handed straight
     * back: the tab on screen is Moon Dream, and another device entering Moon
     * Fuse must find the canvas free. A canvas another device holds is
     * answered read-only and never taken.
     *
     * THE READ HOLDS A LEASE OF ITS OWN: a one-time token, never this tab's
     * (adversarial review of wave D, 28 September 2026). A hand-back's answer
     * can be lost while the request still reaches the server; naming the
     * tab's own token, one that landed after the press's open released the
     * press's lease, and the tab went on editing until its next save was
     * refused and it turned read-only. A hand-back of this token can only
     * ever release this read's lease, whenever it lands. A press that comes
     * before any hand-back was sent takes the read's lease over (its open
     * names this token); otherwise the press's open waits for the hand-back
     * (openWorkspace), which is sent once more when its answer was lost, so
     * the press finds the canvas free.
     *
     * Only with the canvas laid out: a first open may run the legacy
     * migration, which converts pixel positions by the size it is sent.
     * Returns the promise of the answer, or null when nothing is read.
     */
    peekCanvas() {
        if (this.canvasPeekInFlight) return this.canvasPeekInFlight.read;
        if (
            this.destroyed
            || !this.user
            || this.surfaceActive
            || this.entryPreparing
            || this.workspaceLoaded
            || this.workspaceProjected
            || this.workspaceOpenInFlight
            || this.workspaceTakeOverInFlight
        ) return null;
        this.captureWorkspaceCanvasMetrics();
        if (!(this.workspaceCanvasWidth > 1 && this.workspaceCanvasHeight > 1)) return null;
        let markRead = null;
        const read = new Promise((resolve) => { markRead = resolve; });
        const entry = { read, whole: null };
        entry.whole = this.runCanvasPeek(markRead);
        this.canvasPeekInFlight = entry;
        void entry.whole.finally(() => {
            if (this.canvasPeekInFlight === entry) this.canvasPeekInFlight = null;
        });
        return read;
    }

    async runCanvasPeek(markRead) {
        const user = this.user;
        const accountStorageKey = this.accountStorageKey;
        // The read's own lease (peekCanvas): never the token this tab edits under.
        const leaseToken = createFoundryLeaseToken();
        const dimensions = this.getLegacyCanvasDimensions();
        let payload = null;
        try {
            payload = await this.runBoundedRequest(async (signal) => {
                // A lease the Foundry closed before this one is still handing back.
                const pendingRelease = FoundryWorkspaceReleasePromise;
                if (pendingRelease) await awaitFoundryRequestStage(pendingRelease, signal);
                const token = await awaitFoundryRequestStage(user.getIdToken(), signal);
                return await this.requestWorkspaceOpen(token, dimensions, signal, leaseToken);
            }, { abortOnHide: false });
        } catch (err) {
            if (err?.name !== 'AbortError') console.warn('[Foundry] The unseen canvas read failed:', err);
        }
        const current = !this.destroyed
            && this.user === user
            && this.accountStorageKey === accountStorageKey;
        if (payload && current) {
            this.canvasPeek = {
                accountStorageKey,
                elements: this.normalizeWorkspaceElements(payload.elements),
                mode: payload.mode === 'writer' ? 'writer' : 'read_only',
                migrationPending: payload.migrationPending === true,
                readAt: Date.now(),
            };
        }
        markRead();
        // A press that came while this read was out shows its answer as soon as it lands.
        if (current && this.entryPreparing) void this.projectCanvasPeek();
        const entering = current && (this.surfaceActive || this.entryPreparing);
        if (entering && payload?.mode === 'writer') {
            /* The press has come, and nothing was handed back: its open, which
               waits for this, takes the read's lease over. */
            this.adoptWorkspaceLease(leaseToken);
            return;
        }
        /* Handed back otherwise. A read that failed may still have taken the
           lease on the server; releasing a lease this token does not hold
           changes nothing there. A hand-back whose answer was lost is sent
           once more: it can only name this read's lease. */
        if (!payload || payload.mode === 'writer') {
            if (!await this.sendWorkspaceLeaseRelease(user, leaseToken)) {
                await this.sendWorkspaceLeaseRelease(user, leaseToken);
            }
        }
    }

    /** The unseen read is recent enough to stand for the canvas. */
    canvasPeekIsFresh() {
        const peek = this.canvasPeek;
        if (!peek || peek.accountStorageKey !== this.accountStorageKey) return false;
        const age = Date.now() - peek.readAt;
        return age >= 0 && age < CANVAS_PEEK_FRESH_MS;
    }

    /**
     * The unseen read, when the first press may show it as the canvas: fresh,
     * this tab could have edited it (a canvas another device holds waits for
     * the press's own open, whose answer carries the read-only notice), no
     * legacy migration still pending, and no unsaved work of this device's
     * that the press's open would restore over it.
     */
    usableCanvasPeek() {
        if (!this.canvasPeekIsFresh()) return null;
        const peek = this.canvasPeek;
        if (peek.mode !== 'writer' || peek.migrationPending) return null;
        const journal = this.readCrashJournal();
        if (journal && !this.isSameWorkspaceSnapshot(journal.elements, peek.elements)) return null;
        return peek;
    }

    /**
     * The first press shows the canvas the unseen read found, projected in the
     * press's own task (never a stub chip: the projection waits for the list
     * it names, as every projection does). The press's open follows with the
     * lease and reconciles chip by chip; until it lands the canvas is not
     * editable (canEditWorkspace), exactly as before a first open.
     */
    async projectCanvasPeek() {
        if (this.destroyed || !this.surfaceActive || this.workspaceProjected || this.workspaceLoaded) return false;
        const peek = this.usableCanvasPeek();
        if (!peek) return false;
        const projected = await this.applyWorkspaceSnapshot(peek.elements);
        /* A press taken back while the list this projection waited for was
           still coming leaves the tab unseen: its next entry projects afresh
           (or waits for its own open), never this read, by then of any age,
           as the canvas. */
        if (projected && !this.surfaceActive && !this.workspaceLoaded) {
            this.workspaceProjected = false;
            return false;
        }
        return projected;
    }

    /** Intent on Moon Fuse: a canvas read gone stale is read again, unseen. */
    refreshCanvasPeek() {
        if (this.canvasPeekInFlight || this.canvasPeekIsFresh()) return;
        void this.peekCanvas();
    }

    markPanelPreparing(preparing) {
        const panel = this.elementList?.closest?.('.foundry-panel');
        if (!panel) return;
        const panels = panel.parentElement;
        if (preparing) {
            panel.setAttribute('data-foundry-preparing', 'true');
            panels?.setAttribute('data-foundry-preparing', 'true');
            return;
        }
        panel.removeAttribute('data-foundry-preparing');
        panels?.removeAttribute('data-foundry-preparing');
    }

    /**
     * THE PRESS OPENS THE CANVAS. Opened on Moon Dream, this tab prepares its
     * balance and prices unseen, but not its canvas: the workspace lease
     * belongs to the tab on screen. A press on the Moon Fuse tab is the player
     * choosing it, so the workspace is opened at the press — with the
     * inventory beside it when intent has not read it already — while the
     * panel stays unseen (`data-foundry-preparing`), and FoundryTabs keeps
     * Moon Dream up until the chips are projected (capped there at 1.5 s, its
     * one loading state past the grace). The owner (26 September 2026),
     * "switching from moon dream screen to moon fuse": the tab used to arrive
     * with an empty canvas and the chips popped in after its read.
     *
     * EVERY FIRST ENTRY WAITS FOR A SERVER ANSWER ABOUT THE CANVAS. A
     * device-local hint of what the canvas last showed here ('empty') let the
     * tab swap in at the press, and when another device had put chips on this
     * account's canvas since, they popped in after the switch (26 September
     * 2026 review). The canvas is the server's; only its answer is shown as
     * final — the press's own open, or the one read unseen while Moon Dream
     * showed (peekCanvas), when it is fresh: that one is projected here, in
     * the press's task, and the switch is a swap again (round 15: 0.61-0.89 s
     * against the baseline's 0.10 s without it). A canvas already projected
     * needs nothing: the switch is a swap and its resume read reconciles.
     */
    prepareEntry() {
        if (this.destroyed || this.surfaceActive || !this.user || this.workspaceProjected) return;
        this.entryPreparing = true;
        this.markPanelPreparing(true);
        // An inventory intent read unseen is laid out now, in the box the tab will have.
        if (this.inventoryLoaded && !this.inventoryRenderedPageSize) {
            this.renderSidebar({ preservePage: this.hasRestoredViewState });
        }
        this.handleSurfaceVisibility(true);
        void this.projectCanvasPeek();
    }

    /* THE PLAYER'S INTENT TO ENTER MOON FUSE (a hover, focus or touch on its
       tab, foundry-tabs.js `moonfall:foundry-tabintent`) reads the inventory
       unseen, so the press that follows finds it in hand — and the canvas
       again when the unseen read of it has gone stale (refreshCanvasPeek),
       its lease handed straight back: a hover is not a choice. The list
       reads as a return would — the page and scroll the player left the
       list on are kept (a first session's page 2 fell back to page 1, 27
       September 2026 review) — and a read ahead that fails changes nothing:
       the press reads again (readInventoryForSurface). */
    warmEntryInventory() {
        if (this.destroyed || this.surfaceActive || !this.user) return;
        if (this.inventoryReadsInFlight > 0 || this.inventoryAdoption || !this.inventoryNeedsRead()) return;
        void this.loadInventory({
            preserveScroll: true,
            preserveView: true,
            preserveOnError: true,
            showLoading: false,
            whileHidden: true,
        });
    }

    /**
     * Whether the inventory on hand is due a read: none yet, older than
     * INVENTORY_FRESH_MS, read in another language than the one now chosen
     * (a language changed while Moon Dream shows cannot read it then: a
     * hidden tab's read is aborted), or requested before a Stardust or
     * holdings announcement. Only a committed answer makes it fresh: a read
     * that fails or is aborted leaves it due.
     */
    inventoryNeedsRead() {
        if (!this.inventoryLoaded || this.inventoryReadChanges !== this.inventoryChanges) return true;
        if (this.inventoryReadLang !== (i18n?.getLanguage?.() || 'en')) return true;
        return !Number.isFinite(this.inventoryReadAt) || Date.now() - this.inventoryReadAt >= INVENTORY_FRESH_MS;
    }

    /** A committed answer: the list on hand is fresh as of the request that brought it. */
    noteInventoryRead(lang, changes = this.inventoryChanges) {
        this.inventoryReadAt = Date.now();
        this.inventoryReadLang = lang;
        this.inventoryReadChanges = changes;
    }

    /** The list says it is loading (a read is out and there is nothing on hand to show). */
    showInventoryLoading() {
        if (!this.elementList) return;
        this.inventorySignature = '';
        this.elementList.innerHTML = `<p style="color:#666;padding:${uiCssPx(8)};">${uiText('foundry.inventory.loading', 'Loading...')}</p>`;
    }

    /**
     * THE TAB ON SCREEN HAS ITS LIST, OR SAYS IT IS COMING (27 September
     * 2026 review). A read already out when the tab is entered — one started
     * on intent, or the lobby's list being taken over — stands in for the
     * tab's own, so a press never pays for the list twice. But it only stands
     * in: with nothing on hand the list shows its loading line at once (a
     * tab shown at the hand-over's cap used to arrive with a blank list and
     * no word of a read), and a read that ends without a list is followed by
     * the tab's own at once — a taken-over read that failed used to leave
     * Moon Fuse with no list and no request at all ("Moon Fuse was not
     * loading anymore", foundry-inventory-warm.ts). The lobby's list is
     * waited on for INVENTORY_ADOPT_WAIT_MS at most. The canvas projection
     * waits on the follow-up too (inventoryLoadPromise), so no chip is
     * painted from a stub while the list is still coming.
     */
    readInventoryForSurface() {
        const options = {
            preserveScroll: true,
            preserveView: true,
            preserveOnError: true,
            reconcileCanvas: true,
            /* A RESUME is silent, but a first arrival is not: entering the
               Foundry on Moon Dream leaves this tab cold, and switching to it
               then showed an empty canvas and an empty sidebar with nothing
               to say a request was in flight — which is what "Moon Fuse was
               not loading" looks like from the outside. */
            showLoading: !this.inventoryLoaded,
        };
        let onWire = null;
        if (this.inventoryReadsInFlight > 0) {
            onWire = this.inventoryLoadPromise;
        } else if (this.inventoryAdoption) {
            onWire = Promise.race([
                this.inventoryAdoption,
                new Promise((resolve) => { setTimeout(resolve, INVENTORY_ADOPT_WAIT_MS); }),
            ]);
        }
        if (!onWire) {
            if (this.inventoryNeedsRead()) void this.loadInventory(options);
            return;
        }
        if (options.showLoading) this.showInventoryLoading();
        const followUp = Promise.resolve(onWire).catch(() => null).then(() => {
            if (this.destroyed || !this.surfaceActive || !this.user) return null;
            if (this.inventoryReadsInFlight > 0 || !this.inventoryNeedsRead()) return null;
            // The loading line, when one is due, is already up.
            return this.loadInventory({ ...options, showLoading: false });
        });
        this.inventoryLoadPromise = followUp.catch(() => null);
    }

    /** The press was taken back (or the Foundry closed) before the tab was shown. */
    withdrawEntry() {
        if (!this.entryPreparing) return;
        this.entryPreparing = false;
        this.markPanelPreparing(false);
        this.handleSurfaceVisibility(this.readFuseSurfaceActive());
    }

    /** The entry's reads are in (or failed): the tab may be shown, and what waited on it runs. */
    endEntryPreparation() {
        if (!this.entryPreparing) return;
        this.entryPreparing = false;
        if (!this.surfaceActive || this.destroyed) return;
        this.markPanelPreparing(false);
        this.flushDeferredAchievementBanners();
        this.flushDeferredUserNotices();
    }

    isInventoryPanelPreparing() {
        return this.elementList?.closest?.('.foundry-panel')?.hasAttribute('data-foundry-preparing') === true;
    }

    /** Request options for a read that may run while the tab is hidden. */
    surfaceReadOptions(whileHidden) {
        return whileHidden
            ? { abortOnHide: false, controllerSet: this.hiddenReadControllers }
            : {};
    }

    getElementById(id) {
        if (this.root === document) return document.getElementById(id);
        return this.root?.querySelector?.(`#${id}`) || null;
    }

    readFuseSurfaceActive() {
        const lifecycle = FoundryLifecycle;
        if (typeof lifecycle?.isTabActive === 'function') {
            return lifecycle.isTabActive('moon-fuse-panel');
        }
        return lifecycle?.isActive?.() !== false;
    }

    scheduleSurfaceTimer(callback, delayMs) {
        const timer = setTimeout(() => {
            this.surfaceTimers.delete(timer);
            if (!this.destroyed) callback();
        }, delayMs);
        this.surfaceTimers.add(timer);
        return timer;
    }

    restoreViewState(viewState) {
        if (!viewState || typeof viewState !== 'object' || Array.isArray(viewState)) return;
        const search = typeof viewState.search === 'string'
            ? viewState.search.trim().slice(0, 120)
            : '';
        const page = Number(viewState.page);
        const scrollTop = Number(viewState.scrollTop);
        this.inventorySearch = search.toLowerCase();
        this.inventoryPage = Number.isSafeInteger(page) && page > 0 ? Math.min(page, 10_000) : 1;
        this.inventoryScrollTop = Number.isFinite(scrollTop) && scrollTop >= 0
            ? Math.min(scrollTop, 10_000_000)
            : 0;
        if (this.searchInput) this.searchInput.value = search;
        this.hasRestoredViewState = true;
    }

    captureViewState() {
        const scrollTop = Number(this.captureInventoryScroll());
        return {
            search: String(this.searchInput?.value || '').trim().slice(0, 120),
            page: Number.isSafeInteger(this.inventoryPage) && this.inventoryPage > 0
                ? Math.min(this.inventoryPage, 10_000)
                : 1,
            scrollTop: Number.isFinite(scrollTop) && scrollTop >= 0
                ? Math.min(scrollTop, 10_000_000)
                : 0,
        };
    }

    getOrCreateWorkspaceLeaseToken() {
        try {
            const existing = sessionStorage.getItem(FOUNDRY_WORKSPACE_LEASE_KEY);
            if (existing && existing.length <= 128) {
                return existing;
            }
            const created = createFoundryLeaseToken();
            sessionStorage.setItem(FOUNDRY_WORKSPACE_LEASE_KEY, created);
            return created;
        } catch (_) {
            return createFoundryLeaseToken();
        }
    }

    readCrashJournalStore() {
        try {
            const parsed = JSON.parse(localStorage.getItem(FOUNDRY_CRASH_JOURNAL_KEY) || '{}');
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
            const now = Date.now();
            let pruned = false;
            Object.entries(parsed).forEach(([accountKey, entry]) => {
                const writtenAt = Number(entry?.writtenAt);
                if (
                    !Number.isFinite(writtenAt)
                    || writtenAt <= 0
                    || writtenAt > now + FOUNDRY_CRASH_JOURNAL_TTL_MS
                    || now - writtenAt > FOUNDRY_CRASH_JOURNAL_TTL_MS
                ) {
                    delete parsed[accountKey];
                    pruned = true;
                }
            });
            if (pruned) {
                if (Object.keys(parsed).length === 0) {
                    localStorage.removeItem(FOUNDRY_CRASH_JOURNAL_KEY);
                } else {
                    localStorage.setItem(FOUNDRY_CRASH_JOURNAL_KEY, JSON.stringify(parsed));
                }
            }
            return parsed;
        } catch (_) {
            return {};
        }
    }

    readCrashJournal() {
        if (!this.accountStorageKey) return null;
        const entry = this.readCrashJournalStore()[this.accountStorageKey];
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
        const baseRevision = Number(entry.baseRevision);
        if (!Number.isSafeInteger(baseRevision) || baseRevision < 0 || !Array.isArray(entry.elements)) {
            return null;
        }
        return {
            baseRevision,
            baseHash: typeof entry.baseHash === 'string' ? entry.baseHash : null,
            elements: this.normalizeWorkspaceElements(entry.elements),
        };
    }

    writeCrashJournal(elements = null) {
        if (!this.accountStorageKey || !this.canEditWorkspace()) return;
        try {
            const store = this.readCrashJournalStore();
            store[this.accountStorageKey] = {
                baseRevision: this.workspaceRevision,
                // The canvas the server held at that revision, as this tab
                // knows it; unknown while a save conflict is unresolved.
                ...(this.lastSavedWorkspaceHash && !this.workspaceSaveBlockedByConflict
                    ? { baseHash: this.lastSavedWorkspaceHash }
                    : {}),
                writtenAt: Date.now(),
                elements: this.normalizeWorkspaceElements(elements || this.buildWorkspaceElements()),
            };
            localStorage.setItem(FOUNDRY_CRASH_JOURNAL_KEY, JSON.stringify(store));
        } catch (_) { }
    }

    clearCrashJournal(expectedElements) {
        if (!this.accountStorageKey) return;
        try {
            const store = this.readCrashJournalStore();
            const current = store[this.accountStorageKey];
            if (!current) return;
            if (expectedElements && JSON.stringify(current.elements) !== JSON.stringify(expectedElements)) return;
            delete store[this.accountStorageKey];
            if (Object.keys(store).length === 0) {
                localStorage.removeItem(FOUNDRY_CRASH_JOURNAL_KEY);
            } else {
                localStorage.setItem(FOUNDRY_CRASH_JOURNAL_KEY, JSON.stringify(store));
            }
        } catch (_) { }
    }

    /** Two workspace snapshots place the same elements at the same points, in the same order. */
    isSameWorkspaceSnapshot(left, right) {
        return JSON.stringify(this.normalizeWorkspaceElements(left))
            === JSON.stringify(this.normalizeWorkspaceElements(right));
    }

    /** A save answered with a revision whose canvas is exactly the one sent. */
    isSnapshotAlreadyOnServer(payload, elements) {
        return Number.isSafeInteger(payload?.revision)
            && Array.isArray(payload?.elements)
            && this.isSameWorkspaceSnapshot(payload.elements, elements);
    }

    /**
     * A CRASH JOURNAL IS UNSAVED WORK ONLY WHEN IT DIFFERS from the canvas the
     * server returned. Its base revision alone cannot say so: the server's
     * open rewrites the workspace under a new revision, so a journal holding
     * exactly the server's canvas read as "changed elsewhere" on the next
     * visit (snappy meter round 13, 25 September 2026). Such a journal is
     * cleared and the read goes on as a clean one; returns the journal that
     * still carries unsaved work, or null.
     */
    retireJournalMatchingServer(journal, canonicalElements) {
        if (!journal || !this.isSameWorkspaceSnapshot(journal.elements, canonicalElements)) return journal;
        this.clearCrashJournal(journal.elements);
        return null;
    }

    /**
     * A JOURNAL IS RESTORED WHEN NOTHING CHANGED UNDER IT: the server is still
     * at the journal's base revision, or it still holds exactly the canvas the
     * journal was based on. The second case is the one that matters today,
     * because a server open can move the revision without changing the canvas;
     * an edit the tab died holding, or a discovery the recovery had put back,
     * was then thrown away as "changed elsewhere" (review of the round-13
     * fix, 26 September 2026). Whatever another tab changed is a conflict.
     */
    isJournalRestorable(journal, canonicalHash) {
        return Boolean(journal) && (
            journal.baseRevision === this.workspaceRevision
            || (Boolean(journal.baseHash) && journal.baseHash === canonicalHash)
        );
    }

    async init() {
        this.setupEventListeners();
        const parentUser = this.user || Firebase?.getUser?.() || null;
        if (parentUser) {
            await this.handleAuthUser(parentUser);
            return;
        }

        /* Subscribe rather than poll. This used to read
           `window.MoonfallAuthStateChanged(window.MoonfallAuth, …)` — a pair of
           globals that NOTHING has ever assigned, on this branch or on main — so
           the condition was always false and the foundry only ever reached the
           retry timer below. `Firebase.subscribe` is the registry that actually
           exists: firebase-loader keeps a subscriber set and calls it from its
           own onAuthStateChanged. */
        this.authUnsubscribe = Firebase?.subscribe?.((user) => {
            if (this.authRetryTimer) {
                clearTimeout(this.authRetryTimer);
                this.authRetryTimer = null;
            }
            void this.handleAuthUser(user);
        }) || null;

        /* The deadline stays armed even when the subscription took, and that is
           deliberate: `Firebase.subscribe` only ever fires on the NEXT auth
           change, so a foundry opened after Firebase has already settled — or
           one opened when Firebase failed to load at all — would otherwise wait
           forever. Closing on a null user after 100 ms is the behaviour this
           panel has always had; the subscription is what was missing. */
        this.authRetryTimer = setTimeout(() => {
            this.authRetryTimer = null;
            if (this.destroyed) return;
            const user = Firebase?.getUser?.() || null;
            if (user) {
                void this.handleAuthUser(user);
            } else {
                this.onClose?.();
            }
        }, 100);
    }

    discardAccountBoundState() {
        this.accountBoundaryGeneration += 1;
        this.workspaceAuthorityGeneration += 1;
        this.workspaceProjectionGeneration += 1;
        this.inventoryLoadSeq += 1;

        this.surfaceRequestControllers.forEach((controller) => controller.abort());
        this.surfaceRequestControllers.clear();
        this.hiddenReadControllers.forEach((controller) => controller.abort());
        this.hiddenReadControllers.clear();
        this.authRequestControllers.clear();
        this.workspaceSaveRequestControllers.forEach((controller) => controller.abort());
        this.workspaceSaveRequestControllers.clear();
        this.workspaceLeaseRequestControllers.forEach((controller) => controller.abort());
        this.workspaceLeaseRequestControllers.clear();
        this.workspaceRecoveryRequestControllers.forEach((controller) => controller.abort());
        this.workspaceRecoveryRequestControllers.clear();
        this.artSelectionRequestControllers.forEach((controller) => controller.abort());
        this.artSelectionRequestControllers.clear();

        if (this.workspaceSaveTimer) clearTimeout(this.workspaceSaveTimer);
        if (this.workspaceOpenRetryTimer) clearTimeout(this.workspaceOpenRetryTimer);
        if (this.workspaceRenewTimer) clearTimeout(this.workspaceRenewTimer);
        if (this.workspaceRecoveryTimer) clearTimeout(this.workspaceRecoveryTimer);
        if (this.artModalReconcileTimer) clearTimeout(this.artModalReconcileTimer);
        this.workspaceSaveTimer = null;
        this.workspaceOpenRetryTimer = null;
        this.workspaceRenewTimer = null;
        this.workspaceRecoveryTimer = null;
        this.artModalReconcileTimer = null;
        this.workspacePendingSaveRequest = null;
        this.workspaceOpenInFlight = null;
        this.workspaceRecoveryInFlight = null;
        this.workspaceRecoveryState = null;
        this.workspaceTakeOverInFlight = false;
        this.workspaceRecoveryOverflow = false;
        this.workspaceSaveBlockedByConflict = false;
        this.workspaceLoaded = false;
        this.workspaceProjected = false;
        this.workspacePersistenceSuspended = false;
        this.workspaceRevision = 0;
        this.workspaceUpdatedAt = 0;
        this.workspaceLeaseExpiresAt = 0;
        this.lastSavedWorkspaceHash = '';
        this.forgeCosts = null;
        this.canvasMutationVersion = 0;
        this.canvasPeek = null;

        this.forgePollTimers.forEach((timer) => clearTimeout(timer));
        this.forgePollTimers.clear();
        this.pendingForges.clear();
        this.materializedOperationIds.clear();
        this.elements.slice().forEach(({ el }) => this.removeElement(el, {
            skipSessionSave: true,
            triggerRecovery: false,
        }));
        this.inventory = [];
        this.inventoryById.clear();
        this.inventoryReadAt = null;
        this.inventoryReadLang = null;
        this.inventoryAdoption = null;
        this.filteredInventory = [];
        this.firstDiscoveredCardIds.clear();
        this.cardDetailCache.clear();
        this.inventoryCacheKey = '';
        this.inventorySignature = '';
        this.inventoryVersion += 1;
        this.hiddenWhileOpen = false;
        this.inventoryLoaded = false;
        this.inventoryPage = 1;
        this.inventoryScrollTop = 0;
        this.inventorySearch = '';
        this.inventoryPaged = false;
        this.inventoryPagedInfo = null;
        clearTimeout(this.inventorySearchTimer);
        this.inventorySearchTimer = null;
        this.inventoryResizeRead = null;
        this.inventoryIndexWaits = 0;
        if (this.searchInput) this.searchInput.value = '';
        this.elementList?.replaceChildren?.();
        // First frame: the balance the lobby chip already holds, not "0" until
        // /api/foundry/user answers; that read still confirms it below.
        this.updateStardustDisplay(lastKnownStardust() ?? 0);

        this.artQueue = [];
        this.dismissedArtOperations.clear();
        this.pendingMergePairs.clear();
        this.artReadySoundPlayed.clear();
        this.activeArtModal = false;
        this.activeArtModalOperationId = null;
        this.activeArtModalComplete = null;
        this.artModalReconcileInFlight = null;
        this.artModalReconcileLocked = false;
        this.artModalReconcileOperationId = null;
        this.artDismissLatchUntil = 0;
        const discoveryModal = this.getElementById('discovery-modal');
        discoveryModal?.classList.add('hidden');
        discoveryModal?.setAttribute('aria-hidden', 'true');
        this.releaseArtModalAccessibility();
        this.releaseArtConfirmGuard();
        this.deferredAchievementBanners = [];
        this.deferredUserNotices = [];
        this.achievementConsumePending = false;
        this.achievementConsumeInFlight = null;
        this.surfaceTimers.forEach((timer) => clearTimeout(timer));
        this.surfaceTimers.clear();
        this.clearSurfaceTransientState();
        this.workspaceNoticeReasons.clear();
        this.setWorkspaceMode('read_only');
        MoonfallCloud?.setAuthToken?.(null);
    }

    async handleAuthUser(user) {
        if (this.destroyed) {
            this.settleFirstView();
            return;
        }
        const authAttemptGeneration = ++this.authAttemptGeneration;
        let firstViewReads = null;
        try {
            firstViewReads = await this.authenticateAndReadFirstView(user, authAttemptGeneration);
        } finally {
            /* A superseded attempt leaves the first view to the attempt that
               replaced it; every other ending settles it, so a surface waiting
               on it is never held by an attempt that went nowhere. */
            if (this.destroyed || this.authAttemptGeneration === authAttemptGeneration) {
                if (firstViewReads) {
                    void Promise.allSettled(firstViewReads).then(() => this.settleFirstView());
                } else {
                    this.settleFirstView();
                }
            }
        }
    }

    /** Returns the first view's reads when it started them, else null. */
    async authenticateAndReadFirstView(user, authAttemptGeneration) {
        this.authRequestControllers.forEach((controller) => controller.abort());
        this.authRequestControllers.clear();
        if (!user) {
            MoonfallCloud?.setAuthToken?.(null);
            if (this.onClose) {
                this.onClose();
                return;
            }
            if (this.accountStorageKey) this.discardAccountBoundState();
            this.user = null;
            this.accountStorageKey = '';
            return;
        }

        const nextAccountStorageKey = buildFoundryAccountStorageKey(user);
        if (this.accountStorageKey && this.accountStorageKey !== nextAccountStorageKey) {
            if (this.onClose) {
                MoonfallCloud?.setAuthToken?.(null);
                this.onClose();
                return;
            }
            this.discardAccountBoundState();
        }
        this.user = user;
        this.accountStorageKey = nextAccountStorageKey;
        /* THE SURFACE MAY OPEN ON THE OTHER TAB. Restoring the player's last
           tab — or the `?tab=moon-dream` deep link — makes `surfaceActive`
           false before this instance is even built, and every read below was
           behind that guard, so entering on Moon Dream produced a Foundry that
           issued ZERO /api/foundry requests and sat read_only until the player
           happened to click Moon Fuse (measured by moonfall-c5 on ea524b02:
           inv 0, workspaceLoaded false, no request pending or failed).
           The account's cloud token is NOT surface state — Moon Dream and the
           banners are served by it too — so it is established either way; only
           the Moon Fuse reads stay deferred to `resumeSurfaceReads`, which the
           tab switch runs. */
        const authorityGeneration = this.workspaceAuthorityGeneration;
        let authToken = null;
        try {
            if (
                typeof MoonfallCloud?.setAuthToken === 'function'
                && typeof user.getIdToken === 'function'
            ) {
                const token = await this.runBoundedRequest((signal) => (
                    awaitFoundryRequestStage(user.getIdToken(), signal)
                ), { controllerSet: this.authRequestControllers });
                // Account identity, not surface state: the same four
                // generation checks, without the active-tab one.
                if (
                    this.destroyed
                    || this.authAttemptGeneration !== authAttemptGeneration
                    || this.accountStorageKey !== nextAccountStorageKey
                    || this.workspaceAuthorityGeneration !== authorityGeneration
                ) return;
                if (token) {
                    authToken = token;
                    MoonfallCloud.setAuthToken(token);
                }
            }
        } catch (err) {
            if (err?.name === 'AbortError') return;
            console.warn('[Foundry] Failed to set MoonfallCloud auth token:', err);
        }
        if (
            this.destroyed
            || this.authAttemptGeneration !== authAttemptGeneration
            || this.accountStorageKey !== nextAccountStorageKey
            || this.workspaceAuthorityGeneration !== authorityGeneration
        ) return;
        // Opened on Moon Dream: the token above is set, the Moon Fuse reads
        // wait for the tab that needs them (handleSurfaceVisibility → resume),
        // apart from the unseen first view the surface may have asked for.
        if (!this.surfaceActive) {
            void this.readHiddenFirstView();
            return null;
        }
        const inventory = this.loadInventory({
            preserveView: this.hasRestoredViewState,
            showLoading: true,
        }).then(() => {
            if (this.destroyed || !this.hasRestoredViewState) return;
            this.restoreInventoryScroll();
        });
        const balance = this.loadStardust();
        const pricing = this.loadForgeCosts();
        void this.consumeAndShowCloudAchievementBanners();
        void this.openWorkspace({ authToken });
        // The canvas counts from its projection, not from discovery recovery
        // after it: a recovered discovery is news that may arrive later.
        return [inventory, balance, pricing, this.firstProjection.promise];
    }

    setupEventListeners() {
        const signal = this.eventController.signal;

        // Spend-source menu: one app-wide delegated listener, installed here
        // and idempotent, so Moon Fuse, Moon Dream and Collection all share it.

        // Search
        this.searchInput?.addEventListener('input', (e) => {
            this.inventorySearch = String(e.target.value || '').trim().toLowerCase();
            this.inventoryPage = 1;
            if (this.inventoryPaged) {
                this.readPagedInventory({ debounce: true });
                return;
            }
            this.renderSidebar({ resetScroll: true });
        }, { signal });

        if (this.sidebarPrev) {
            this.sidebarPrev.addEventListener('click', () => {
                this.inventoryPage = Math.max(1, this.inventoryPage - 1);
                if (this.inventoryPaged) {
                    this.readPagedInventory();
                    return;
                }
                this.renderSidebar({ resetScroll: true });
            }, { signal });
        }

        if (this.sidebarNext) {
            this.sidebarNext.addEventListener('click', () => {
                this.inventoryPage += 1;
                if (this.inventoryPaged) {
                    this.readPagedInventory();
                    return;
                }
                this.renderSidebar({ resetScroll: true });
            }, { signal });
        }

        this.elementList?.addEventListener('scroll', () => {
            if (this.isInventoryPanelActive()) {
                this.inventoryScrollTop = this.elementList.scrollTop;
            }
        }, { signal });
        window.addEventListener('moonfall:foundry-tabchanging', (event) => {
            if (event?.detail?.previousTargetId !== 'moon-fuse-panel') return;
            this.captureInventoryScroll();
            this.captureWorkspaceCanvasMetrics();
            this.writeCrashJournal();
        }, { signal });
        window.addEventListener('moonfall:foundry-tabchange', (event) => {
            if (event?.detail?.targetId === 'moon-fuse-panel') {
                this.reprojectCanvasElementsToCurrentSize();
                /* A RETURN KEEPS THE ROWS. The list is kept built while Moon
                   Dream shows, so it is re-rendered only when it has no page
                   yet (read while hidden) or the page it fits has changed —
                   the refitToPresentedLayout rule. Re-rendering it on every
                   return wiped and rebuilt every row, dropping hover and focus
                   and restarting the first-discovery gradients (26 September
                   2026 review). */
                if (
                    this.inventoryLoaded
                    && (
                        !this.inventoryRenderedPageSize
                        || this.measureInventoryPageCapacity() !== this.inventoryRenderedPageSize
                    )
                ) {
                    this.renderSidebar({ preservePage: true, preserveLogicalOffset: true });
                }
                this.restoreInventoryScroll();
            }
        }, { signal });
        window.addEventListener('moonfall:foundry-tabrequest', (event) => {
            if (event?.detail?.targetId === 'moon-fuse-panel') this.prepareEntry();
        }, { signal });
        window.addEventListener('moonfall:foundry-tabintent', (event) => {
            const { detail } = /** @type {CustomEvent} */ (event);
            if (detail?.targetId !== 'moon-fuse-panel') return;
            this.warmEntryInventory();
            this.refreshCanvasPeek();
        }, { signal });
        /* WHAT MAKES THE INVENTORY ON HAND STALE (inventoryNeedsRead): cards
           granted or taken elsewhere (a pack, a trade, a Moon Dream deck), and
           a Stardust figure that moved — every surface that spends or grants
           announces it. This tab's own crafts re-read the list themselves, and
           a read that confirms the figure already shown changes nothing. */
        window.addEventListener('moonfall:holdings-changed', (event) => {
            const { detail } = /** @type {CustomEvent} */ (event);
            if (detail?.source !== 'foundry') this.inventoryChanges += 1;
        }, { signal });
        this.unsubscribeStardustAnnounced = onStardustAnnounced((balance) => {
            if (balance === null || balance !== this.lastStardustBalance) this.inventoryChanges += 1;
        });
        window.addEventListener('moonfall:foundry-tabrequestcancel', (event) => {
            if (event?.detail?.targetId === 'moon-fuse-panel') this.withdrawEntry();
        }, { signal });
        // Re-fit the element grid's page size when the viewport changes (e.g. a
        // phone rotates) so paginate-to-fit keeps a page inside the viewport.
        // A page that still fits keeps its rows, as a tab return does: a
        // phone's address bar moving rebuilt every row, and a tap whose press
        // and release straddled that landed on two rows and opened nothing
        // (moon-fuse-tap, 22 October 2026).
        window.addEventListener('moonfall:viewport-change', () => {
            this.reprojectCanvasElementsToCurrentSize();
            if (
                this.isInventoryPanelActive()
                && !(
                    this.inventoryLoaded
                    && this.inventoryRenderedPageSize
                    && this.measureInventoryPageCapacity() === this.inventoryRenderedPageSize
                )
            ) {
                this.renderSidebar({ preserveLogicalOffset: true });
            }
            if (this.activeArtModal && !OrientationGuard?.isActive?.()) {
                this.focusActiveArtModal();
            }
        }, { signal });

        if (this.lobbyBtn && !this.onClose) {
            this.lobbyBtn.addEventListener('click', () => this.returnToLobby(), { signal });
        }
        this.getElementById('art-close-btn')?.addEventListener('click', () => {
            this.dismissActiveArtModal();
        }, { signal });
        // A plain click on the trash zone (never a drag-drop, which stays
        // per-element) offers a confirmed clear-all of the canvas.
        this.trashZone?.addEventListener('click', async () => {
            if (this.draggedEl || this.sidebarDrag || this.pendingCanvasDrag) return;
            if (!this.canEditWorkspace() || this.elements.length === 0) return;
            const confirmed = await Confirm.ask(uiText(
                'foundry.clearAllConfirm',
                'Clear the whole canvas? Your cards stay in your inventory — only the workspace is swept clean.'
            ), { destructive: true });
            if (!confirmed) return;
            // The canvas can change while the dialog is open.
            if (!this.canEditWorkspace() || this.elements.length === 0) return;
            let lockedKept = false;
            this.elements.slice().forEach((entry) => {
                if (this.isElementLocked(entry.el)) { lockedKept = true; return; }
                this.removeElement(entry.el, { skipSessionSave: true });
            });
            if (lockedKept) {
                this.notifyUser(uiText('foundry.forgingLocked', 'Some elements are still forging and cannot be removed yet.'));
            }
            this.scheduleWorkspaceSave();
        }, { signal });
        document.addEventListener('keydown', (event) => {
            if (OrientationGuard?.isActive?.()) return;
            if (event.key === 'Escape') this.dismissActiveArtModal();
            else if (event.key === 'Tab') this.trapArtModalFocus(event);
        }, { signal });

        window.addEventListener('moonfall:convex-ready', async () => {
            if (!this.user || this.destroyed) return;
            await this.refreshCloudAuthToken();
            this.consumeAndShowCloudAchievementBanners();
        }, { signal });

        window.addEventListener('moonfall:languagechange', () => {
            this.renderForgeCosts();
            void this.refreshLocalizedContent();
        }, { signal });

        this.workspaceTakeOverBtn?.addEventListener('click', () => {
            void this.takeOverWorkspace();
        }, { signal });

        // Global pointer events for dragging (mouse, touch, pen)
        document.addEventListener('pointermove', (e) => this.onPointerMove(e), { signal });
        document.addEventListener('pointerup', (e) => this.onPointerUp(e), { signal });
        document.addEventListener('pointercancel', (e) => this.onPointerCancel(e), { signal });

        window.addEventListener('beforeunload', () => {
            if (!this.destroying) void this.persistWorkspaceState({ force: true });
        }, { signal });
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.hiddenWhileOpen = true;
                void this.persistWorkspaceState({ force: true });
                return;
            }
            this.resumeAfterPageHidden();
        }, { signal });
        /* iOS restores a frozen page through `pageshow` with `persisted`, and
           a page frozen mid-read may never have fired `visibilitychange` at
           all. Treat the restore as a return either way. */
        window.addEventListener('pageshow', (event) => {
            if (event?.persisted) this.hiddenWhileOpen = true;
            this.resumeAfterPageHidden();
        }, { signal });
    }

    /**
     * THE PAGE came back, not the tab.
     *
     * `FoundryLifecycle` is driven by the SURFACE (open/close, Moon Fuse vs
     * Moon Dream), so leaving the browser and returning never ran a resume:
     * `surfaceActive` was true the whole time, and `handleSurfaceVisibility`
     * early-returns when nothing changed. Reported from an iPhone
     * (2026-09-21): "Moon Fuse was not loading anymore... I went to Moon Dream
     * and back and it was fixed" — that tab switch is the only thing that ever
     * re-read it. A freeze can abort the inventory read in flight, and iOS can
     * freeze a page without firing `visibilitychange` first.
     *
     * The resume is the same idempotent one the tab switch performs
     * (`openWorkspace` + inventory with the view and scroll preserved), so a
     * spurious call costs one cheap read and repaints nothing.
     */
    resumeAfterPageHidden() {
        if (document.visibilityState === 'hidden') return;
        if (!this.hiddenWhileOpen) return;
        this.hiddenWhileOpen = false;
        if (this.destroying || this.destroyed || !this.surfaceActive || !this.user) return;
        void this.resumeSurfaceReads();
    }

    returnToLobby() {
        if (this.onClose) {
            this.onClose();
            return;
        }
        if (FoundrySurface?.isOpen?.()) {
            FoundrySurface.close();
            return;
        }
        window.location.href = '/play';
    }

    playSoundEffect(name) {
        if (!this.surfaceActive) return false;
        if (!SoundEffects?.play) return false;
        SoundEffects.play(name);
        return true;
    }

    notifyUser(message) {
        const notice = String(message || '').trim();
        if (!notice) return;
        if (!this.surfaceActive) {
            this.deferredUserNotices.push(notice);
            return;
        }
        this.showNoticeToast(notice);
    }

    // Non-blocking foundry notice (capacity, cooldowns, queue state): a small
    // dismissible toast instead of a browser alert() that freezes the forge.
    showNoticeToast(notice) {
        let stack = document.getElementById('foundry-notice-stack');
        if (!stack) {
            stack = document.createElement('div');
            stack.id = 'foundry-notice-stack';
            stack.setAttribute('role', 'status');
            stack.setAttribute('aria-live', 'polite');
            document.body.appendChild(stack);
        }
        while (stack.children.length >= 3) stack.firstElementChild.remove();
        const toast = document.createElement('div');
        toast.className = 'foundry-notice-toast';
        toast.textContent = notice;
        toast.addEventListener('click', () => toast.remove());
        stack.appendChild(toast);
        this.scheduleSurfaceTimer(() => {
            toast.classList.add('closing');
            this.scheduleSurfaceTimer(() => toast.remove(), 300);
        }, 6000);
    }

    flushDeferredUserNotices() {
        if (!this.surfaceActive || this.deferredUserNotices.length === 0) return;
        const notices = this.deferredUserNotices.splice(0);
        notices.forEach((notice, index) => {
            this.scheduleSurfaceTimer(() => {
                if (this.surfaceActive) {
                    this.showNoticeToast(notice);
                } else {
                    this.deferredUserNotices.unshift(notice);
                }
            }, index * 150);
        });
    }

    clearSurfaceTransientState() {
        this.releaseDragPointer();
        this.hoverTimers.forEach((timer) => clearTimeout(timer));
        this.hoverTimers.clear();

        if (this.draggedEl) {
            this.draggedEl.classList.remove('dragging');
            this.draggedEl.style.zIndex = 10;
        }
        this.draggedEl = null;
        this.pendingCanvasDrag = null;
        this.sidebarDrag = null;
        this.setTrashZoneActive(false);

        this.dismissHoverPreview();
        Cards?.clearHoverPreview?.();
    }

    async runBoundedRequest(executor, {
        abortOnHide = true,
        controllerSet = null,
        timeoutMs = this.operationRequestTimeoutMs,
    } = {}) {
        if (abortOnHide && (!this.surfaceActive || this.destroyed)) {
            throw createFoundryAbortError();
        }
        const controller = new AbortController();
        const trackedControllerSets = new Set([
            ...(abortOnHide ? [this.surfaceRequestControllers] : []),
            ...(controllerSet ? [controllerSet] : []),
        ]);
        trackedControllerSets.forEach((controllers) => controllers.add(controller));
        // The reason travels with the signal, so anything that rejects with it
        // natively says what happened rather than "aborted without reason".
        const timeout = setTimeout(() => {
            controller.abort(createFoundryAbortError('Foundry request timed out', { timedOut: true }));
        }, Math.max(1, Number(timeoutMs) || 1));
        try {
            return await executor(controller.signal);
        } finally {
            clearTimeout(timeout);
            trackedControllerSets.forEach((controllers) => controllers.delete(controller));
        }
    }

    /**
     * The account's ID token for the cloud client. `force` asks Firebase for a
     * new one (a round trip: 0.35-1.05 s in round 14); without it Firebase
     * returns the one it holds and renews it itself when it is near expiry.
     */
    async refreshCloudAuthToken({ force = true } = {}) {
        if (!this.user?.getIdToken) return null;
        try {
            const token = await this.runBoundedRequest((signal) => (
                awaitFoundryRequestStage(this.user.getIdToken(force), signal)
            ));
            if (token && typeof MoonfallCloud?.setAuthToken === 'function') {
                MoonfallCloud.setAuthToken(token);
            }
            return token || null;
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.warn('[Foundry] Failed to refresh cloud auth token:', err);
            }
            return null;
        }
    }

    /**
     * `forceTokenRefresh`: a page coming back from being hidden or frozen
     * renews its token first; a tab switch reads with the token the account
     * holds (Firebase renews it itself near expiry), so the canvas the switch
     * waits for is not behind a token round trip.
     *
     * The inventory is read again only when the one on hand is older than
     * INVENTORY_FRESH_MS or an announcement made it stale (inventoryNeedsRead):
     * every switch and return used to re-read every card the player owns. A
     * read it needs goes out FIRST, beside the canvas, because the canvas
     * projection waits for it (applyWorkspaceSnapshot never paints a stub
     * chip); read after the canvas, the two round trips were paid in a row.
     */
    async resumeSurfaceReads({ forceTokenRefresh = true } = {}) {
        if (this.surfaceActive && this.user) this.readInventoryForSurface();
        const authToken = await this.refreshCloudAuthToken({ force: forceTokenRefresh });
        if (!this.surfaceActive || !this.user) return;
        await this.openWorkspace({ authToken });
        if (!this.surfaceActive) return;
        this.pendingForges.forEach((context, operationId) => {
            if (context?.pollInFlight) {
                context.resumePollRequested = true;
                return;
            }
            this.schedulePollForResult(operationId, context, 0);
        });
        void this.reconcileActiveArtModal();
        void this.loadStardust();
        void this.loadForgeCosts();
        void this.consumeAndShowCloudAchievementBanners();
    }

    handleSurfaceVisibility(active) {
        const nextActive = active === true;
        if (nextActive === this.surfaceActive) return;
        this.surfaceActive = nextActive;
        if (!this.surfaceActive) {
            this.entryPreparing = false;
            this.surfacePausedAt = Date.now();
            this.forgePollTimers.forEach((timer) => clearTimeout(timer));
            this.forgePollTimers.clear();
            this.stopForgeTimerTicking();
            this.surfaceRequestControllers.forEach((controller) => controller.abort());
            this.surfaceRequestControllers.clear();
            this.artSelectionRequestControllers.clear();
            this.releaseArtConfirmGuard();
            if (this.artModalReconcileTimer) {
                clearTimeout(this.artModalReconcileTimer);
                this.artModalReconcileTimer = null;
            }
            if (this.workspaceOpenRetryTimer) {
                clearTimeout(this.workspaceOpenRetryTimer);
                this.workspaceOpenRetryTimer = null;
            }
            if (this.workspaceRecoveryTimer) {
                clearTimeout(this.workspaceRecoveryTimer);
                this.workspaceRecoveryTimer = null;
            }
            this.stopWorkspaceLeaseRenewal();
            if (!this.destroying) void this.persistWorkspaceState({ force: true });
            this.clearSurfaceTransientState();
            return;
        }

        // Shown now: a panel prepared unseen takes its ordinary box in this
        // task — unless the press is still opening its canvas (prepareEntry).
        if (!this.entryPreparing) this.markPanelPreparing(false);
        if (Number.isFinite(this.surfacePausedAt)) {
            const pausedDurationMs = Math.max(0, Date.now() - this.surfacePausedAt);
            this.pendingForges.forEach((context) => {
                if (Number.isFinite(context?.startedAt)) {
                    context.startedAt += pausedDurationMs;
                }
            });
        }
        this.surfacePausedAt = null;
        // Counted on while unseen: the chips show the time left now.
        this.tickForgeTimers();
        if (!this.entryPreparing) {
            this.flushDeferredAchievementBanners();
            this.flushDeferredUserNotices();
        }
        if (this.user) {
            const resumed = this.resumeSurfaceReads({ forceTokenRefresh: false });
            // The projection ends the entry (applyWorkspaceSnapshot); this
            // ends it when the reads end without one.
            if (this.entryPreparing) void resumed.finally(() => this.endEntryPreparation());
        }
    }

    flushDeferredAchievementBanners() {
        if (!this.surfaceActive || this.deferredAchievementBanners.length === 0) return;
        const queued = this.deferredAchievementBanners.splice(0);
        queued.forEach((banner, index) => {
            this.scheduleSurfaceTimer(() => this.showAchievementBanner(...banner), index * 4500);
        });
    }

    async refreshLocalizedContent() {
        this.cardDetailCache.clear();
        this.inventoryCacheKey = '';

        if (this.user) {
            await this.loadInventory({
                preserveScroll: true,
                preserveView: true,
                preserveOnError: true,
                showLoading: false,
            });
        }

        await Promise.all(this.elements.map(async ({ el, card }) => {
            if (!el?.isConnected || this.isElementLocked(el) || !card?.cardId) return;
            const localized = await this.fetchCardDetails(card.cardId);
            if (localized) {
                this.updateElementCard(el, localized, { skipSessionSave: true });
            }
        }));
    }

    updateStardustDisplay(value) {
        if (!this.stardustCountEl) return;
        const numeric = Number(value);
        const balance = Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : 0;
        const changed = this.lastStardustBalance !== balance;
        this.lastStardustBalance = balance;
        this.stardustCountEl.dataset.countValue = String(balance);
        this.stardustCountEl.textContent = formatStardust(balance);
        // Re-read the split only when the balance actually moved, so the menu
        // retires itself the moment a grant is spent out without polling.
    }

    showAchievementBanner(title, subtitle, imageUrl, imageKind, bannerOptions) {
        if (!this.surfaceActive) {
            this.deferredAchievementBanners.push(bannerOptions
                ? [title, subtitle, imageUrl, imageKind, bannerOptions]
                : [title, subtitle, imageUrl, imageKind]);
            return;
        }
        // The "banner.js not loaded" guard is gone with the global it checked.
        // The renderer is a static import now, so the bundler cannot produce a
        // build where it is missing — the branch could never be taken, and a
        // check that can never fire reads as protection while providing none.
        // A banner with a card image is the forge's reward moment: the shared
        // engine renders it as a full reveal (framed pop + spark burst +
        // forge chime) instead of a bare toast.
        const options = bannerOptions && typeof bannerOptions === 'object' ? bannerOptions : {};
        const hasArtIdentity = Boolean(imageUrl || options.cardId || options.cardbackSlug);
        showAchievementBanner(
            title,
            subtitle,
            imageUrl,
            imageKind,
            hasArtIdentity
                ? { ...options, reveal: true, chime: 'forge_reveal' }
                : options,
        );
    }

    sanitizeAchievementImageUrl(value) {
        if (typeof value !== 'string') return undefined;
        const trimmed = value.trim();
        if (!trimmed || trimmed.startsWith('/api/card-image/')) return undefined;
        if (
            trimmed.startsWith('http') ||
            trimmed.startsWith('data:') ||
            trimmed.startsWith('blob:') ||
            trimmed.startsWith('/') ||
            trimmed.startsWith('/assets/') ||
            trimmed.startsWith('assets/')
        ) {
            return trimmed;
        }
        return undefined;
    }

    consumeAndShowCloudAchievementBanners() {
        if (!this.surfaceActive) {
            this.achievementConsumePending = true;
            return Promise.resolve();
        }
        if (!MoonfallCloud?.users) return Promise.resolve();
        if (this.achievementConsumeInFlight) {
            this.achievementConsumePending = true;
            return this.achievementConsumeInFlight;
        }

        this.achievementConsumePending = false;
        const consume = this.performCloudAchievementConsumption();
        const trackedConsume = consume.finally(() => {
            if (this.achievementConsumeInFlight !== trackedConsume) return;
            this.achievementConsumeInFlight = null;
            if (this.achievementConsumePending && this.surfaceActive) {
                void this.consumeAndShowCloudAchievementBanners();
            }
        });
        this.achievementConsumeInFlight = trackedConsume;
        return trackedConsume;
    }

    async performCloudAchievementConsumption() {
        if (!this.surfaceActive || !MoonfallCloud?.users) return;
        const accountBoundaryGeneration = this.accountBoundaryGeneration;
        const accountStorageKey = this.accountStorageKey;
        const accountUid = this.user?.uid || null;
        const isSameAccount = () => (
            !this.destroyed
            && this.accountBoundaryGeneration === accountBoundaryGeneration
            && this.accountStorageKey === accountStorageKey
        );
        const isCurrentAccount = () => (
            isSameAccount()
            && this.surfaceActive
        );

        let bannerDelayMs = 0;
        const queueBanner = (title, subtitle, imageUrl, imageKind, bannerOptions) => {
            if (!isCurrentAccount()) return;
            this.scheduleSurfaceTimer(
                () => {
                    if (isCurrentAccount()) {
                        this.showAchievementBanner(
                            title,
                            subtitle,
                            imageUrl,
                            imageKind,
                            bannerOptions,
                        );
                    }
                },
                bannerDelayMs
            );
            bannerDelayMs += 4500;
        };

        try {
            if (StellarFirstBannerDelivery) {
                const stellarShown = await StellarFirstBannerDelivery.request({
                    requesterId: 'foundry',
                    profileId: accountUid,
                    isIdentityCurrent: () => (
                        !accountUid
                        || Firebase?.getUser?.()?.uid === accountUid
                    ),
                    isActive: () => this.surfaceActive && !this.destroyed,
                });
                if (!isCurrentAccount()) return;
                if (stellarShown) bannerDelayMs = 4500;
            }

            let newUnlocks = [];
            if (typeof MoonfallCloud.users.consumeNewCardbackUnlocks === 'function') {
                const response = await MoonfallCloud.users.consumeNewCardbackUnlocks();
                if (!isCurrentAccount()) return;
                if (Array.isArray(response?.newUnlocks)) {
                    newUnlocks = response.newUnlocks;
                }
            }

            if (newUnlocks.length > 0 && MoonfallCloud?.cardbacks) {
                let cardbacks = [];
                try {
                    cardbacks = await MoonfallCloud.cardbacks.list() || [];
                } catch (error) {
                    console.warn('[Foundry] Cardback catalog unavailable; retaining unlock identity.', error);
                }
                if (!isCurrentAccount()) return;
                newUnlocks.forEach((slug) => {
                    const cardback = cardbacks.find((item) => item.slug === slug);
                    queueBanner(
                        `🎴 ${uiText(
                            'collection.achievements.cardbackUnlocked',
                            'Cardback Unlocked!'
                        )}`,
                        cardback?.name || slug,
                        cardback?.imageUrl,
                        'cardback',
                        { cardbackSlug: slug },
                    );
                });
            }
        } catch (err) {
            console.warn('[Foundry] Failed to show cardback unlock banners:', err);
        }

        if (!isSameAccount()) return;
        if (!this.surfaceActive) {
            this.achievementConsumePending = true;
            return;
        }

        try {
            let newAchievements = [];
            if (typeof MoonfallCloud.users.consumeNewRecipeDiscoveryAchievements === 'function') {
                const response = await MoonfallCloud.users.consumeNewRecipeDiscoveryAchievements();
                if (!isCurrentAccount()) return;
                if (Array.isArray(response?.newAchievements)) newAchievements = response.newAchievements;
            }
            newAchievements.forEach((achievement) => {
                const cardId = typeof achievement?.cardId === 'string' ? achievement.cardId : '';
                const cardName = achievement?.cardName || cardId || uiText(
                    'collection.achievements.unknownCard',
                    'Unknown Card'
                );
                queueBanner(
                    uiText('collection.achievements.recipeDiscovery', 'Recipe Discovered!'),
                    uiText(
                        'collection.achievements.recipeDiscoverySubtitle',
                        '{{cardName}} is now craftable from Collection.',
                        { cardName }
                    ),
                    this.sanitizeAchievementImageUrl(achievement?.imageUrl),
                    'card',
                    cardId ? { cardId } : undefined
                );
            });
        } catch (err) {
            console.warn('[Foundry] Failed to show recipe-discovery banners:', err);
        }

        try {
            let newAchievements = [];
            if (typeof MoonfallCloud.users.consumeNewFirstDiscoveryAchievements === 'function') {
                const response = await MoonfallCloud.users.consumeNewFirstDiscoveryAchievements();
                if (!isCurrentAccount()) return;
                if (Array.isArray(response?.newAchievements)) {
                    newAchievements = response.newAchievements;
                }
            }

            const resolvedAchievements = await Promise.all(newAchievements.map(async (achievement) => {
                const cardId = typeof achievement?.cardId === 'string' ? achievement.cardId : '';
                const cardName = achievement?.cardName || cardId || uiText(
                    'collection.achievements.unknownCard',
                    'Unknown Card'
                );
                const discovererName = achievement?.discovererName || uiText('collection.detail.unknown', 'Unknown');
                const subtitle = uiText(
                    'collection.achievements.firstDiscoverySubtitle',
                    "{{cardName}} was first discovered by {{discovererName}}. Your name is now recorded as this card's discoverer.",
                    { cardName, discovererName }
                );
                return {
                    cardId,
                    subtitle,
                    imageUrl: this.sanitizeAchievementImageUrl(achievement?.imageUrl),
                };
            }));
            if (!isCurrentAccount()) return;

            resolvedAchievements.forEach(({ cardId, subtitle, imageUrl }) => {
                queueBanner(
                    `🌙 ${uiText('collection.achievements.firstDiscovery', 'First Discovery!')}`,
                    subtitle,
                    imageUrl,
                    'card',
                    cardId ? { cardId } : undefined,
                );
            });
        } catch (err) {
            console.warn('[Foundry] Failed to show first-discovery banners:', err);
        }
    }

    getElementCanvasCoordinates(el) {
        const x = Number.parseFloat(el?.style?.left);
        const y = Number.parseFloat(el?.style?.top);
        return {
            x: Number.isFinite(x) ? Math.round(x) : Math.round(el?.offsetLeft || 0),
            y: Number.isFinite(y) ? Math.round(y) : Math.round(el?.offsetTop || 0),
        };
    }

    readWorkspaceCanvasMetrics() {
        const playRect = this.playArea?.getBoundingClientRect?.() || {};
        const width = Number(playRect.width) || Number(this.playArea?.clientWidth) || 0;
        const height = Number(playRect.height) || Number(this.playArea?.clientHeight) || 0;
        return { width, height };
    }

    captureWorkspaceCanvasMetrics() {
        const { width, height } = this.readWorkspaceCanvasMetrics();
        if (width > 1 && height > 1) {
            const storedWidth = this.workspaceCanvasWidth;
            const storedHeight = this.workspaceCanvasHeight;
            const geometryChanged = storedWidth > 1
                && storedHeight > 1
                && (
                    Math.abs(storedWidth - width) >= 0.5
                    || Math.abs(storedHeight - height) >= 0.5
                );
            if (geometryChanged && this.elements?.some?.((entry) => entry?.el?.isConnected)) {
                this.reprojectCanvasElementsToCurrentSize();
                return {
                    width: this.workspaceCanvasWidth,
                    height: this.workspaceCanvasHeight,
                };
            }
            this.workspaceCanvasWidth = width;
            this.workspaceCanvasHeight = height;
        }
        return {
            width: this.workspaceCanvasWidth || Math.max(1, width),
            height: this.workspaceCanvasHeight || Math.max(1, height),
        };
    }

    getCanonicalCanvasPosition(entry, width, height) {
        const normalized = entry?.normalizedPosition;
        if (Number.isFinite(normalized?.x) && Number.isFinite(normalized?.y)) {
            return {
                x: Math.min(1, Math.max(0, normalized.x)),
                y: Math.min(1, Math.max(0, normalized.y)),
            };
        }
        const { x, y } = this.getElementCanvasCoordinates(entry?.el);
        return {
            x: Math.min(1, Math.max(0, x / Math.max(1, width))),
            y: Math.min(1, Math.max(0, y / Math.max(1, height))),
        };
    }

    /** Where a saved fraction puts a chip on a width x height canvas, kept wholly inside it. */
    projectCanvasSpot(el, normalized, width, height) {
        const rect = el.getBoundingClientRect?.() || {};
        const elementWidth = Math.max(0, Number(el.offsetWidth) || Number(rect.width) || 0);
        const elementHeight = Math.max(0, Number(el.offsetHeight) || Number(rect.height) || 0);
        const targetX = Math.round(normalized.x * width);
        const targetY = Math.round(normalized.y * height);
        return {
            x: Math.max(0, Math.min(targetX, Math.max(0, width - elementWidth))),
            y: Math.max(0, Math.min(targetY, Math.max(0, height - elementHeight))),
        };
    }

    positionCanvasElement(entry, normalized, width, height) {
        const el = entry?.el;
        if (!el) return;
        const { x, y } = this.projectCanvasSpot(el, normalized, width, height);
        // A re-projection that lands a chip where it already is writes nothing.
        const left = `${x}px`;
        const top = `${y}px`;
        if (el.style.left !== left) el.style.left = left;
        if (el.style.top !== top) el.style.top = top;
    }

    setCanonicalCanvasPosition(el, normalized, { position = false } = {}) {
        const entry = this.elements.find((item) => item.el === el);
        if (!entry || !Number.isFinite(normalized?.x) || !Number.isFinite(normalized?.y)) return;
        entry.normalizedPosition = {
            x: Math.min(1, Math.max(0, Number(normalized.x))),
            y: Math.min(1, Math.max(0, Number(normalized.y))),
        };
        if (position) {
            const { width, height } = this.captureWorkspaceCanvasMetrics();
            this.positionCanvasElement(entry, entry.normalizedPosition, width, height);
        }
    }

    clearCanonicalCanvasPosition(el) {
        const entry = this.elements.find((item) => item.el === el);
        if (entry) entry.normalizedPosition = null;
    }

    reprojectCanvasElementsToCurrentSize() {
        const current = this.readWorkspaceCanvasMetrics();
        if (current.width <= 1 || current.height <= 1) return false;
        const previous = {
            width: this.workspaceCanvasWidth || current.width,
            height: this.workspaceCanvasHeight || current.height,
        };
        const geometryChanged = Math.abs(previous.width - current.width) >= 0.5
            || Math.abs(previous.height - current.height) >= 0.5;
        if (!geometryChanged) {
            // Chips restored behind a hidden panel had no size to separate by.
            if (this.canvasChipSeparationPending && this.separateOverlappingChips(current.width, current.height)) {
                this.scheduleWorkspaceSave();
            }
            return false;
        }

        this.elements.forEach((entry) => {
            if (!entry?.el?.isConnected) return;
            const normalized = this.getCanonicalCanvasPosition(entry, previous.width, previous.height);
            // Keep the semantic normalized position separate from the clamped
            // presentation. A narrow canvas must not silently rewrite workspace
            // persistence merely because a chip needs to remain fully visible.
            entry.normalizedPosition = normalized;
            this.positionCanvasElement(entry, normalized, current.width, current.height);
        });
        const lifted = this.separateOverlappingChips(current.width, current.height);
        this.workspaceCanvasWidth = current.width;
        this.workspaceCanvasHeight = current.height;
        if (lifted) this.scheduleWorkspaceSave();
        return true;
    }

    /**
     * Keep projected chips off one another.
     *
     * A saved position is a fraction of the canvas while a chip keeps its
     * pixel size, so a layout made on a big canvas can land chips on top of
     * each other on a small one. A player's desktop workspace, opened on their
     * phone's 715x371 canvas, stacked four pairs 31-44% deep, and a drop whose
     * centre lands on another chip is a paid fuse. Only a chip that overlaps
     * another moves, to the nearest free spot, for display only: its saved
     * fraction stays the player's, so the roomy layout returns on a roomy
     * canvas. A chip resting clear of the others stays where it shows and
     * blocks, as does one placed since the last projection (it has no
     * fraction): spreading every chip in canvas order let a stack's chip take
     * a free spot first and push the chip resting there aside, so a card the
     * player had placed came back 16px lower (dev, 22 October 2026). When the
     * canvas is too crowded for nudging, the chips that overlap are shelved,
     * widest first, in rows from the top, and failing that every chip is. A
     * chip that fits nowhere stays where it was projected.
     *
     * Chips SAVED on one spot overlap on every canvas: that is broken data,
     * not a small screen. They move rather than a chip they lie on, and the
     * writer saves the canvas as it shows (returns how many chips that saves
     * where they show), so every visit after shows them there.
     */
    separateOverlappingChips(width, height) {
        const chips = [];
        let measured = true;
        this.elements.forEach((entry) => {
            const el = entry?.el;
            if (!el?.isConnected || el.classList.contains('dragging')) return;
            const w = Number(el.offsetWidth) || 0;
            const h = Number(el.offsetHeight) || 0;
            if (w <= 0 || h <= 0) {
                measured = false;
                return;
            }
            chips.push({ entry, el, projected: Boolean(entry.normalizedPosition), ...this.getElementCanvasCoordinates(el), w, h });
        });
        this.canvasChipSeparationPending = !measured;
        const fixed = chips.filter((chip) => !chip.projected);
        const projected = chips.filter((chip) => chip.projected);
        const stacked = new Set(projected.filter((chip) => projected.some((other) => (
            other !== chip
            && other.entry.normalizedPosition.x === chip.entry.normalizedPosition.x
            && other.entry.normalizedPosition.y === chip.entry.normalizedPosition.y
        ))));
        // A chip saved on another's spot moves, and never pushes a chip that
        // lies only on it out of the way.
        const liesOnAnother = (chip) => chips.some((other) => (
            other !== chip
            && (stacked.has(chip) || !stacked.has(other))
            && !canvasSpotIsClear(chip, chip.x, chip.y, [other], 0)
        ));
        const resting = [...fixed, ...projected.filter((chip) => !liesOnAnother(chip))];
        const loose = projected.filter((chip) => !resting.includes(chip));
        // A chip moved for the player stays out of the band a drag keeps clear
        // of, where a phone's edge gesture takes the touch.
        const usableHeight = Math.max(0, height - this.readCanvasDropFloor());
        // A chip that fits nowhere keeps the spot it was projected to.
        const arrange = (order, findSpot, around) => {
            const placed = [...around];
            const spots = new Map();
            let stranded = 0;
            for (const chip of order) {
                const spot = findSpot(chip, placed, width, usableHeight);
                if (!spot) stranded += 1;
                spots.set(chip, spot || chip);
                placed.push({ ...chip, ...(spot || {}) });
            }
            return { spots, stranded };
        };
        // Nudging moves the fewest chips; shelving fits more once a canvas is
        // crowded (measured: 20 random chips on a phone canvas strand 6
        // nudged, none shelved). Each is tried only while the last stranded a
        // chip, and the fewest stranded wins, the fewest moved on a tie: a
        // full canvas re-shelved around the one chip that fit nowhere moved
        // every other chip and still left one on another (phone, 22 October 2026).
        const widestFirst = (list) => [...list].sort((a, b) => (b.w - a.w) || (a.y - b.y) || (a.x - b.x));
        const passes = [
            () => arrange(loose, findFreeCanvasSpot, resting),
            () => arrange(widestFirst(loose), findFirstFreeCanvasSpot, resting),
            () => arrange(widestFirst(projected), findFirstFreeCanvasSpot, fixed),
        ];
        let best = null;
        for (const pass of passes) {
            const arranged = pass();
            if (!best || arranged.stranded < best.stranded) best = arranged;
            if (!best.stranded) break;
        }
        best.spots.forEach((spot, chip) => {
            if (spot.x === chip.x && spot.y === chip.y) return;
            chip.el.style.left = `${spot.x}px`;
            chip.el.style.top = `${spot.y}px`;
        });
        if (this.workspaceMode !== 'writer') return 0;
        // The spread the player sees is built around the stack, and cannot be
        // built again once the stack is saved apart: the whole canvas is saved
        // as it shows, or only the stack would come back where it showed.
        const away = projected.filter((chip) => {
            const shown = this.getElementCanvasCoordinates(chip.el);
            const projectedSpot = this.projectCanvasSpot(chip.el, chip.entry.normalizedPosition, width, height);
            return shown.x !== projectedSpot.x || shown.y !== projectedSpot.y;
        });
        if (!away.some((chip) => stacked.has(chip))) return 0;
        away.forEach((chip) => { chip.entry.normalizedPosition = null; });
        return away.length;
    }

    /**
     * A chip coming to rest on another steps to the nearest free spot. The
     * rest is saved where it lands, so no chip is ever saved lying on a
     * neighbour: that hid the neighbour's name, and the next projection would
     * separate the pair anyway, so the canvas would differ between visits.
     * Touching is not overlapping; only a real overlap moves the chip.
     * Returns whether it moved.
     */
    settleChipClearOfOthers(el) {
        if (!el?.isConnected) return false;
        const { width, height } = this.readWorkspaceCanvasMetrics();
        const w = Number(el.offsetWidth) || 0;
        const h = Number(el.offsetHeight) || 0;
        if (width <= 1 || height <= 1 || w <= 0 || h <= 0) return false;
        const others = this.elements
            .filter((entry) => entry.el !== el && entry.el?.isConnected)
            .map(({ el: other }) => ({
                ...this.getElementCanvasCoordinates(other),
                w: Number(other.offsetWidth) || 0,
                h: Number(other.offsetHeight) || 0,
            }))
            .filter((other) => other.w > 0 && other.h > 0);
        const at = this.getElementCanvasCoordinates(el);
        const usableHeight = Math.max(0, height - this.readCanvasDropFloor());
        const spot = findFreeCanvasSpot({ ...at, w, h }, others, width, usableHeight);
        if (!spot || (spot.x === at.x && spot.y === at.y)) return false;
        el.style.left = `${spot.x}px`;
        el.style.top = `${spot.y}px`;
        return true;
    }

    normalizeWorkspaceElements(rawElements) {
        if (!Array.isArray(rawElements)) return [];
        const seen = new Set();
        return rawElements.slice(0, MAX_FOUNDRY_WORKSPACE_ELEMENTS).map((entry) => {
            if (!entry || typeof entry !== 'object') return null;
            const elementId = String(entry.elementId || '').trim().slice(0, 128);
            const kind = entry.kind;
            const cardId = String(entry.cardId || '').trim().slice(0, 128);
            const operationId = String(entry.operationId || '').trim().slice(0, 128);
            const sourceOperationId = String(entry.sourceOperationId || '').trim().slice(0, 128);
            const x = Number(entry.x);
            const y = Number(entry.y);
            if (
                !elementId
                || seen.has(elementId)
                || !Number.isFinite(x)
                || !Number.isFinite(y)
                || (kind === 'card' && (!cardId || operationId))
                || (kind === 'discovery_operation' && (!operationId || cardId || sourceOperationId))
                || (kind !== 'card' && kind !== 'discovery_operation')
            ) return null;
            seen.add(elementId);
            return {
                elementId,
                kind,
                ...(kind === 'card'
                    ? { cardId, ...(sourceOperationId ? { sourceOperationId } : {}) }
                    : { operationId }),
                x: Math.min(1, Math.max(0, x)),
                y: Math.min(1, Math.max(0, y)),
            };
        }).filter(Boolean);
    }

    buildWorkspaceElements() {
        const { width, height } = this.captureWorkspaceCanvasMetrics();
        return this.elements
            .map((entry) => {
                const el = entry?.el;
                if (!el || !el.isConnected) return null;

                const { x, y } = this.getElementCanvasCoordinates(el);
                const operationId = String(el.dataset.foundryOperationId || '').trim();
                const cardId = String(entry?.card?.cardId || el.dataset.cardId || '').trim();
                if (!operationId && (!cardId || cardId === 'loading')) return null;
                const elementId = String(el.dataset.foundryElementId || createFoundryCanvasElementId());
                el.dataset.foundryElementId = elementId;

                const position = entry.normalizedPosition
                    ? this.getCanonicalCanvasPosition(entry, width, height)
                    : {
                        x: Math.min(1, Math.max(0, x / width)),
                        y: Math.min(1, Math.max(0, y / height)),
                    };
                if (operationId) {
                    return { elementId, kind: 'discovery_operation', operationId, ...position };
                }
                const sourceOperationId = String(el.dataset.foundrySourceOperationId || '').trim();
                return {
                    elementId,
                    kind: 'card',
                    cardId,
                    ...(sourceOperationId ? { sourceOperationId } : {}),
                    ...position,
                };
            })
            .filter(Boolean);
    }

    canEditWorkspace() {
        return this.workspaceLoaded && this.workspaceProjected && this.workspaceMode === 'writer';
    }

    canAddWorkspaceElement() {
        if (this.elements.length < MAX_FOUNDRY_WORKSPACE_ELEMENTS) return true;
        console.warn('[Foundry] Workspace capacity reached.');
        this.setWorkspaceMode(this.workspaceMode, 'unsaved', 'capacity');
        return false;
    }

    setWorkspaceMode(mode, messageKey = null, noticeOwner = 'general') {
        this.workspaceMode = mode === 'writer' ? 'writer' : 'read_only';
        const surfaceRoot = this.root === document
            ? (document.querySelector('[data-foundry-surface-root]') || document.body)
            : this.root;
        surfaceRoot?.classList?.toggle?.('foundry-workspace-read-only', this.workspaceMode === 'read_only');
        if (messageKey) this.workspaceNoticeReasons.set(noticeOwner, messageKey);
        else this.workspaceNoticeReasons.delete(noticeOwner);
        this.renderWorkspaceNotice();
    }

    clearWorkspaceNotice(noticeOwner) {
        this.workspaceNoticeReasons.delete(noticeOwner);
        this.renderWorkspaceNotice();
    }

    clearWorkspaceNotices(noticeOwners = []) {
        noticeOwners.forEach((noticeOwner) => this.workspaceNoticeReasons.delete(noticeOwner));
        this.renderWorkspaceNotice();
    }

    clearAllWorkspaceNotices() {
        this.workspaceNoticeReasons.clear();
        this.renderWorkspaceNotice();
    }

    renderWorkspaceNotice() {
        if (!this.workspaceNotice) return;
        let messageKey = null;
        if (this.workspaceMode === 'read_only') {
            if (this.workspaceLoaded) messageKey = 'read_only';
        } else {
            const reasons = Array.from(this.workspaceNoticeReasons.values());
            if (reasons.includes('conflict')) messageKey = 'conflict';
            else if (reasons.includes('unsaved')) messageKey = 'unsaved';
        }
        if (!messageKey) {
            this.workspaceNotice.hidden = true;
            this.workspaceNotice.classList.add('hidden');
            this.workspaceTakeOverBtn?.classList.add('hidden');
            return;
        }
        const messages = {
            conflict: uiText('foundry.workspace.conflict', 'Your Foundry workspace changed elsewhere. The latest saved version has been loaded.'),
            unsaved: uiText('foundry.workspace.unsaved', 'Changes are not saved yet. Keep this tab open while Moonfall reconnects.'),
            read_only: uiText('foundry.workspace.readOnly', 'This Foundry workspace is open elsewhere. You can view it here, but only the active editor can save.'),
        };
        if (this.workspaceNoticeText) {
            this.workspaceNoticeText.textContent = messages[messageKey];
        }
        this.workspaceNotice.hidden = false;
        this.workspaceNotice.classList.remove('hidden');
        this.workspaceTakeOverBtn?.classList.toggle('hidden', messageKey !== 'read_only');
    }

    /**
     * The recovery holds this save back and writes it when it ends. The
     * "not saved yet" notice is for a player looking at Moon Fuse: raised on
     * a hidden panel or page, or by a closing Foundry (the notice lives in the
     * page and outlasts the instance), it was only ever seen as a flash on the
     * way back, where the return's read retires it (every "not saved yet" in
     * snappy round 13 appeared with a Moon Fuse return; review of the round-13
     * fix, 26 September 2026). The journal keeps the canvas meanwhile.
     * `announce` is false for the snapshot already on the wire.
     */
    noteWorkspaceSaveDeferredByRecovery({ announce = true } = {}) {
        if (this.isWorkspaceRecoveryCurrent(this.workspaceRecoveryState)) {
            this.workspaceRecoveryState.saveWasDeferred = true;
        }
        if (
            announce
            && !this.destroying
            && !this.destroyed
            && this.surfaceActive
            && document.visibilityState !== 'hidden'
        ) {
            this.setWorkspaceMode(this.workspaceMode, 'unsaved', 'recovery');
        }
    }

    scheduleWorkspaceSave({
        force = false,
        clearNoticeOwnerOnSuccess = null,
    } = {}) {
        if (
            this.destroyed
            || !this.user
            || this.workspacePersistenceSuspended
            || !this.canEditWorkspace()
            || this.workspaceSaveBlockedByConflict
        ) return;
        this.writeCrashJournal();

        if (force) {
            if (this.workspaceSaveTimer) {
                clearTimeout(this.workspaceSaveTimer);
                this.workspaceSaveTimer = null;
            }
            void this.persistWorkspaceState({
                clearNoticeOwnerOnSuccess,
                force: true,
            });
            return;
        }

        if (this.workspaceSaveTimer) {
            clearTimeout(this.workspaceSaveTimer);
        }
        this.workspaceSaveTimer = setTimeout(() => {
            this.workspaceSaveTimer = null;
            void this.persistWorkspaceState({ clearNoticeOwnerOnSuccess });
        }, this.workspacePersistDelayMs);
    }

    persistWorkspaceState({
        force = false,
        allowPendingMerge = false,
        allowDestroying = false,
        clearNoticeOwnerOnSuccess = null,
    } = {}) {
        const activeDrain = this.workspaceSaveDrainPromise;
        if (
            (this.destroyed && !allowDestroying)
            || !this.user
            || this.workspacePersistenceSuspended
            || !this.canEditWorkspace()
            || this.workspaceSaveBlockedByConflict
        ) return activeDrain || undefined;
        if (this.pendingMergePairs.size > 0 && !allowPendingMerge) {
            return activeDrain || undefined;
        }
        const elements = this.buildWorkspaceElements();
        const snapshotHash = JSON.stringify(elements);
        this.writeCrashJournal(elements);
        /* NOTHING TO SAVE IS NOT "UNSAVED". Leaving Moon Fuse (a tab switch,
           a close) while the discovery recovery after a workspace read was
           still reading stood this save down for the recovery even when the
           canvas was the one last saved: "Changes are not saved yet" for no
           change, and a crash journal left behind for the next read to call a
           conflict (snappy meter round 13, 25 September 2026). A snapshot the
           server already has settles first; the recovery only holds back a
           canvas that still has something to write. */
        if (
            snapshotHash === this.lastSavedWorkspaceHash
            && !activeDrain
            && !this.workspaceSaveInFlight
            && !this.workspacePendingSaveRequest
        ) {
            this.clearCrashJournal(elements);
            // An unfinished recovery keeps its own notice until it completes.
            if (clearNoticeOwnerOnSuccess && !this.workspaceRecoveryOverflow) {
                this.clearWorkspaceNotice(clearNoticeOwnerOnSuccess);
            }
            return undefined;
        }
        if (this.workspaceRecoveryOverflow) {
            // The very snapshot already on the wire needs no notice, but the
            // recovery still retries it when it ends, in case that save fails.
            const alreadySending = snapshotHash === this.workspaceSaveInFlightHash
                && !this.workspacePendingSaveRequest;
            this.noteWorkspaceSaveDeferredByRecovery({ announce: !alreadySending });
            return activeDrain || undefined;
        }

        const pendingRequest = this.workspacePendingSaveRequest;
        const sharesPendingAuthority = Boolean(
            pendingRequest
            && pendingRequest.accountStorageKey === this.accountStorageKey
            && pendingRequest.authorityGeneration === this.workspaceAuthorityGeneration
            && pendingRequest.projectionGeneration === this.workspaceProjectionGeneration
            && pendingRequest.leaseToken === this.workspaceLeaseToken
        );
        const clearNoticeOwners = new Set([
            ...this.workspaceSaveInFlightNoticeOwners,
            ...(sharesPendingAuthority ? (pendingRequest.clearNoticeOwners || []) : []),
        ]);
        if (clearNoticeOwnerOnSuccess) clearNoticeOwners.add(clearNoticeOwnerOnSuccess);
        this.workspacePendingSaveRequest = {
            accountStorageKey: this.accountStorageKey,
            allowDestroying,
            allowPendingMerge,
            authorityGeneration: this.workspaceAuthorityGeneration,
            clearNoticeOwners: Array.from(clearNoticeOwners),
            elements,
            force: force || (sharesPendingAuthority && pendingRequest.force === true),
            leaseToken: this.workspaceLeaseToken,
            projectionGeneration: this.workspaceProjectionGeneration,
            requiresWrite: Boolean(
                this.workspaceSaveInFlightHash
                && this.workspaceSaveInFlightHash !== snapshotHash
            ),
            snapshotHash,
            user: this.user,
        };
        if (!this.workspaceSaveDrainPromise) {
            const drain = this.drainWorkspaceSaveRequests().catch((error) => {
                console.warn('Failed to drain Foundry workspace saves:', error);
            });
            const trackedDrain = drain.finally(() => {
                if (this.workspaceSaveDrainPromise === trackedDrain) {
                    this.workspaceSaveDrainPromise = null;
                }
            });
            this.workspaceSaveDrainPromise = trackedDrain;
        }
        return this.workspaceSaveDrainPromise;
    }

    async drainWorkspaceSaveRequests() {
        while (this.workspacePendingSaveRequest) {
            const request = this.workspacePendingSaveRequest;
            this.workspacePendingSaveRequest = null;
            await this.persistWorkspaceSnapshot(request);
        }
    }

    /* A FUSION STOPS A SAVE FROM LEAVING, NEVER ITS ANSWER. A save the server
       has answered changed the server's revision whatever began meanwhile;
       throwing that answer away because a fusion started left the fusion's
       own save on the old revision, and the server refused it as a conflict
       (replay 01a16cf7, 24 Oct 2026: the canvas rolled back, the fusion was
       cancelled and "changed elsewhere" stayed up). `answered` judges an
       answer; without it the check is for a save still to be sent. */
    isWorkspaceSaveRequestCurrent(request, { answered = false } = {}) {
        return !(
            (this.destroyed && !this.destroying && !request.allowDestroying)
            || !this.user
            || this.accountStorageKey !== request.accountStorageKey
            || this.workspacePersistenceSuspended
            || !this.canEditWorkspace()
            || this.workspaceSaveBlockedByConflict
            || this.workspaceProjectionGeneration !== request.projectionGeneration
            || this.workspaceAuthorityGeneration !== request.authorityGeneration
            || this.workspaceLeaseToken !== request.leaseToken
            || (!answered && this.pendingMergePairs.size > 0 && !request.allowPendingMerge)
        );
    }

    async persistWorkspaceSnapshot(request) {
        const {
            elements,
            force,
            snapshotHash,
            user,
        } = request;
        if (!this.isWorkspaceSaveRequestCurrent(request)) return;
        // Already saved settles before the recovery can defer it (see persistWorkspaceState).
        if (snapshotHash === this.lastSavedWorkspaceHash && !request.requiresWrite) {
            this.clearCrashJournal(elements);
            if (!this.workspaceRecoveryOverflow) this.clearWorkspaceNotices(request.clearNoticeOwners);
            return;
        }
        if (this.workspaceRecoveryOverflow) {
            this.noteWorkspaceSaveDeferredByRecovery();
            return;
        }

        const save = this.runBoundedRequest(async (signal) => {
            const token = await awaitFoundryRequestStage(user.getIdToken(), signal);
            if (!this.isWorkspaceSaveRequestCurrent(request)) return null;
            const baseRevision = this.workspaceRevision;
            const response = await awaitFoundryRequestStage(fetch('/api/foundry/workspace', {
                method: 'PUT',
                keepalive: force === true,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    leaseToken: request.leaseToken,
                    baseRevision,
                    elements,
                }),
                signal,
            }), signal);
            const payload = await awaitFoundryRequestStage(
                response.json().catch(() => ({})),
                signal,
            );
            return { payload, response };
        }, {
            abortOnHide: false,
            controllerSet: this.workspaceSaveRequestControllers,
        });

        this.workspaceSaveInFlight = save;
        this.workspaceSaveInFlightHash = snapshotHash;
        this.workspaceSaveInFlightNoticeOwners = request.clearNoticeOwners;
        try {
            const result = await save;
            if (!result || !this.isWorkspaceSaveRequestCurrent(request, { answered: true })) return;
            const { payload, response } = result;
            if (payload.outcome === 'conflict' && this.isSnapshotAlreadyOnServer(payload, elements)) {
                /* The server names a newer revision but holds exactly this
                   canvas (another tab saved the same thing, or the server
                   rewrote it unchanged): nothing changed elsewhere and nothing
                   is announced. The revision is adopted; the snapshot is saved. */
                this.workspaceRevision = payload.revision;
                if (Number.isFinite(Number(payload.updatedAt)) && Number(payload.updatedAt) > 0) {
                    this.workspaceUpdatedAt = Number(payload.updatedAt);
                }
                this.lastSavedWorkspaceHash = snapshotHash;
                this.clearCrashJournal(elements);
                if (!this.destroyed) {
                    this.setWorkspaceMode('writer', null, 'save');
                    this.clearWorkspaceNotices([...request.clearNoticeOwners, 'conflict']);
                }
                return;
            }
            if (payload.outcome === 'conflict') {
                this.workspaceSaveBlockedByConflict = true;
                this.workspaceRevision = Number.isSafeInteger(payload.revision) ? payload.revision : this.workspaceRevision;
                if (Number.isFinite(Number(payload.updatedAt)) && Number(payload.updatedAt) > 0) {
                    this.workspaceUpdatedAt = Number(payload.updatedAt);
                }
                if (!this.destroyed) {
                    const applied = await this.applyWorkspaceSnapshot(payload.elements, {
                        authorityAccountStorageKey: request.accountStorageKey,
                        authorityGeneration: request.authorityGeneration,
                    });
                    if (!applied) return;
                    if (!this.isWorkspaceAuthorityCurrent(
                        request.authorityGeneration,
                        request.accountStorageKey,
                    )) return;
                    this.setWorkspaceMode('writer', 'conflict', 'conflict');
                }
                return;
            }
            if (!response.ok || payload.outcome !== 'saved') {
                if (!this.destroyed) {
                    if (payload.outcome === 'lease_lost' || payload.outcome === 'lease_expired') {
                        this.setWorkspaceMode('read_only');
                        this.stopWorkspaceLeaseRenewal();
                    } else {
                        this.setWorkspaceMode('writer', 'unsaved', 'save');
                    }
                }
                return;
            }
            this.workspaceRevision = Number.isSafeInteger(payload.revision)
                ? payload.revision
                : this.workspaceRevision + 1;
            if (Number.isFinite(Number(payload.updatedAt)) && Number(payload.updatedAt) > 0) {
                this.workspaceUpdatedAt = Number(payload.updatedAt);
            }
            this.workspaceLeaseExpiresAt = Number(payload.leaseExpiresAt) || this.workspaceLeaseExpiresAt;
            this.lastSavedWorkspaceHash = snapshotHash;
            this.clearCrashJournal(elements);
            if (!this.destroyed) {
                this.setWorkspaceMode('writer', null, 'save');
                // The server now holds the canvas on screen: a conflict
                // announced earlier is no longer news.
                this.clearWorkspaceNotices([...request.clearNoticeOwners, 'conflict']);
                this.scheduleWorkspaceLeaseRenewal();
            }
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.warn('Failed to persist Foundry workspace:', err);
            }
            if (!this.destroyed && this.isWorkspaceSaveRequestCurrent(request)) {
                this.setWorkspaceMode('writer', 'unsaved', 'save');
            }
        } finally {
            if (this.workspaceSaveInFlight === save) {
                this.workspaceSaveInFlight = null;
                this.workspaceSaveInFlightHash = '';
                this.workspaceSaveInFlightNoticeOwners = [];
            }
        }
    }

    async applyWorkspaceSnapshot(rawElements, {
        authorityGeneration = null,
        authorityAccountStorageKey = null,
    } = {}) {
        /* NEVER PAINT A STUB CHIP. The inventory load and this projection
           race out of handleAuthUser, and when the projection won, every
           canvas chip spawned from a synthetic {name: cardId} stub — an
           uppercased camelCase slug ("🃏 MOONLITASHWARDEN") that then
           reflowed into its real localized name and finally its
           first-discovery gradient: "in a split second it changes from one
           font to another and then again it gets the colored gradients".
           Waiting here (bounded — a dead inventory request must not hold the
           workspace hostage, per docs/asset-loading.md) means the first paint
           is the final form: real name, real emoji, ribbon already known.
           The same holds for a discovery's chip: it painted as a
           "Discovering..." stub with a spinner and turned into its draft (its
           name, its gold "choose your art" border) when its poll answered, on
           every fresh open, and the stub's width spread the chips around it
           differently from the visit before (22 October 2026). Its state is
           read alongside, under its own cap, and the chip paints from it. */
        const discoveryReads = this.readSnapshotDiscoveries(rawElements);
        const discoveryAnswers = new Map();
        const discoveriesRead = discoveryReads.size ? Promise.race([
            Promise.all([...discoveryReads].map(([operationId, read]) => read.then(
                (answer) => { discoveryAnswers.set(operationId, answer); },
                () => {},
            ))),
            new Promise((resolve) => setTimeout(resolve, DISCOVERY_FIRST_READ_CAP_MS)),
        ]) : null;
        if (this.inventoryLoadPromise) {
            await Promise.race([
                this.inventoryLoadPromise,
                new Promise((resolve) => setTimeout(resolve, 4000)),
            ]);
        }
        if (discoveriesRead) await discoveriesRead;
        this.retireWorkspaceRecovery();
        const projectionGeneration = ++this.workspaceProjectionGeneration;
        const elements = this.normalizeWorkspaceElements(rawElements);
        const { width, height } = this.captureWorkspaceCanvasMetrics();
        this.workspacePersistenceSuspended = true;
        this.workspaceProjected = false;
        /* A RE-READ RECONCILES, CHIP BY CHIP. Returning to Moon Fuse, a page
           return and a save conflict all re-read the workspace, and the
           projection removed every chip and spawned it again. A discovery
           still forging came back as a "Discovering..." stub with a spinner
           and without its gold "choose your art" border until its poll
           answered a round trip later, on every tab or app return for as long
           as it stayed pending (flicker hunt, 25 September 2026). A chip the
           snapshot still lists as the same card or the same discovery keeps
           its node, content and state and is only moved; only what the
           snapshot no longer lists is removed, only what is missing spawned. */
        const previousForges = new Map(this.pendingForges);
        const kept = this.matchProjectedElements(elements);
        const keptEntries = new Set(kept.values());
        this.pendingForges.clear();
        this.materializedOperationIds.clear();
        this.elements.slice().forEach((entry) => {
            if (!keptEntries.has(entry)) this.removeElement(entry.el, { skipSessionSave: true });
        });
        const projected = [];
        const pendingHydration = [];
        const discoveryPolls = [];
        elements.forEach((saved) => {
            const x = Math.round(saved.x * width);
            const y = Math.round(saved.y * height);
            const keptEntry = kept.get(saved.elementId) || null;
            if (keptEntry) {
                /* A chip whose saved spot the re-read did not change stays where
                   it shows, which can be off that spot when it was spread off
                   another. Spreading it again from its saved spot, beside chips
                   that had changed size since (a discovery's stub naming its
                   draft), moved chips nobody had touched (22 October 2026). */
                const unchanged = keptEntry.normalizedPosition?.x === saved.x
                    && keptEntry.normalizedPosition?.y === saved.y;
                keptEntry.normalizedPosition = { x: saved.x, y: saved.y };
                if (!unchanged) this.positionCanvasElement(keptEntry, keptEntry.normalizedPosition, width, height);
                projected.push(keptEntry);
            }
            if (saved.kind === 'card') {
                if (keptEntry) {
                    const { el } = keptEntry;
                    const sourceOperationId = /** @type {{ sourceOperationId?: string }} */ (saved).sourceOperationId;
                    if (sourceOperationId) {
                        if (el.dataset.foundrySourceOperationId !== sourceOperationId) {
                            el.dataset.foundrySourceOperationId = sourceOperationId;
                        }
                        this.materializedOperationIds.add(sourceOperationId);
                    } else if (el.dataset.foundrySourceOperationId !== undefined) {
                        delete el.dataset.foundrySourceOperationId;
                    }
                    return;
                }
                const cached = this.inventoryById.get(saved.cardId);
                /* A card the settled inventory does not know (rare: purged or
                   foreign) hydrates via fetchCardDetails below — until then
                   the chip wears a neutral ellipsis. The internal cardId is an
                   English camelCase slug and must never reach the screen. */
                const el = this.spawnElement(cached || {
                    cardId: saved.cardId,
                    name: '…',
                    type: 'standard',
                }, x, y, {
                    elementId: saved.elementId,
                    normalizedPosition: { x: saved.x, y: saved.y },
                    skipSessionSave: true,
                });
                if (saved.sourceOperationId) {
                    el.dataset.foundrySourceOperationId = saved.sourceOperationId;
                    this.materializedOperationIds.add(saved.sourceOperationId);
                }
                if (!cached) {
                    pendingHydration.push(this.fetchCardDetails(saved.cardId).then((card) => {
                        if (card && el.isConnected) this.updateElementCard(el, card, { skipSessionSave: true });
                    }));
                }
                projected.push(this.elements.find((entry) => entry.el === el));
                return;
            }

            const operationId = /** @type {{ operationId: string }} */ (saved).operationId;
            let el = keptEntry?.el || null;
            if (!el) {
                el = this.spawnElement({
                    cardId: 'loading',
                    name: uiText('foundry.discovery.discovering', 'Discovering...'),
                    type: 'forged',
                }, x, y, {
                    elementId: saved.elementId,
                    normalizedPosition: { x: saved.x, y: saved.y },
                    skipSessionSave: true,
                });
                el.dataset.foundryOperationId = operationId;
                el.dataset.pendingForgeId = operationId;
                this.setElementLoading(el, true, { skipSessionSave: true });
                const spawnedEl = el;
                projected.push(this.elements.find((entry) => entry.el === spawnedEl));
            }
            // A kept chip keeps the poll that owns it (and a fresh merge's
            // pending "magic" cue); it is polled again for news from away.
            const previous = previousForges.get(operationId);
            const context = previous?.element === el ? previous : {
                element: el,
                x,
                y,
                inventoryUpdated: false,
                mergeCompleteSoundPlayed: true,
            };
            this.pendingForges.set(operationId, context);
            this.materializedOperationIds.add(operationId);
            if (this.surfaceActive) discoveryPolls.push([operationId, context]);
        });
        this.orderProjectedElements(projected);
        // Each discovery's chip takes the state its read found once the canvas
        // is in order, so the canvas is separated by the sizes the player
        // sees; a read still out is the one its poll waits for.
        discoveryPolls.forEach(([operationId, context]) => {
            pendingHydration.push(this.pollForResult(operationId, context, {
                answered: discoveryAnswers.get(operationId) || null,
                read: discoveryReads.get(operationId) || null,
            }));
        });
        // Before the first await, so the first paint is already separated.
        const lifted = this.separateOverlappingChips(width, height);
        await Promise.allSettled(pendingHydration);
        if (
            projectionGeneration !== this.workspaceProjectionGeneration
            || (authorityGeneration !== null
                && !this.isWorkspaceAuthorityCurrent(
                    authorityGeneration,
                    authorityAccountStorageKey,
                ))
        ) return false;
        this.workspaceRecoveryOverflow = false;
        this.workspacePersistenceSuspended = false;
        this.workspaceProjected = true;
        // Chips saved on one spot were lifted to where they show: save them there.
        if (lifted) this.scheduleWorkspaceSave();
        this.settleFirstProjection();
        /* A switch held for the canvas (prepareEntry) waited for exactly
           this: the discovery recovery after it is news that may arrive
           later, as it is for the Foundry's own first view. Holding on for
           it added two more reads (round 14: 0.87 s) and ran every first
           switch into the hand-over's cap. */
        this.endEntryPreparation();
        return true;
    }

    /**
     * The chips on the canvas a snapshot still lists, by element id: a card
     * chip showing the same card (and not locked by a merge in flight), or
     * the chip of the same pending discovery. Everything else is re-projected.
     */
    matchProjectedElements(elements) {
        const onCanvas = new Map();
        this.elements.forEach((entry) => {
            const elementId = entry?.el?.isConnected ? entry.el.dataset.foundryElementId : '';
            if (elementId && !onCanvas.has(elementId)) onCanvas.set(elementId, entry);
        });
        const kept = new Map();
        elements.forEach((saved) => {
            const entry = onCanvas.get(saved.elementId);
            const el = entry?.el;
            if (!el) return;
            const same = saved.kind === 'card'
                ? !this.isElementLocked(el)
                    && String(entry.card?.cardId || el.dataset.cardId || '') === saved.cardId
                : el.dataset.foundryOperationId === saved.operationId;
            if (same) kept.set(saved.elementId, entry);
        });
        return kept;
    }

    /**
     * Put the canvas in the snapshot's order, which is also its stacking
     * order. A chip already in its place is not touched: re-inserting a node
     * restarts its animations and drops its focus.
     */
    orderProjectedElements(ordered) {
        this.elements = ordered;
        let previous = null;
        ordered.forEach(({ el }) => {
            const expected = previous
                ? previous.nextElementSibling
                : this.canvasElements.firstElementChild;
            if (el !== expected) this.canvasElements.insertBefore(el, expected);
            previous = el;
        });
    }

    retireWorkspaceRecovery() {
        this.workspaceRecoveryRequestControllers.forEach((controller) => controller.abort());
        this.workspaceRecoveryRequestControllers.clear();
        if (this.workspaceRecoveryTimer) clearTimeout(this.workspaceRecoveryTimer);
        this.workspaceRecoveryTimer = null;
        this.workspaceRecoveryState = null;
        this.workspaceRecoveryInFlight = null;
        this.workspaceRecoveryOverflow = false;
        this.clearWorkspaceNotice('recovery');
    }

    async recoverParticipantOperations(authToken = null) {
        if (!this.user || !this.surfaceActive || this.destroyed) return;
        if (this.workspaceRecoveryInFlight) return this.workspaceRecoveryInFlight;
        const recovery = this.performParticipantOperationRecovery(authToken);
        const recoveryState = this.workspaceRecoveryState;
        this.workspaceRecoveryInFlight = recovery;
        try {
            return await recovery;
        } catch (err) {
            if (
                this.workspaceRecoveryInFlight !== recovery
                || !this.isWorkspaceRecoveryCurrent(recoveryState)
            ) return undefined;
            if (err?.name !== 'AbortError') {
                console.warn('Failed to recover Foundry discovery operations:', err);
            }
            recoveryState.deferred = true;
            this.workspaceRecoveryOverflow = true;
            this.setWorkspaceMode(this.workspaceMode, 'unsaved', 'recovery');
            this.scheduleParticipantOperationRecovery(5000);
            return undefined;
        } finally {
            if (this.workspaceRecoveryInFlight === recovery) {
                this.workspaceRecoveryInFlight = null;
            }
        }
    }

    /**
     * Every discovery the canvas already represents.
     *
     * A pending discovery carries `foundryOperationId`; once it resolves, that
     * key is deleted and the resulting card carries `foundrySourceOperationId`
     * instead. Both mean "this operation is already on the canvas" — reading
     * only the first makes recovery re-add a finished discovery, and a canvas
     * holding two elements for one operation is one the server refuses to save
     * from then on.
     */
    collectMaterializedOperationIds() {
        return new Set([
            ...this.materializedOperationIds,
            ...this.elements.flatMap(({ el }) => [
                String(el?.dataset?.foundryOperationId || ''),
                String(el?.dataset?.foundrySourceOperationId || ''),
            ]).filter(Boolean),
        ]);
    }

    async performParticipantOperationRecovery(authToken = null) {
        const accountStorageKey = this.accountStorageKey;
        const authorityGeneration = this.workspaceAuthorityGeneration;
        const user = this.user;
        const projectionGeneration = this.workspaceProjectionGeneration;
        let recovery = this.workspaceRecoveryState;
        if (
            !recovery
            || recovery.accountStorageKey !== accountStorageKey
            || recovery.authorityGeneration !== authorityGeneration
            || recovery.projectionGeneration !== projectionGeneration
        ) {
            const statuses = [{
                status: 'paid',
                states: new Set(['generating_text', 'generating_art', 'awaiting_art_selection', 'complete']),
            }];
            if (Number.isFinite(this.workspaceUpdatedAt) && this.workspaceUpdatedAt > 0) {
                statuses.push({
                    status: 'fulfilled',
                    states: new Set(['complete']),
                    createdAfter: this.workspaceUpdatedAt,
                });
            }
            recovery = {
                accountStorageKey,
                authorityGeneration,
                canvasChanged: false,
                cursor: null,
                deferred: false,
                projectionGeneration,
                seenCursors: new Set(),
                saveWasDeferred: false,
                statusIndex: 0,
                statuses,
                wasBlocked: this.workspaceRecoveryOverflow,
            };
            this.workspaceRecoveryState = recovery;
        }
        this.workspaceRecoveryOverflow = true;
        const token = authToken || await this.runBoundedRequest((signal) => (
            awaitFoundryRequestStage(user.getIdToken(), signal)
        ), { controllerSet: this.workspaceRecoveryRequestControllers });
        if (!this.isWorkspaceRecoveryCurrent(recovery)) return;
        const operationIds = this.collectMaterializedOperationIds();
        const { width, height } = this.captureWorkspaceCanvasMetrics();
        let pagesProcessed = 0;

        while (
            recovery.statusIndex < recovery.statuses.length
            && pagesProcessed < MAX_FOUNDRY_RECOVERY_PAGES_PER_PASS
        ) {
            const recoveryStatus = recovery.statuses[recovery.statusIndex];
            let reachedWorkspaceHistory = false;
            if (!this.isWorkspaceRecoveryCurrent(recovery)) return;
            const query = new URLSearchParams({
                limit: '50',
                status: recoveryStatus.status,
            });
            if (recovery.cursor) query.set('cursor', recovery.cursor);
            const { response, payload } = await this.runBoundedRequest(async (signal) => {
                const response = await awaitFoundryRequestStage(fetch(
                    `/api/foundry/discoveries?${query.toString()}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'X-Moonfall-Language': i18n?.getLanguage?.() || 'en',
                        },
                        signal,
                    },
                ), signal);
                const payload = await awaitFoundryRequestStage(
                    response.json().catch(() => ({})),
                    signal,
                );
                return { payload, response };
            }, {
                controllerSet: this.workspaceRecoveryRequestControllers,
                timeoutMs: Math.min(
                    this.operationRequestTimeoutMs,
                    FOUNDRY_RECOVERY_PAGE_TIMEOUT_MS,
                ),
            });
            if (!this.isWorkspaceRecoveryCurrent(recovery)) return;
            if (!response.ok) {
                recovery.deferred = true;
                this.setWorkspaceMode(this.workspaceMode, 'unsaved', 'recovery');
                this.scheduleParticipantOperationRecovery(5000);
                return;
            }

            const items = Array.isArray(payload.items) ? payload.items : [];
            for (const operation of items) {
                if (recoveryStatus.status === 'fulfilled') {
                    const participantCreatedAt = Number(operation?.participantCreatedAt);
                    if (
                        !Number.isFinite(participantCreatedAt)
                        || participantCreatedAt <= recoveryStatus.createdAfter
                    ) {
                        reachedWorkspaceHistory = true;
                        break;
                    }
                }

                const operationId = String(operation?.operationId || '').trim();
                if (
                    !operationId
                    || operation.participantStatus !== recoveryStatus.status
                    || !recoveryStatus.states.has(operation.state)
                ) continue;
                if (operationIds.has(operationId)) continue;
                const sourceReplacement = this.findRecoverableOperationSources(operation);
                if (!sourceReplacement && this.elements.length >= MAX_FOUNDRY_WORKSPACE_ELEMENTS) {
                    if (recovery.canvasChanged) this.writeCrashJournal();
                    recovery.deferred = true;
                    this.setWorkspaceMode(this.workspaceMode, 'unsaved', 'recovery');
                    this.scheduleParticipantOperationRecovery(5000);
                    console.warn('[Foundry] Workspace capacity reached while recovering discovery operations.');
                    return;
                }

                const slot = this.elements.length;
                const spawnX = sourceReplacement?.x
                    ?? Math.round(width * (0.08 + ((slot % 5) * 0.16)));
                const spawnY = sourceReplacement?.y
                    ?? Math.round(height * (0.12 + ((Math.floor(slot / 5) % 5) * 0.15)));
                const draft = operation.draft || {};
                sourceReplacement?.entries.forEach(({ el }) => {
                    this.removeElement(el, { skipSessionSave: true });
                });
                const el = this.spawnElement({
                    cardId: draft.cardId || 'loading',
                    name: draft.name || uiText('foundry.discovery.discovering', 'Discovering...'),
                    emoji: draft.emoji,
                    type: 'forged',
                    forgeStatus: operation.state,
                }, spawnX, spawnY, {
                    elementId: sourceReplacement?.elementId,
                    skipSessionSave: true,
                });
                el.dataset.foundryOperationId = operationId;
                el.dataset.pendingForgeId = operationId;
                this.setElementLoading(el, operation.state !== 'awaiting_art_selection', { skipSessionSave: true });
                // A recovered chip comes to rest like any other, before it is saved.
                this.settleChipClearOfOthers(el);
                const { x, y } = this.getElementCanvasCoordinates(el);
                const context = {
                    element: el,
                    x,
                    y,
                    inventoryUpdated: false,
                    mergeCompleteSoundPlayed: true,
                };
                this.pendingForges.set(operationId, context);
                this.materializedOperationIds.add(operationId);
                operationIds.add(operationId);
                recovery.canvasChanged = true;
                void this.pollForResult(operationId, context);
            }
            pagesProcessed += 1;

            const nextCursor = typeof payload.cursor === 'string' && payload.cursor
                ? payload.cursor
                : null;
            if (
                reachedWorkspaceHistory
                || payload.isDone !== false
                || !nextCursor
                || recovery.seenCursors.has(nextCursor)
            ) {
                recovery.statusIndex += 1;
                recovery.cursor = null;
                recovery.seenCursors.clear();
            } else {
                recovery.seenCursors.add(nextCursor);
                recovery.cursor = nextCursor;
            }
        }

        if (recovery.statusIndex < recovery.statuses.length) {
            if (recovery.canvasChanged) this.writeCrashJournal();
            recovery.deferred = true;
            this.setWorkspaceMode(this.workspaceMode, 'unsaved', 'recovery');
            this.scheduleParticipantOperationRecovery(0);
            return;
        }

        this.workspaceRecoveryState = null;
        this.workspaceRecoveryOverflow = false;
        if (recovery.canvasChanged || recovery.saveWasDeferred || recovery.wasBlocked) {
            this.scheduleWorkspaceSave({
                clearNoticeOwnerOnSuccess: 'recovery',
            });
        } else if (recovery.deferred) {
            this.setWorkspaceMode(this.workspaceMode, null, 'recovery');
        }
    }

    isWorkspaceRecoveryCurrent(recovery) {
        return Boolean(recovery)
            && !this.destroyed
            && this.surfaceActive
            && this.workspaceRecoveryState === recovery
            && this.accountStorageKey === recovery.accountStorageKey
            && this.workspaceAuthorityGeneration === recovery.authorityGeneration
            && this.workspaceProjectionGeneration === recovery.projectionGeneration;
    }

    findRecoverableOperationSources(operation) {
        const participantCreatedAt = Number(operation?.participantCreatedAt);
        if (
            !Number.isFinite(this.workspaceUpdatedAt)
            || this.workspaceUpdatedAt <= 0
            || !Number.isFinite(participantCreatedAt)
            || participantCreatedAt <= this.workspaceUpdatedAt
        ) return null;
        const parentIds = [operation?.parent1Id, operation?.parent2Id]
            .map((cardId) => String(cardId || '').trim());
        if (parentIds.some((cardId) => !cardId)) return null;

        const entries = [];
        for (const cardId of parentIds) {
            const source = this.elements.find((entry) => (
                !entries.includes(entry)
                && !entry?.el?.dataset?.foundryOperationId
                && String(entry?.card?.cardId || entry?.el?.dataset?.cardId || '') === cardId
            ));
            if (!source) return null;
            entries.push(source);
        }
        const positions = entries.map(({ el }) => this.getElementCanvasCoordinates(el));
        return {
            entries,
            elementId: entries[0].el.dataset.foundryElementId,
            x: Math.round((positions[0].x + positions[1].x) / 2),
            y: Math.round((positions[0].y + positions[1].y) / 2),
        };
    }

    scheduleParticipantOperationRecovery(delayMs = 0) {
        if (
            !this.workspaceRecoveryOverflow
            || !this.surfaceActive
            || this.destroyed
            || this.workspaceRecoveryTimer
        ) return;
        const timer = setTimeout(() => {
            if (this.workspaceRecoveryTimer !== timer) return;
            this.workspaceRecoveryTimer = null;
            if (this.surfaceActive && !this.destroyed) void this.recoverParticipantOperations();
        }, Math.max(0, Number(delayMs) || 0));
        this.workspaceRecoveryTimer = timer;
    }

    async openWorkspace({ authToken = null } = {}) {
        if (!this.user || this.workspaceTakeOverInFlight) {
            this.settleFirstProjection();
            return;
        }
        if (this.workspaceOpenInFlight) return this.workspaceOpenInFlight;
        const authorityAccountStorageKey = this.accountStorageKey;
        const authorityUser = this.user;
        let authorityGeneration = null;
        const open = (async () => {
            try {
                const pendingSave = this.workspaceSaveDrainPromise;
                if (pendingSave) await pendingSave;
                if (
                    !this.user
                    || this.accountStorageKey !== authorityAccountStorageKey
                    || !this.surfaceActive
                    || this.destroyed
                    || this.workspaceTakeOverInFlight
                ) return;
                this.retireWorkspaceRecovery();
                this.stopWorkspaceLeaseRenewal();
                this.workspaceLeaseRequestControllers.forEach((controller) => controller.abort());
                this.workspaceLeaseRequestControllers.clear();
                authorityGeneration = ++this.workspaceAuthorityGeneration;
                const network = await this.runBoundedRequest(async (signal) => {
                    const pendingRelease = FoundryWorkspaceReleasePromise;
                    if (pendingRelease) await awaitFoundryRequestStage(pendingRelease, signal);
                    /* The unseen canvas read, and a hand-back of its lease
                       already sent, land first: until then its lease stands
                       (a different token, peekCanvas), and this open would be
                       answered read-only; or the read hands its lease over to
                       this open. The token is read after this wait. */
                    const pendingPeek = this.canvasPeekInFlight?.whole;
                    if (pendingPeek) await awaitFoundryRequestStage(pendingPeek, signal);
                    const token = authToken || await awaitFoundryRequestStage(
                        authorityUser.getIdToken(),
                        signal,
                    );
                    const dimensions = this.getLegacyCanvasDimensions();
                    const leaseToken = this.workspaceLeaseToken;
                    let payload = await this.requestWorkspaceOpen(
                        token,
                        dimensions,
                        signal,
                        leaseToken,
                    );
                    payload = await this.completeWorkspaceMigration(
                        payload,
                        token,
                        dimensions,
                        signal,
                        leaseToken,
                    );
                    return { payload, token };
                });
                if (!this.isWorkspaceAuthorityCurrent(
                    authorityGeneration,
                    authorityAccountStorageKey,
                )) return;
                const { payload, token } = network;
                this.workspaceLoaded = true;
                this.workspaceRevision = Number.isSafeInteger(payload.revision) ? payload.revision : 0;
                this.workspaceUpdatedAt = Number.isFinite(Number(payload.updatedAt)) && Number(payload.updatedAt) > 0
                    ? Number(payload.updatedAt)
                    : 0;
                this.workspaceLeaseExpiresAt = Number(payload.leaseExpiresAt) || 0;
                if (payload.leaseToken) {
                    this.adoptWorkspaceLease(payload.leaseToken);
                }
                this.workspaceSaveBlockedByConflict = false;
                this.workspaceNoticeReasons.clear();
                this.setWorkspaceMode(payload.mode);
                let journal = this.readCrashJournal();
                const canonicalElements = this.normalizeWorkspaceElements(payload.elements);
                const canonicalHash = JSON.stringify(canonicalElements);
                const projectionAuthority = {
                    authorityAccountStorageKey,
                    authorityGeneration,
                };
                const isWriter = this.workspaceMode === 'writer';
                // The writer settles a journal the server already holds; a
                // read-only tab leaves it to the tab that edits.
                if (isWriter) {
                    journal = this.retireJournalMatchingServer(journal, canonicalElements);
                }
                const restoredElements = isWriter && journal && this.isJournalRestorable(journal, canonicalHash)
                    ? journal.elements
                    : null;
                /* A restored journal is projected in one pass. It is the
                   server's canvas plus this tab's changes, and projecting the
                   server's canvas first removed each of those chips only to
                   spawn it again (a recovered discovery as a "Discovering..."
                   stub) a moment later. */
                if (!await this.applyWorkspaceSnapshot(
                    restoredElements || canonicalElements,
                    projectionAuthority,
                )) return;
                this.lastSavedWorkspaceHash = canonicalHash;

                if (restoredElements) {
                    this.scheduleWorkspaceSave({ force: true });
                } else {
                    if (journal && isWriter) {
                        this.setWorkspaceMode('writer', 'conflict', 'conflict');
                    }
                    if (JSON.stringify(this.buildWorkspaceElements()) !== canonicalHash) {
                        this.scheduleWorkspaceSave({ force: true });
                    }
                }
                try {
                    await this.recoverParticipantOperations(token);
                } catch (err) {
                    console.warn('Failed to recover Foundry discovery operations:', err);
                }
                if (
                    this.isWorkspaceAuthorityCurrent(
                        authorityGeneration,
                        authorityAccountStorageKey,
                    )
                    && this.workspaceMode === 'writer'
                ) this.scheduleWorkspaceLeaseRenewal();
            } catch (err) {
                if (err?.name !== 'AbortError') {
                    console.warn('Failed to open Foundry workspace:', err);
                }
                if (
                    authorityGeneration === null
                    || !this.isWorkspaceAuthorityCurrent(
                        authorityGeneration,
                        authorityAccountStorageKey,
                    )
                ) return;
                this.setWorkspaceMode('read_only');
                this.scheduleWorkspaceOpenRetry();
            }
        })();
        this.workspaceOpenInFlight = open;
        try {
            return await open;
        } finally {
            if (this.workspaceOpenInFlight === open) this.workspaceOpenInFlight = null;
            // A projection settles it earlier; this covers an open that
            // failed or was abandoned before it projected anything.
            this.settleFirstProjection();
        }
    }

    isWorkspaceAuthorityCurrent(generation, accountStorageKey = null) {
        return !this.destroyed
            && this.surfaceActive
            && generation === this.workspaceAuthorityGeneration
            && (accountStorageKey === null || accountStorageKey === this.accountStorageKey);
    }

    getLegacyCanvasDimensions() {
        const { width, height } = this.captureWorkspaceCanvasMetrics();
        return {
            legacyCanvasWidth: Math.min(100_000, Math.max(1, Math.round(width))),
            legacyCanvasHeight: Math.min(100_000, Math.max(1, Math.round(height))),
        };
    }

    adoptWorkspaceLease(leaseToken) {
        const token = String(leaseToken || '').trim();
        if (!token) return;
        this.workspaceLeaseToken = token;
        try { sessionStorage.setItem(FOUNDRY_WORKSPACE_LEASE_KEY, token); } catch (_) { }
    }

    async requestWorkspaceOpen(
        authToken,
        dimensions,
        signal = null,
        leaseToken = this.workspaceLeaseToken,
    ) {
        const query = new URLSearchParams({
            leaseToken,
            legacyCanvasWidth: String(dimensions.legacyCanvasWidth),
            legacyCanvasHeight: String(dimensions.legacyCanvasHeight),
        });
        const response = await awaitFoundryRequestStage(fetch(
            `/api/foundry/workspace?${query.toString()}`,
            {
                headers: { 'Authorization': `Bearer ${authToken}` },
                signal,
            },
        ), signal);
        const payload = await awaitFoundryRequestStage(
            response.json().catch(() => ({})),
            signal,
        );
        if (!response.ok) throw new Error(payload.error || 'Workspace unavailable');
        return payload;
    }

    async completeWorkspaceMigration(
        initialPayload,
        authToken,
        dimensions,
        signal = null,
        initialLeaseToken = this.workspaceLeaseToken,
    ) {
        let payload = initialPayload;
        let leaseToken = String(payload?.leaseToken || initialLeaseToken || '').trim();
        let pageCount = 1;
        while (payload?.mode === 'writer' && payload?.migrationPending === true) {
            if (pageCount >= MAX_FOUNDRY_WORKSPACE_MIGRATION_PAGES) {
                throw new Error('legacy_foundry_migration_page_limit');
            }
            payload = await this.requestWorkspaceOpen(
                authToken,
                dimensions,
                signal,
                leaseToken,
            );
            leaseToken = String(payload?.leaseToken || leaseToken).trim();
            pageCount += 1;
        }
        return payload;
    }

    scheduleWorkspaceOpenRetry(delayMs = 2000) {
        if (this.workspaceOpenRetryTimer || !this.surfaceActive) return;
        this.workspaceOpenRetryTimer = setTimeout(() => {
            this.workspaceOpenRetryTimer = null;
            if (this.surfaceActive && this.user) void this.openWorkspace();
        }, Math.max(250, Number(delayMs) || 2000));
    }

    stopWorkspaceLeaseRenewal() {
        if (this.workspaceRenewTimer) clearTimeout(this.workspaceRenewTimer);
        this.workspaceRenewTimer = null;
    }

    scheduleWorkspaceLeaseRenewal() {
        this.stopWorkspaceLeaseRenewal();
        if (!this.surfaceActive || !this.canEditWorkspace()) return;
        this.workspaceRenewTimer = setTimeout(() => {
            this.workspaceRenewTimer = null;
            void this.renewWorkspaceLease();
        }, FOUNDRY_WORKSPACE_LEASE_RENEW_MS);
    }

    async renewWorkspaceLease() {
        if (!this.user || !this.surfaceActive || !this.canEditWorkspace()) return;
        const owner = {
            accountStorageKey: this.accountStorageKey,
            authorityGeneration: this.workspaceAuthorityGeneration,
            leaseToken: this.workspaceLeaseToken,
            user: this.user,
        };
        try {
            const payload = await this.runBoundedRequest(async (signal) => {
                const token = await awaitFoundryRequestStage(owner.user.getIdToken(), signal);
                const response = await awaitFoundryRequestStage(fetch(
                    '/api/foundry/workspace/renew',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ leaseToken: owner.leaseToken }),
                        signal,
                    },
                ), signal);
                const body = await awaitFoundryRequestStage(
                    response.json().catch(() => ({})),
                    signal,
                );
                return { body, response };
            }, { controllerSet: this.workspaceLeaseRequestControllers });
            if (!this.isWorkspaceLeaseOwnerCurrent(owner)) return;
            if (!payload.response.ok || payload.body.outcome !== 'renewed') {
                this.setWorkspaceMode('read_only');
                return;
            }
            this.workspaceLeaseExpiresAt = Number(payload.body.leaseExpiresAt)
                || this.workspaceLeaseExpiresAt;
            this.clearWorkspaceNotice('lease');
            this.scheduleWorkspaceLeaseRenewal();
        } catch (err) {
            if (!this.isWorkspaceLeaseOwnerCurrent(owner)) return;
            if (err?.name !== 'AbortError') {
                console.warn('Failed to renew Foundry workspace lease:', err);
            }
            if (this.destroyed || !this.surfaceActive) return;
            this.setWorkspaceMode('writer', 'unsaved', 'lease');
            this.scheduleWorkspaceLeaseRenewal();
        }
    }

    isWorkspaceLeaseOwnerCurrent(owner) {
        return Boolean(owner)
            && !this.destroyed
            && this.surfaceActive
            && this.accountStorageKey === owner.accountStorageKey
            && this.workspaceAuthorityGeneration === owner.authorityGeneration
            && this.workspaceLeaseToken === owner.leaseToken;
    }

    async takeOverWorkspace() {
        if (!this.user || this.workspaceTakeOverInFlight) return;
        if (this.workspaceOpenRetryTimer) {
            clearTimeout(this.workspaceOpenRetryTimer);
            this.workspaceOpenRetryTimer = null;
        }
        this.retireWorkspaceRecovery();
        this.stopWorkspaceLeaseRenewal();
        this.workspaceLeaseRequestControllers.forEach((controller) => controller.abort());
        this.workspaceLeaseRequestControllers.clear();
        this.workspaceSaveRequestControllers.forEach((controller) => controller.abort());
        this.workspaceSaveRequestControllers.clear();
        this.workspacePendingSaveRequest = null;
        const authorityAccountStorageKey = this.accountStorageKey;
        const authorityUser = this.user;
        const authorityGeneration = ++this.workspaceAuthorityGeneration;
        this.workspaceTakeOverInFlight = true;
        let takeoverApplied = false;
        if (this.workspaceTakeOverBtn) this.workspaceTakeOverBtn.disabled = true;
        try {
            const dimensions = this.getLegacyCanvasDimensions();
            const network = await this.runBoundedRequest(async (signal) => {
                const pendingPeek = this.canvasPeekInFlight?.whole;
                if (pendingPeek) await awaitFoundryRequestStage(pendingPeek, signal);
                const token = await awaitFoundryRequestStage(authorityUser.getIdToken(), signal);
                const response = await awaitFoundryRequestStage(fetch(
                    '/api/foundry/workspace/take-over',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify(dimensions),
                        signal,
                    },
                ), signal);
                let payload = await awaitFoundryRequestStage(
                    response.json().catch(() => ({})),
                    signal,
                );
                if (!response.ok || payload.mode !== 'writer' || !payload.leaseToken) {
                    return { payload, response, token };
                }
                payload = await this.completeWorkspaceMigration(
                    payload,
                    token,
                    dimensions,
                    signal,
                    payload.leaseToken,
                );
                return { payload, response, token };
            });
            const { payload, response, token } = network;
            if (!this.isWorkspaceAuthorityCurrent(
                authorityGeneration,
                authorityAccountStorageKey,
            )) return;
            if (!response.ok || payload.mode !== 'writer' || !payload.leaseToken) return;
            this.adoptWorkspaceLease(payload.leaseToken);
            this.workspaceRevision = Number.isSafeInteger(payload.revision) ? payload.revision : 0;
            this.workspaceUpdatedAt = Number.isFinite(Number(payload.updatedAt)) && Number(payload.updatedAt) > 0
                ? Number(payload.updatedAt)
                : 0;
            this.workspaceLeaseExpiresAt = Number(payload.leaseExpiresAt) || 0;
            this.workspaceSaveBlockedByConflict = false;
            this.workspaceNoticeReasons.clear();
            this.setWorkspaceMode('writer');
            const canonicalElements = this.normalizeWorkspaceElements(payload.elements);
            this.lastSavedWorkspaceHash = JSON.stringify(canonicalElements);
            const projectionAuthority = {
                authorityAccountStorageKey,
                authorityGeneration,
            };
            const journal = this.retireJournalMatchingServer(this.readCrashJournal(), canonicalElements);
            // Restored as openWorkspace restores it: in one projection.
            const restoredElements = journal && this.isJournalRestorable(journal, this.lastSavedWorkspaceHash)
                ? journal.elements
                : null;
            if (!await this.applyWorkspaceSnapshot(
                restoredElements || canonicalElements,
                projectionAuthority,
            )) return;
            takeoverApplied = true;
            if (restoredElements) {
                this.scheduleWorkspaceSave({ force: true });
            } else if (journal) {
                this.setWorkspaceMode('writer', 'conflict', 'conflict');
            }
            await this.recoverParticipantOperations(token);
            if (this.isWorkspaceAuthorityCurrent(
                authorityGeneration,
                authorityAccountStorageKey,
            )) {
                this.scheduleWorkspaceLeaseRenewal();
            }
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.warn('Failed to take over Foundry workspace:', err);
            }
        } finally {
            this.workspaceTakeOverInFlight = false;
            if (this.workspaceTakeOverBtn) this.workspaceTakeOverBtn.disabled = false;
            if (
                this.surfaceActive
                && !this.destroyed
                && (!takeoverApplied || !this.isWorkspaceAuthorityCurrent(
                    authorityGeneration,
                    authorityAccountStorageKey,
                ))
            ) this.scheduleWorkspaceOpenRetry(250);
        }
    }

    releaseWorkspaceLease() {
        if (!this.user || !this.workspaceLeaseToken || !this.workspaceLoaded) return Promise.resolve(false);
        return this.sendWorkspaceLeaseRelease(this.user, this.workspaceLeaseToken);
    }

    /**
     * Hands `leaseToken`'s lease back; one this token does not hold is left as
     * it is. Resolves true once the server has answered (released, or not this
     * token's lease), false when no answer came back.
     */
    async sendWorkspaceLeaseRelease(user, leaseToken) {
        if (!user?.getIdToken || !leaseToken) return false;
        try {
            const response = await this.runBoundedRequest(async (signal) => {
                const token = await awaitFoundryRequestStage(user.getIdToken(), signal);
                return await awaitFoundryRequestStage(fetch('/api/foundry/workspace/lease', {
                    method: 'DELETE',
                    keepalive: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ leaseToken }),
                    signal,
                }), signal);
            }, { abortOnHide: false });
            // 404 and 409 are answers too: no workspace, or not this token's lease.
            const status = Number(response?.status);
            return Boolean(response?.ok) || status === 404 || status === 409;
        } catch (_) {
            return false;
        }
    }

    /**
     * The page on hand of a paged inventory, as paginateList shapes a page,
     * cut to what fits (`pageSize`). The answer is page `info.page` cut at the
     * size its request asked: the measured one, except for a read taken while
     * the panel had no geometry or before a resize, and the entry warm (which
     * asks no size and gets the server's). Page 1 is exact from any answer for
     * page 1; any other page at another size is `exact: false`, and is read
     * again at this size.
     */
    pagedInventoryPagination(pageSize) {
        const info = this.inventoryPagedInfo || { page: 1, pageSize, totalItems: null, hasMore: false };
        const onHand = Array.isArray(this.inventory) ? this.inventory : [];
        const pageItems = onHand.slice(0, pageSize);
        const hasMore = info.hasMore || onHand.length > pageSize;
        const exact = info.pageSize === pageSize
            || (info.page === 1 && (onHand.length >= pageSize || !info.hasMore));
        const counted = Number.isFinite(info.totalItems);
        return {
            totalItems: counted ? info.totalItems : null,
            totalPages: counted
                ? Math.max(1, Math.ceil(info.totalItems / pageSize))
                : (hasMore ? info.page + 1 : info.page),
            totalsUnknown: !counted,
            page: info.page,
            pageSize,
            startIndex: 0,
            endIndex: pageItems.length,
            pageItems,
            exact,
        };
    }

    paginateList(items, pageSize, page) {
        if (Pagination?.paginate) {
            return Pagination.paginate(items, pageSize, page);
        }
        const list = Array.isArray(items) ? items : [];
        const size = Number(pageSize) > 0 ? Number(pageSize) : 1;
        const totalItems = list.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / size));
        const currentPage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
        const startIndex = (currentPage - 1) * size;
        const endIndex = startIndex + size;
        return {
            totalItems,
            totalPages,
            page: currentPage,
            pageSize: size,
            startIndex,
            endIndex: Math.min(endIndex, totalItems),
            pageItems: list.slice(startIndex, endIndex),
        };
    }

    // Same viewport gate mobile.css uses, so the copy and the layout can never
    // disagree about what "a phone" is.
    updateInventoryPagination(pagination) {
        if (this.sidebarPrev) this.sidebarPrev.disabled = pagination.page <= 1;
        if (this.sidebarNext) this.sidebarNext.disabled = pagination.page >= pagination.totalPages;
        if (this.sidebarPageInfo) {
            /* "Page 1 of 12" wraps under the Next button in the phone's narrow
               pagination strip. The total is already implied by Next going
               disabled, so the phone shows just the page. */
            this.sidebarPageInfo.textContent = isCompactFoundry() || pagination.totalsUnknown
                ? uiText('common.page', `Page ${pagination.page}`, { page: pagination.page })
                : uiText('common.pageOf', `Page ${pagination.page} of ${pagination.totalPages}`, {
                    page: pagination.page,
                    total: pagination.totalPages,
                });
        }
    }

    captureInventoryScroll() {
        if (!this.elementList) return this.inventoryScrollTop;
        if (this.isInventoryPanelActive()) {
            this.inventoryScrollTop = this.elementList.scrollTop;
        }
        return this.inventoryScrollTop;
    }

    isInventoryPanelActive() {
        const panel = this.elementList?.closest?.('.foundry-panel');
        return !panel || panel.classList.contains('active');
    }

    restoreInventoryScroll() {
        if (!this.elementList) return;
        const restore = () => {
            this.elementList.scrollTop = Math.min(this.inventoryScrollTop, this.elementList.scrollHeight);
        };
        restore();
        requestAnimationFrame(restore);
    }

    async loadStardust({ whileHidden = false } = {}) {
        if (!this.user || this.destroyed) return;
        try {
            const data = await this.runBoundedRequest(async (signal) => {
                const token = await awaitFoundryRequestStage(this.user.getIdToken(), signal);
                const res = await awaitFoundryRequestStage(fetch('/api/foundry/user', {
                    headers: { 'Authorization': `Bearer ${token}` },
                    signal,
                }), signal);
                if (!res.ok) return null;
                return await awaitFoundryRequestStage(res.json(), signal);
            }, this.surfaceReadOptions(whileHidden));
            if (!data || this.destroyed || (!this.surfaceActive && !whileHidden)) return;
            this.updateStardustDisplay(data?.stardust ?? 0);
            // The lobby chip behind this surface was painted at sign-in and
            // every craft here moves the balance; this read is authoritative.
            announceStardust(data?.stardust);
        } catch (err) {
            if (err?.name !== 'AbortError') console.warn('Failed to load stardust:', err);
        }
    }

    renderForgeCosts() {
        if (!this.fuseCostEl) return;
        const crafting = Number(this.forgeCosts?.CRAFTING);
        const discovery = Number(this.forgeCosts?.DISCOVERY);
        if (
            Number.isSafeInteger(crafting) && crafting > 0
            && Number.isSafeInteger(discovery) && discovery > 0
        ) {
            const compact = {
                crafting: formatStardust(crafting),
                discovery: formatStardust(discovery),
            };
            /* The phone header packs the tabs, this note, the Stardust pill,
               the language picker AND the inventory search into one 53px row —
               measured, they collided at 667x275. The full sentence is the
               easiest thing in it to shorten, and the surface it sits on is
               already named "Moon Fuse", so the noun is redundant there. The
               title attribute keeps the long form for anyone who hovers. */
            // The real-money equivalent goes on the full-width form only: the
            // compact phone header packs six controls into one 53px row and
            // measured a collision at 667x275 before this text existed. The
            // title below carries the equivalent for the compact case.
            // The note now finishes the canvas hint sentence ("Fuse two
            // elements from the list · 50 ✦ / discovery"), so it is the short
            // form on every layout; the title keeps the exact figure and the
            // money equivalent.
            const summary = uiText('foundry.costSummaryShort', '{{discovery}} ✦ / discovery', compact);
            // A confirming read that returns the same price writes nothing.
            if (this.fuseCostEl.textContent !== summary) this.fuseCostEl.textContent = summary;
            /* The title is the EXACT figure — that is its whole job, since the
               visible text is compacted ("12.3K"). `withMoneyLive` runs the
               amount through the same compacting formatter, so reusing it here
               published "Discovery cost 12.3K ✦" as the accessible name and
               lost the precise number entirely. Take only the money equivalent
               from Stardust and pair it with the raw amount. */
            const exactMoney = Stardust?.moneyEquivalent?.(discovery);
            this.fuseCostEl.title = uiText(
                'foundry.costSummaryMoney',
                'Discovery cost {{discovery}}',
                {
                    crafting,
                    discovery: exactMoney ? `${discovery} ✦ (${exactMoney})` : `${discovery} ✦`,
                },
            );
            return;
        }
        this.fuseCostEl.removeAttribute('title');
        const status = this.forgeCosts === null
            ? uiText('foundry.pricingLoading', 'Loading fusion prices...')
            : uiText('foundry.pricingUnavailable', 'Fusion pricing unavailable.');
        if (this.fuseCostEl.textContent !== status) this.fuseCostEl.textContent = status;
    }

    async loadForgeCosts({ whileHidden = false } = {}) {
        if (!this.fuseCostEl || this.destroyed) return;
        // Prices read ahead of the press (or by the last open, within their
        // one-minute lifetime) are the answer; they change only with a deploy.
        const warmed = FoundryInventoryWarm?.peekConfig?.();
        if (warmed) {
            this.forgeCosts = warmed;
            this.renderForgeCosts();
            return;
        }
        try {
            const data = await this.runBoundedRequest(async (signal) => {
                // Join a warm read already on the wire rather than racing it.
                const pending = FoundryInventoryWarm?.pendingConfig?.();
                const joined = pending ? await awaitFoundryRequestStage(pending, signal) : null;
                if (joined) return { cost: joined };
                const response = await awaitFoundryRequestStage(fetch(
                    '/api/foundry/config',
                    { signal },
                ), signal);
                if (!response.ok) throw new Error('foundry_config_unavailable');
                return await awaitFoundryRequestStage(response.json(), signal);
            }, this.surfaceReadOptions(whileHidden));
            const crafting = Number(data?.cost?.CRAFTING);
            const discovery = Number(data?.cost?.DISCOVERY);
            if (
                !Number.isSafeInteger(crafting) || crafting <= 0
                || !Number.isSafeInteger(discovery) || discovery <= 0
            ) {
                throw new Error('foundry_config_invalid');
            }
            if (this.destroyed) return;
            this.forgeCosts = { CRAFTING: crafting, DISCOVERY: discovery };
            FoundryInventoryWarm?.rememberConfig?.(this.forgeCosts);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            if (this.destroyed) return;
            this.forgeCosts = false;
            console.warn('[Foundry] Failed to load fusion pricing.');
        }
        this.renderForgeCosts();
    }

    getCardEmoji(card) {
        if (card?.emoji) return card.emoji;
        const fallback = card?.cardId ? this.inventoryById.get(card.cardId) : null;
        return fallback?.emoji || '🃏';
    }

    clearHoverTimer(el) {
        const timer = this.hoverTimers.get(el);
        if (timer) {
            clearTimeout(timer);
            this.hoverTimers.delete(el);
        }
    }

    dismissHoverPreview(preview = document.getElementById('card-hover-preview')) {
        if (!preview) return;
        const card = preview.querySelector('.card');
        if (
            preview.dataset.stellarPlaybackActive === 'true'
            && card
            && Cards?.setStellarHover
        ) {
            Cards.setStellarHover(card, false);
            delete preview.dataset.stellarPlaybackActive;
        }
        CardTilt?.unregister?.(card);
        preview.remove();
    }

    setTrashZoneActive(isActive) {
        if (!this.trashZone) return;
        this.trashZone.classList.toggle('active', isActive);
    }

    /**
     * How much of the canvas floor is reserved for the device's own edge
     * gesture, in CSS pixels. `foundry.css` owns the number — CSS is where the
     * safe-area insets are already resolved (docs/mobile-page-box.md), and a
     * JS copy of that arithmetic would be a second definition to keep in step.
     */
    readCanvasDropFloor() {
        if (!this.playArea) return 0;
        // The `::after` carrier, not the custom property itself: an
        // unregistered `--var` reads back as its unresolved text and parses to
        // NaN. See the note beside `#play-area::after` in foundry.css.
        const declared = getComputedStyle(this.playArea, '::after').height;
        const px = Number.parseFloat(declared);
        return Number.isFinite(px) && px > 0 ? px : 0;
    }

    isOverTrash(el) {
        if (!this.trashZone || !el) return false;
        const rect1 = el.getBoundingClientRect();
        const rect2 = this.trashZone.getBoundingClientRect();
        return !(rect1.right < rect2.left ||
            rect1.left > rect2.right ||
            rect1.bottom < rect2.top ||
            rect1.top > rect2.bottom);
    }

    removeElement(el, {
        skipSessionSave = false,
        triggerRecovery = !skipSessionSave,
    } = {}) {
        if (!el) return;
        this.clearHoverTimer(el);
        el.remove();
        this.elements = this.elements.filter(e => e.el !== el);
        if (this.elements.length < MAX_FOUNDRY_WORKSPACE_ELEMENTS) {
            this.clearWorkspaceNotice('capacity');
        }
        this.markCanvasMutation();
        this.dismissHoverPreview();
        if (!skipSessionSave) {
            this.scheduleWorkspaceSave();
        }
        if (triggerRecovery) this.scheduleParticipantOperationRecovery();
    }

    getCardDetailCacheKey(cardId, language = i18n?.getLanguage?.() || 'en') {
        return `${cardId}:${encodeURIComponent(language)}`;
    }

    seedCardDetailCache(cards, language = i18n?.getLanguage?.() || 'en') {
        if (!Array.isArray(cards)) return;
        cards.forEach((card) => {
            if (!card?.cardId) return;
            this.cardDetailCache.set(this.getCardDetailCacheKey(card.cardId, language), card);
        });
    }

    async fetchCardDetails(cardId) {
        if (!cardId || cardId === 'loading' || cardId.startsWith('pending_')) return null;
        const lang = i18n?.getLanguage?.() || 'en';
        const cacheKey = this.getCardDetailCacheKey(cardId, lang);
        if (this.cardDetailCache.has(cacheKey)) return this.cardDetailCache.get(cacheKey);
        // Authenticated like every other foundry fetch: discovered forged
        // cards are private-visibility, so an anonymous lookup 404s and a
        // COMPLETE card gets misread as "art still generating" downstream
        // (hover previews and merge validation both consume this).
        try {
            return await this.runBoundedRequest(async (signal) => {
                const headers = {};
                try {
                    const token = await awaitFoundryRequestStage(
                        this.user?.getIdToken?.(),
                        signal,
                    );
                    if (token) headers.Authorization = `Bearer ${token}`;
                } catch (error) {
                    if (error?.name === 'AbortError') throw error;
                    // A public card may still be readable without auth.
                }
                const res = await awaitFoundryRequestStage(fetch(
                    `/api/foundry/card/${encodeURIComponent(cardId)}?lang=${encodeURIComponent(lang)}`,
                    { headers, signal },
                ), signal);
                if (!res.ok) return null;
                const data = await awaitFoundryRequestStage(res.json(), signal);
                if (!this.destroyed && this.surfaceActive) {
                    this.cardDetailCache.set(cacheKey, data);
                }
                return data;
            });
        } catch (error) {
            if (error?.name === 'AbortError') return null;
            throw error;
        }
    }

    async showHoverPreview(cardData, anchorRect, {
        sidebarSource = false,
        sourceEl = null,
        requireSettledRaster = false,
    } = {}) {
        if (!Cards?.createCardElement) return;
        // The detail read or the warm before this call can outlast the
        // chooser's arrival (closeCardInspection).
        if (this.isDiscoveryModalOpen()) return false;

        this.dismissHoverPreview();
        Cards?.clearHoverPreview?.();

        const sourceRect = anchorRect;
        const preview = document.createElement('div');
        preview.id = 'card-hover-preview';
        preview.className = 'card-hover-preview';
        preview.style.zIndex = Cards.HOVER_PREVIEW_Z_INDEX || '2147483647';
        preview.style.left = `${Math.round(sourceRect.left)}px`;
        preview.style.top = `${Math.round(sourceRect.top)}px`;
        preview.style.visibility = 'hidden';

        // Match AssetPreloader.cardSource(): the decoded authority cached for
        // this inventory item must be the source used by the popup renderer.
        const imgSrc = cardData.imageUrl || cardData.picture || cardData.image || '';
        const cardType = cardData.type === 'Spell' ? 'Spell' : 'Character';
        const uiCard = {
            id: cardData.cardId,
            instanceId: cardData.cardId,
            name: cardData.name,
            type: cardType,
            manaCost: Number.isFinite(cardData.manaCost) ? cardData.manaCost : 0,
            picture: imgSrc,
            imageUrl: imgSrc,
            description: cardData.description || '',
            character: cardData.character || null,
            collection: cardData.collection || 'Moon Foundry',
            forgeStatus: cardData.forgeStatus || cardData.status || null,
            isStellar: !!cardData.isStellar,
        };
        StellarAssets?.applyMediaToCard?.(uiCard, {
            status: 'complete',
            videoUrl: cardData.stellarVideoUrl,
            videoWebmUrl: cardData.stellarVideoWebmUrl,
            audioUrl: cardData.stellarAudioUrl,
            audioWebmUrl: cardData.stellarAudioWebmUrl,
        });

        const isStellarPreview = !!uiCard.isStellar;
        const cardEl = Cards.createCardElement(uiCard, 'foundry-preview-card');
        cardEl.style.transformOrigin = 'center center';
        cardEl.style.setProperty('--card-hover-transform', 'scale(1) rotate(0deg) translateY(0%)');
        if (isStellarPreview) {
            cardEl.dataset.stellarPreview = 'collection';
        }
        preview.appendChild(cardEl);
        preview.addEventListener('mouseleave', () => this.dismissHoverPreview(preview));

        document.body.appendChild(preview);

        if (Cards?.applyPreviewScale) {
            const previewVariant = isStellarPreview ? 'stellar' : 'normal';
            Cards.applyPreviewScale(cardEl, previewVariant, sourceRect, preview);
        }
        // Inventory rows do not paint a transferable source card. Their page
        // has already prewarmed the decoded-source LRU, and the popup stays
        // hidden while the final CSS geometry survives a layout frame and the
        // exact locked raster signature is revalidated — for as long as the
        // shared popup's cap, never longer. docs/asset-loading.md law 2, as
        // cards.js showCardPreviewFor does it: gate the art swap, never the
        // element. Dismissing on an unsettled raster left a cold first hover
        // (or tap) on a list card showing nothing, and nothing again until the
        // pointer left and came back.
        if (imgSrc) {
            let settled = false;
            try {
                const settlement = Cards?.settleCardRasterForPresentation?.(
                    cardEl,
                    uiCard,
                    { oversample: 1, stableGeometry: true, coverVisualExpansion: true },
                );
                settled = await (Cards?.withHoverPreviewArtGrace
                    ? Cards.withHoverPreviewArtGrace(settlement)
                    : settlement);
            } catch (_) {
                settled = false;
            }
            if (
                !preview.isConnected
                || document.getElementById('card-hover-preview') !== preview
            ) {
                this.dismissHoverPreview(preview);
                return false;
            }
            // `null` is the cap: the art is still on its way and lands behind
            // the frame and text. `false` ended this attempt: keep the face
            // inspectable without its undecoded paint, never invented art.
            if (settled === false) Cards?.presentUnavailableArtWell?.(cardEl);
        }
        // So can the raster settle: a chooser that opened meanwhile owns the
        // screen, and the staged enlargement never shows.
        if (this.isDiscoveryModalOpen()) {
            this.dismissHoverPreview(preview);
            return false;
        }

        // Sidebar popups reposition BESIDE their source and dismiss when the
        // pointer leaves it, so the mini stays the tilt host. Canvas popups
        // open OVER their source, occluding it, so the enlarged card itself
        // owns the pointer and its own tilt.
        if (sidebarSource && sourceEl && CardTilt?.registerPreview) {
            CardTilt.registerPreview(cardEl, { host: sourceEl });
        } else if (CardTilt?.registerPreview) {
            CardTilt.registerPreview(cardEl);
        }

        // Allow drag-through only once the popup is about to show. Until then
        // the hidden staging preview must not own pointer behavior.
        if (sourceEl && sourceEl.classList.contains('element')) {
            preview.style.touchAction = 'none';
            preview.addEventListener('pointerdown', (e) => {
                if (e.button !== 0) return;
                /* A finger may be the first of a pinch, which magnifies this
                   very popup (zoom-policy.js), so on touch it stays until the
                   press becomes a drag: startCanvasDrag dismisses it then. */
                if (e.pointerType !== 'touch') this.dismissHoverPreview(preview);
                this.onPointerDown(e, sourceEl);
            });
        }
        if (isStellarPreview && Cards?.setStellarHover) {
            Cards.setStellarHover(cardEl, true);
            preview.dataset.stellarPlaybackActive = 'true';
        }
        // Sidebar items: reposition to the left of the sidebar so scrolling isn't blocked.
        // This remains in the creation task so no centered intermediate frame can paint.
        if (sidebarSource) {
            const sidebar = this.getElementById('sidebar');
            if (sidebar) {
                const sidebarLeft = sidebar.getBoundingClientRect().left;
                const cardBCR = cardEl.getBoundingClientRect();
                const curLeft = parseFloat(preview.style.left) || 0;
                const edge = uiPx(16);
                const offset = cardBCR.right - sidebarLeft + edge;
                preview.style.left = `${Math.round(Math.max(edge, curLeft - offset))}px`;
            }
        }
        preview.style.removeProperty('visibility');
        return true;
    }

    attachHoverPreview(el) {
        if (!Cards?.createCardElement) return;
        let hoverActive = false;
        const isCanvasElement = el.classList.contains('element');
        const isSidebarElement = el.classList.contains('sidebar-element');
        const delayMs = isCanvasElement ? this.hoverDelayMs : 0;

        const showPreview = async () => {
            if (!this.surfaceActive || !hoverActive) return;
            if (this.draggedEl || el.classList.contains('dragging')) return;
            /* The art chooser is a modal decision and owns the screen. Tapping
               an awaiting chip reopens it, and the very same press also armed
               this preview — so the enlarged card landed ON TOP of the chooser.
               An enlargement is inspection; while the player is being asked to
               pick an appearance there is nothing to inspect. */
            if (this.isArtChooserOpen()) return;
            const cardId = el.dataset.cardId;
            // Interim forge results have no card document until the operation
            // completes, so the detail endpoint cannot serve them. Their draft
            // text already arrived with the operation poll — preview from the
            // local card so the popup shows name/text over placeholder art.
            const localCard = this.elements.find((item) => item.el === el)?.card;
            const interimForge = localCard?.type === 'forged'
                && localCard.forgeStatus
                && localCard.forgeStatus !== 'complete'
                && localCard.forgeStatus !== 'failed';
            let data = null;
            if (interimForge) {
                const interimId = String(localCard.cardId || '');
                if (!localCard.name || !interimId || interimId === 'loading'
                    || interimId.startsWith('pending_')) return;
                data = { ...localCard };
            } else {
                data = await this.fetchCardDetails(cardId);
            }
            if (!this.surfaceActive || !hoverActive || !data) return;
            if (this.draggedEl || el.classList.contains('dragging')) return;
            // renderSidebar already starts this exact visible-page warm. Join
            // its deduplicated promise at the interaction boundary so a pointer
            // that beats decode cannot create a blank or soft first popup.
            const warm = AssetPreloader?.warmCards?.(
                [data],
                { priority: 'high' }
            );
            await waitForBoundedCardWarm(warm);
            if (!this.surfaceActive || !hoverActive) return;
            if (this.draggedEl || el.classList.contains('dragging')) return;
            // Merge stellar data from inventory
            const inventoryCard = this.inventoryById?.get(cardId);
            if (inventoryCard?.isStellar) {
                data.isStellar = true;
                if (inventoryCard.stellarVideoUrl) data.stellarVideoUrl = inventoryCard.stellarVideoUrl;
                if (inventoryCard.stellarVideoWebmUrl) data.stellarVideoWebmUrl = inventoryCard.stellarVideoWebmUrl;
                if (inventoryCard.stellarAudioUrl) data.stellarAudioUrl = inventoryCard.stellarAudioUrl;
                if (inventoryCard.stellarAudioWebmUrl) data.stellarAudioWebmUrl = inventoryCard.stellarAudioWebmUrl;
            }
            await this.showHoverPreview(data, el.getBoundingClientRect(), {
                sidebarSource: isSidebarElement,
                sourceEl: el,
                requireSettledRaster: true,
            });
        };

        // A press that never became a drag opens the same popup hovering does.
        // `hoverActive` is the hover path's own gate; a deliberate tap is just
        // as much an intent to look, so it opens that gate itself.
        this.chipPreviewOpeners.set(el, () => {
            hoverActive = true;
            this.clearHoverTimer(el);
            showPreview();
        });

        // Touch owns a direct callback path. Routing through a synthetic
        // mouseenter inherited the canvas' 600ms hover delay and depended on a
        // later compatibility-mouse sequence not cancelling it; on a phone that
        // made an ordinary inspection tap feel random. Desktop hover keeps its
        // deliberate delay below, while a released touch begins inspection now.
        TouchPreview?.attach?.(el, {
            onPreview: () => {
                hoverActive = true;
                this.clearHoverTimer(el);
                if (isSidebarElement && this.sidebarDrag && !this.sidebarDrag.created) {
                    this.sidebarDrag = null;
                }
                showPreview();
            },
            onDismiss: () => {
                hoverActive = false;
                this.clearHoverTimer(el);
                this.dismissHoverPreview();
            },
        });
        el.addEventListener('mouseenter', async (event) => {
            if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
            // A synthetic (untrusted) mouseenter is TouchPreview resolving an
            // inspection tap. Its pending sidebar tap-spawn must not survive,
            // or the same release would also add and persist the card.
            if (event?.isTrusted === false && isSidebarElement
                && this.sidebarDrag && !this.sidebarDrag.created) {
                this.sidebarDrag = null;
            }
            hoverActive = true;
            this.clearHoverTimer(el);
            if (delayMs > 0) {
                const timer = setTimeout(showPreview, delayMs);
                this.hoverTimers.set(el, timer);
            } else {
                showPreview();
            }
        });

        el.addEventListener('mouseleave', (e) => {
            hoverActive = false;
            this.clearHoverTimer(el);
            const preview = document.getElementById('card-hover-preview');
            if (preview && e.relatedTarget && (preview === e.relatedTarget || preview.contains(e.relatedTarget))) {
                return;
            }
            this.dismissHoverPreview(preview);
        });
    }

    /* The workspace projection waits (bounded) on the tracked promise before
       it paints card chips — see applyWorkspaceSnapshot. A settled inventory
       is what lets a chip's FIRST paint carry its real localized name and its
       first-discovery gradient instead of morphing through id → name →
       gradient as each arrived. */
    /* Longer than the warm's own 8s abort, so an honest prefetch still wins
       the race; short enough that a dead one costs a beat, not the screen. */
    static INVENTORY_WARM_WAIT_MS = 9000;

    // The longest a re-asked inventory read waits (a timed-out one doubles).
    static INVENTORY_TIMEOUT_MAX_MS = 120_000;

    loadInventory(options = {}) {
        const run = this._loadInventoryInner(options);
        if (options.warmOnly) {
            this.inventoryAdoption = run;
            const settle = () => {
                if (this.inventoryAdoption === run) this.inventoryAdoption = null;
            };
            void run.then(settle, settle);
        } else {
            // A request of this instance supersedes a list being taken over: its answer is dropped.
            this.inventoryAdoption = null;
            this.inventoryReadsInFlight += 1;
            const settle = () => {
                this.inventoryReadsInFlight = Math.max(0, this.inventoryReadsInFlight - 1);
            };
            void run.then(settle, settle);
        }
        this.inventoryLoadPromise = run.catch(() => null);
        return run;
    }

    async _loadInventoryInner({
        preserveScroll = false,
        preserveView = false,
        preserveOnError = false,
        reconcileCanvas = false,
        showLoading = true,
        whileHidden = false,
        // Only a response already read on intent (FoundryInventoryWarm), never a request.
        warmOnly = false,
        // A paged inventory's page turn, search or resize: the server may
        // answer it from the collection it read moments ago. Every other read
        // asks for the holdings as they are now.
        pageTurn = false,
        // The read's bound; a read that outlives it is asked again with more.
        timeoutMs = this.operationRequestTimeoutMs,
    } = {}) {
        if (this.destroyed || !this.user || !this.elementList) return;
        const loadSeq = ++this.inventoryLoadSeq;
        // An announcement heard after this point leaves this read's answer stale in turn.
        const changes = this.inventoryChanges;
        // The page a paged read asks for is the one wanted now, not whatever
        // a repaint left behind by the time the token is in hand.
        const pagedQuery = this.pagedInventoryQuery({ refresh: !pageTurn });

        if (showLoading) this.showInventoryLoading();

        try {
            const lang = i18n?.getLanguage?.() || 'en';
            /* An entry prefetch may already have this account's inventory in
               flight (the player hovered Foundry before pressing it). Consuming
               it removes the ~1s gap in which the first-discoverer ribbons were
               still unknown and the surface had already revealed. Strictly
               best-effort: a cold, stale, foreign-account or failed warm falls
               straight through to the ordinary request below. */
            const warmed = this.inventoryPaged ? null : FoundryInventoryWarm?.take?.(this.user?.uid, lang);
            if (warmed) {
                try {
                    /* The warm is bounded at its source, and bounded again
                       here: awaiting it unconditionally made the whole
                       inventory hostage to a prefetch that never settles, and
                       a surface with no cards reports nothing at all. Losing
                       the race costs one ordinary request. */
                    const warmPayload = await Promise.race([
                        warmed,
                        new Promise((resolve) => {
                            const timer = setTimeout(() => resolve(null), MoonFoundry.INVENTORY_WARM_WAIT_MS);
                            this.surfaceTimers?.add?.(timer);
                        }),
                    ]);
                    if (this.destroyed || loadSeq !== this.inventoryLoadSeq) return;
                    if (warmPayload) {
                        this.applyInventoryPayload(warmPayload, {
                            preserveScroll, preserveView, reconcileCanvas, lang, changes,
                        });
                        return;
                    }
                } catch (_) { /* fall through to the normal request */ }
            }
            if (warmOnly) return;
            const payload = await this.runBoundedRequest(async (signal) => {
                const token = await awaitFoundryRequestStage(this.user.getIdToken(), signal);
                const res = await awaitFoundryRequestStage(fetch(
                    `/api/foundry/inventory?lang=${encodeURIComponent(lang)}${pagedQuery}`,
                    {
                        headers: { 'Authorization': `Bearer ${token}` },
                        signal,
                    },
                ), signal);
                if (!res.ok || res.status === 202) {
                    const errorPayload = await awaitFoundryRequestStage(
                        res.json().catch(() => ({})),
                        signal,
                    );
                    // A very large collection's index preparing, or a page it
                    // has not reached yet: asked again, never an error.
                    if (errorPayload?.error === 'collection_index_building' || errorPayload?.error === 'collection_page_in_progress') {
                        return { indexWait: Math.max(250, Number(errorPayload.retryAfterMs) || 0) };
                    }
                    throw new Error(
                        errorPayload?.error
                        || res.statusText
                        || `Inventory request failed (${res.status})`,
                    );
                }
                return await awaitFoundryRequestStage(res.json(), signal);
            }, { ...this.surfaceReadOptions(whileHidden), timeoutMs });
            if (payload?.indexWait) {
                if (this.destroyed || (!this.surfaceActive && !whileHidden) || loadSeq !== this.inventoryLoadSeq) return;
                if (!this.inventoryLoaded) {
                    this.inventorySignature = '';
                    this.elementList.textContent = '';
                    const notice = document.createElement('p');
                    notice.style.cssText = `color:#666;padding:${uiCssPx(8)};`;
                    notice.dataset.indexWait = 'true';
                    notice.textContent = uiText('collection.cards.preparing', 'Preparing your collection. A very large one can take a while.');
                    this.elementList.appendChild(notice);
                }
                const delay = Math.min(10_000, payload.indexWait * 1.5 ** Math.min(this.inventoryIndexWaits++, 4));
                const timer = setTimeout(() => {
                    this.surfaceTimers?.delete?.(timer);
                    if (!this.destroyed && loadSeq === this.inventoryLoadSeq) {
                        void this.loadInventory({ preserveScroll, preserveView, preserveOnError, reconcileCanvas, showLoading: false, whileHidden, pageTurn });
                    }
                }, delay);
                this.surfaceTimers?.add?.(timer);
                return;
            }
            this.inventoryIndexWaits = 0;
            if (
                this.destroyed
                || (!this.surfaceActive && !whileHidden)
                || loadSeq !== this.inventoryLoadSeq
            ) return;
            this.applyInventoryPayload(payload, {
                preserveScroll, preserveView, reconcileCanvas, lang, changes,
            });
        } catch (err) {
            if (this.destroyed || loadSeq !== this.inventoryLoadSeq) return;
            /* A SLOW LINK IS NOT A CLOSED SCREEN (22 October 2026). The bound
               keeps a stalled read from holding the list, but on slow 3G the
               whole list can need longer while the surface's own files arrive,
               and a timeout was taken for the player leaving: the list stayed
               on "Loading..." for good. While Moon Fuse is shown, a read that
               timed out is asked again, inside this same load, with twice the
               time (at most INVENTORY_TIMEOUT_MAX_MS); the loading line stays. */
            if (isFoundryTimeout(err) && this.surfaceActive) {
                return await this._loadInventoryInner({
                    preserveScroll, preserveView, preserveOnError, reconcileCanvas,
                    showLoading: false, whileHidden, pageTurn,
                    timeoutMs: Math.min(MoonFoundry.INVENTORY_TIMEOUT_MAX_MS, timeoutMs * 2),
                });
            }
            if (err?.name === 'AbortError') return;
            console.error('Failed to load inventory:', err);
            if (!preserveOnError) {
                this.inventorySignature = '';
                this.elementList.innerHTML = `<p style="color:#f66;padding:${uiCssPx(8)};">${uiText('foundry.inventory.failed', 'Failed to load cards')}</p>`;
            }
        }
    }

    /**
     * The page, its size and the search a paged inventory read asks for ('' for
     * a whole list, whose reads stay exactly as they were). The first read of a
     * collection too large to list whole asks none: its answer is page 1 at the
     * server's size, which is exact for page 1 at any size that fits.
     */
    pagedInventoryQuery({ refresh = false } = {}) {
        if (!this.inventoryPaged) return '';
        const params = new URLSearchParams({
            page: String(Math.max(1, Number(this.inventoryPage) || 1)),
            pageSize: String(this.measureInventoryPageCapacity()),
            search: this.inventorySearch || '',
        });
        if (refresh) params.set('refresh', '1');
        return `&${params.toString()}`;
    }

    /**
     * A paged inventory's page was cut at another size than fits now: read
     * the page holding its first entry, at this size. Once per size and
     * place, however often the panel repaints meanwhile.
     */
    readPagedInventoryAtSize(pageSize) {
        const info = this.inventoryPagedInfo;
        if (!info) return;
        const page = Math.floor(((info.page - 1) * info.pageSize) / pageSize) + 1;
        const wanted = `${page}|${pageSize}|${this.inventorySearch}|${this.inventoryVersion}`;
        if (this.inventoryResizeRead === wanted) return;
        this.inventoryResizeRead = wanted;
        this.inventoryPage = page;
        this.readPagedInventory();
    }

    /** A paged inventory's search or page changed: read that page. */
    readPagedInventory({ debounce = false } = {}) {
        clearTimeout(this.inventorySearchTimer);
        this.inventorySearchTimer = null;
        const read = () => void this.loadInventory({
            preserveView: true,
            preserveOnError: true,
            showLoading: false,
            pageTurn: true,
        });
        if (debounce) this.inventorySearchTimer = setTimeout(read, 250);
        else read();
    }

    /**
     * Commit one inventory payload. Extracted so the warm entry-prefetch and
     * the ordinary request commit through EXACTLY the same path — a second
     * copy of this is how a prefetch quietly starts showing a different
     * collection than a normal load.
     */
    applyInventoryPayload(payload, { preserveScroll, preserveView, reconcileCanvas, lang, changes }) {
        const paged = Boolean(payload) && !Array.isArray(payload) && payload.mode === 'paged';
        this.inventoryPaged = paged;
        this.inventoryPagedInfo = paged ? {
            page: Math.max(1, Number(payload.page) || 1),
            pageSize: Math.max(1, Number(payload.pageSize) || 1),
            totalItems: Number.isFinite(payload.totalItems) ? payload.totalItems : null,
            hasMore: payload.hasMore === true,
        } : null;
        const latestPage = paged ? this.inventoryPagedInfo.page : (preserveView ? this.inventoryPage : 1);
        const latestScrollTop = preserveScroll ? this.captureInventoryScroll() : 0;
        const inventory = Array.isArray(payload) ? payload : (payload?.items || payload?.inventory || []);
        // Alphabetical by localized name (locale- and accent-aware), so
        // the list reads like an index instead of acquisition order. A paged
        // answer comes sorted so by the server, across all its pages.
        const language = i18n?.getLanguage?.() || 'en';
        const collator = new Intl.Collator(language, { sensitivity: 'base', numeric: true });
        const sorted = paged ? inventory.slice() : inventory
            .slice()
            .sort((a, b) => collator.compare(String(a?.name || ''), String(b?.name || '')));
        /* A RE-READ IS NOT AN EVENT (docs/asset-loading.md). Returning to
           Moon Fuse, a page resume and a language round trip all re-read the
           inventory, and every read rebuilt the whole list — the rows the
           player was reading, replaced by identical twins a round trip after
           the tab appeared. An unchanged answer for the page on screen keeps
           the rows; any difference repaints as before. */
        const signature = `${lang}|${paged ? JSON.stringify(this.inventoryPagedInfo) : ''}|${JSON.stringify(sorted)}`;
        if (
            this.inventoryLoaded
            && signature === this.inventorySignature
            && latestPage === this.inventoryPage
            && this.elementList?.childElementCount > 0
        ) {
            this.noteInventoryRead(lang, changes);
            if (reconcileCanvas) this.reconcileCanvasOwnership();
            this.loadStardust();
            return;
        }
        this.inventorySignature = signature;
        this.inventory = sorted;
        this.inventoryById = new Map(this.inventory.map(card => [card.cardId, card]));
        // First-discovered chips must light up even when their card object
        // came from a discovery poll or a restored workspace (neither
        // carries the flag) — the inventory is the authoritative source.
        this.firstDiscoveredCardIds = new Set(
            this.inventory.filter((card) => card.firstDiscovered === true).map((card) => card.cardId),
        );
        this.elements.forEach(({ el, card }) => {
            if (!el?.isConnected) return;
            const isFirst = card?.firstDiscovered === true
                || this.firstDiscoveredCardIds.has(card?.cardId);
            el.classList.toggle('first-discovered', isFirst);
            if (isFirst) MoonfallDiscoveryPalette?.applyTo(el, card?.cardId);
            else MoonfallDiscoveryPalette?.clearFrom(el);
        });
        this.cardDetailCache.clear();
        this.seedCardDetailCache(this.inventory, lang);
        this.inventoryVersion += 1;
        this.inventoryLoaded = true;
        this.noteInventoryRead(lang, changes);
        this.inventoryCacheKey = '';
        this.inventoryPage = latestPage;

        if (reconcileCanvas) {
            this.reconcileCanvasOwnership();
        }

        /* A paged page with nothing on it is a search that found nothing, or
           a page past an end that moved (cards left the collection): the
           collection itself is not empty. The first shows the empty grid, as
           a search of a whole list does; the second steps back a page. */
        if (paged && this.inventory.length === 0 && this.inventoryPagedInfo.page > 1) {
            this.inventoryPage = this.inventoryPagedInfo.page - 1;
            this.readPagedInventory();
            return;
        }
        if (this.inventory.length === 0 && !(paged && this.inventorySearch)) {
            this.renderSidebar();
            this.elementList.innerHTML = `<p style="color:#666;padding:${uiCssPx(8)};">${uiText('foundry.inventory.empty', 'No cards yet. Play to earn cards!')}</p>`;
            this.loadStardust();
            return;
        }

        if (this.isInventoryPanelActive() || this.isInventoryPanelPreparing()) {
            // A panel preparing unseen is laid out in its final box, so its
            // page geometry is as truthful as a shown one's.
            this.renderSidebar({ preservePage: preserveView });
        } else {
            // A hidden tab has no truthful grid geometry. Rendering against
            // the fallback page size would overwrite the restored page's
            // logical offset before Moon Fuse becomes visible.
            this.inventoryRenderedPageSize = null;
            this.elementList.replaceChildren();
        }
        if (preserveScroll && this.elementList) {
            this.inventoryScrollTop = latestScrollTop;
            if (this.isInventoryPanelActive()) {
                this.elementList.scrollTop = Math.min(latestScrollTop, this.elementList.scrollHeight);
                this.inventoryScrollTop = this.elementList.scrollTop;
            }
        }
        this.loadStardust();
    }

    reconcileCanvasOwnership() {
        const unknown = [];
        this.elements.slice().forEach(({ el, card }) => {
            if (!el?.isConnected) return;
            const pendingForgeId = String(el.dataset.pendingForgeId || '').trim();
            if (pendingForgeId || this.isElementLocked(el)) return;
            if (card?.forgeStatus === 'failed' || (card?.type === 'forged' && card?.name === 'Failed')) return;
            const cardId = String(card?.cardId || el.dataset.cardId || '').trim();
            if (!cardId || this.inventoryById.has(cardId)) return;
            unknown.push({ el, cardId });
        });
        if (unknown.length === 0) return;
        if (!this.inventoryPaged) {
            unknown.forEach(({ el }) => this.removeElement(el, { skipSessionSave: true }));
            this.scheduleWorkspaceSave();
            return;
        }
        /* A paged inventory holds one page: a card on the canvas but not on
           this page may still be held. Only the server's answer for those
           cards removes one (card details carry the player's copies). */
        void this.confirmCanvasCardsHeld(unknown);
    }

    async confirmCanvasCardsHeld(unknown) {
        const lang = i18n?.getLanguage?.() || 'en';
        const ids = [...new Set(unknown.map(({ cardId }) => cardId))];
        const held = new Set();
        try {
            const token = await this.user?.getIdToken?.();
            for (let start = 0; start < ids.length; start += 50) {
                const params = new URLSearchParams({ cardIds: ids.slice(start, start + 50).join(','), lang });
                const res = await fetch(`/api/cards/details?${params.toString()}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) return;
                for (const card of await res.json()) {
                    if ((Number(card?.ownedNormalQty) || 0) + (Number(card?.ownedStellarQty) || 0) > 0) held.add(card.id);
                }
            }
        } catch (_) {
            // Not knowing is not "not held": the canvas keeps its cards.
            return;
        }
        if (this.destroyed) return;
        let removedAny = false;
        unknown.forEach(({ el, cardId }) => {
            if (held.has(cardId) || !el?.isConnected) return;
            this.removeElement(el, { skipSessionSave: true });
            removedAny = true;
        });
        if (removedAny) this.scheduleWorkspaceSave();
    }

    measureInventoryPageCapacity() {
        const rect = this.elementList?.getBoundingClientRect?.() || {};
        const style = this.elementList ? window.getComputedStyle?.(this.elementList) : null;
        const width = Number(rect.width) || Number(this.elementList?.clientWidth) || 0;
        const height = Number(rect.height) || Number(this.elementList?.clientHeight) || 0;
        if (width <= 1 || height <= 1) return this.inventoryRenderedPageSize || 8;
        const paddingInline = finiteLayoutNumber(style?.paddingLeft)
            + finiteLayoutNumber(style?.paddingRight);
        const paddingBlock = finiteLayoutNumber(style?.paddingTop)
            + finiteLayoutNumber(style?.paddingBottom);
        const capacity = calculateInventoryPageCapacity({
            width,
            height,
            paddingInline,
            paddingBlock,
            columnGap: finiteLayoutNumber(style?.columnGap, finiteLayoutNumber(style?.gap, uiPx(8))),
            rowGap: finiteLayoutNumber(style?.rowGap, finiteLayoutNumber(style?.gap, uiPx(8))),
            itemMinWidth: finiteLayoutNumber(
                cssLengthPx(style?.getPropertyValue?.('--foundry-inventory-item-min-width')),
                uiPx(180)
            ),
            itemHeight: finiteLayoutNumber(
                cssLengthPx(style?.getPropertyValue?.('--foundry-inventory-item-height')),
                uiPx(60)
            ),
        });
        return Math.max(1, Math.min(this.inventoryPageSize, capacity || 1));
    }

    renderSidebar({
        resetScroll = false,
        preservePage = false,
        preserveLogicalOffset = preservePage,
    } = {}) {
        this.elementList.innerHTML = '';
        const previousPageSize = this.inventoryRenderedPageSize;
        const logicalOffset = previousPageSize
            ? Math.max(0, (this.inventoryPage - 1) * previousPageSize)
            : 0;

        const filterKey = `${this.inventoryVersion}|${this.inventorySearch}`;
        if (filterKey !== this.inventoryCacheKey) {
            this.inventoryCacheKey = filterKey;
            if (this.inventorySearch) {
                const term = this.inventorySearch;
                this.filteredInventory = this.inventory.filter(card =>
                    (card?.name || '').toLowerCase().includes(term)
                );
            } else {
                this.filteredInventory = this.inventory;
            }
            if (!preservePage) this.inventoryPage = 1;
        }

        // Every viewport derives a page from complete rendered grid rows.
        // Pagination, not an internal list scrollbar, reaches the rest.
        const invPageSize = this.measureInventoryPageCapacity();
        if (preserveLogicalOffset && previousPageSize && !this.inventoryPaged) {
            this.inventoryPage = Math.floor(logicalOffset / invPageSize) + 1;
        }
        const pagination = this.inventoryPaged
            ? this.pagedInventoryPagination(invPageSize)
            : this.paginateList(this.filteredInventory, invPageSize, this.inventoryPage);
        this.inventoryRenderedPageSize = pagination.pageSize;
        // A paged inventory's wanted page may be ahead of the page on hand
        // (its read is on the way); each answer sets it to the page it brought.
        if (!this.inventoryPaged) this.inventoryPage = pagination.page;
        this.updateInventoryPagination(pagination);
        if (pagination.exact === false) this.readPagedInventoryAtSize(invPageSize);

        pagination.pageItems.forEach(card => {
            const el = document.createElement('button');
            el.type = 'button';
            el.className = 'sidebar-element';
            const isFirstDiscovered = card?.firstDiscovered === true
                || this.firstDiscoveredCardIds?.has(card?.cardId) === true;
            if (isFirstDiscovered) {
                el.classList.add('first-discovered');
                MoonfallDiscoveryPalette?.applyTo(el, card.cardId);
            }
            const emojiSpan = document.createElement('span');
            emojiSpan.className = 'emoji';
            emojiSpan.textContent = this.getCardEmoji(card);
            el.appendChild(emojiSpan);
            const nameSpan = document.createElement('span');
            nameSpan.textContent = card.name;
            el.appendChild(nameSpan);
            const inspectGlyph = document.createElement('span');
            inspectGlyph.className = 'foundry-inspect-glyph';
            inspectGlyph.textContent = '⤢';
            inspectGlyph.setAttribute('aria-hidden', 'true');
            el.appendChild(inspectGlyph);
            if (card.isStellar) {
                const crystal = document.createElement('span');
                crystal.className = 'sidebar-stellar-badge';
                crystal.textContent = '\u2726';
                crystal.title = uiText('common.stellar', 'Stellar');
                el.appendChild(crystal);
            }
            el.dataset.cardId = card.cardId;
            el.dataset.cardName = card.name;
            el.setAttribute('aria-label', this.getInventoryCardAccessibleLabel(card));

            el.addEventListener('pointerdown', (e) => {
                if (!this.canEditWorkspace()) return;
                if (e.button !== 0) return;
                // Never let a second pointer steal an active drag's capture.
                if (this.isForeignDragPointer(e)) return;
                e.preventDefault();
                this.capturePointerForDrag(el, e);
                this.sidebarDrag = {
                    card,
                    startX: e.clientX,
                    startY: e.clientY,
                    pointerType: e.pointerType,
                    created: false,
                };
            });

            el.addEventListener('click', (event) => {
                // Pointer activation is completed by the drag/tap gesture above.
                // Native keyboard/button activation produces detail === 0.
                if (event.detail !== 0) return;
                this.activateInventoryCard(card);
            });

            this.attachHoverPreview(el);
            this.elementList.appendChild(el);
        });

        const visibleWarm = AssetPreloader?.warmCards?.(
            pagination.pageItems,
            { priority: 'high' }
        );
        if (visibleWarm && typeof visibleWarm.catch === 'function') {
            void visibleWarm.catch(() => undefined);
        }

        if (resetScroll) {
            this.elementList.scrollTop = 0;
            this.inventoryScrollTop = 0;
        }
    }

    getInventoryCardAccessibleLabel(card) {
        return String(card?.name || card?.cardId || '').trim();
    }

    activateInventoryCard(card) {
        if (!this.canEditWorkspace() || !this.canAddWorkspaceElement()) return null;
        const { width, height } = this.readWorkspaceCanvasMetrics();
        if (width <= 1 || height <= 1) return null;
        const slot = this.elements.length;
        const el = this.spawnElement(card, 0, 0, { skipSessionSave: true });
        const rect = el.getBoundingClientRect?.() || {};
        const elementWidth = Math.max(0, Number(el.offsetWidth) || Number(rect.width) || 0);
        const elementHeight = Math.max(0, Number(el.offsetHeight) || Number(rect.height) || 0);
        const staggerX = ((slot % 3) - 1) * uiPx(36);
        const staggerY = ((Math.floor(slot / 3) % 3) - 1) * uiPx(28);
        const x = Math.max(0, Math.min(
            Math.round(((width - elementWidth) / 2) + staggerX),
            Math.max(0, width - elementWidth)
        ));
        const y = Math.max(0, Math.min(
            Math.round(((height - elementHeight) / 2) + staggerY),
            Math.max(0, height - elementHeight)
        ));
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        this.settleChipClearOfOthers(el);
        // Journal/persist only after the final keyboard placement is known.
        this.scheduleWorkspaceSave();
        return el;
    }

    renderElementContent(el, card) {
        // Card names/emojis are AI-generated from user prompts and shared cross-user
        // (discovered recipes), so they are untrusted. Build with textContent — never
        // innerHTML — to prevent stored XSS.
        const isFirstDiscovered = card?.firstDiscovered === true
            || this.firstDiscoveredCardIds?.has(card?.cardId) === true;
        el.classList.toggle('first-discovered', isFirstDiscovered);
        if (isFirstDiscovered) MoonfallDiscoveryPalette?.applyTo(el, card?.cardId);
        else MoonfallDiscoveryPalette?.clearFrom(el);
        el.textContent = '';
        const emojiSpan = document.createElement('span');
        emojiSpan.className = 'emoji';
        emojiSpan.textContent = this.getCardEmoji(card);
        el.appendChild(emojiSpan);
        const nameSpan = document.createElement('span');
        nameSpan.textContent = card?.name || '';
        el.appendChild(nameSpan);
        const inspectGlyph = document.createElement('span');
        inspectGlyph.className = 'foundry-inspect-glyph';
        inspectGlyph.textContent = '⤢';
        inspectGlyph.setAttribute('aria-hidden', 'true');
        el.appendChild(inspectGlyph);
        this.syncForgeTimer(el);
    }

    markCanvasMutation() {
        if (!this.workspacePersistenceSuspended) {
            this.canvasMutationVersion += 1;
            this.workspaceSaveBlockedByConflict = false;
        }
    }

    spawnElement(card, x, y, {
        elementId = createFoundryCanvasElementId(),
        normalizedPosition = null,
        skipSessionSave = false,
    } = {}) {
        const el = document.createElement('div');
        el.className = 'element';
        this.renderElementContent(el, card);
        el.style.left = x + 'px';
        el.style.top = y + 'px';
        el.dataset.cardId = card.cardId;
        el.dataset.cardName = card.name;
        el.dataset.foundryElementId = elementId;
        el.setAttribute('role', 'button');
        el.tabIndex = 0;
        el.setAttribute('aria-label', this.getInventoryCardAccessibleLabel(card));

        el.addEventListener('pointerdown', (e) => this.onPointerDown(e, el));
        el.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            if (el.dataset?.foundryOperationId && el.classList?.contains('awaiting-art-selection')) {
                this.reopenArtSelection(el.dataset.foundryOperationId);
                return;
            }
            this.openCanvasChipPreview(el);
        });
        this.attachHoverPreview(el);

        this.canvasElements.appendChild(el);
        const entry = {
            el,
            card,
            normalizedPosition: Number.isFinite(normalizedPosition?.x)
                && Number.isFinite(normalizedPosition?.y)
                ? {
                    x: Math.min(1, Math.max(0, Number(normalizedPosition.x))),
                    y: Math.min(1, Math.max(0, Number(normalizedPosition.y))),
                }
                : null,
        };
        this.elements.push(entry);
        if (entry.normalizedPosition) {
            const { width, height } = this.captureWorkspaceCanvasMetrics();
            this.positionCanvasElement(entry, entry.normalizedPosition, width, height);
        }
        this.markCanvasMutation();
        if (!skipSessionSave) {
            this.scheduleWorkspaceSave();
        }

        return el;
    }

    updateElementCard(el, card, { skipSessionSave = false } = {}) {
        const entry = this.elements.find(e => e.el === el);
        const previous = entry?.card || {};
        const nextCard = { ...previous, ...card };
        if (entry) {
            entry.card = nextCard;
        }
        el.dataset.cardId = nextCard.cardId;
        el.dataset.cardName = nextCard.name;
        el.setAttribute('aria-label', this.getInventoryCardAccessibleLabel(nextCard));
        this.renderElementContent(el, nextCard);
        this.markCanvasMutation();
        if (!skipSessionSave) {
            this.scheduleWorkspaceSave();
        }
    }

    setElementLoading(el, isLoading, { skipSessionSave = false } = {}) {
        const wasLoading = el.classList.contains('loading');
        el.classList.toggle('loading', isLoading);
        this.syncForgeTimer(el);
        if (wasLoading !== isLoading) this.markCanvasMutation();
        if (!skipSessionSave) {
            this.scheduleWorkspaceSave();
        }
    }

    /* The fusion's whole wait as its poll last told it. The server counts it
       from the stored window as it answers (generationStageEta.js), so a
       cached answer is never stale; this only turns it into local instants. */
    rememberForgeEta(el, eta) {
        if (!el || !eta || typeof eta !== 'object') return;
        // Only with someone ahead: first in line is the moment before it starts,
        // and naming it would flash "#1" over the count on every fusion.
        if (eta.queueStatus === 'queued' && Number.isSafeInteger(eta.queuePosition) && eta.queuePosition > 1) {
            el.dataset.forgeQueuePosition = String(eta.queuePosition);
        } else {
            delete el.dataset.forgeQueuePosition;
        }
        if (eta.queueOverdue === true) {
            el.dataset.forgeOverdue = 'true';
            return;
        }
        delete el.dataset.forgeOverdue;
        const now = Date.now();
        const remember = (key, waitMs) => {
            if (!Number.isFinite(waitMs)) return;
            const next = now + waitMs;
            const previous = Number(el.dataset[key]);
            if (!Number.isFinite(previous) || Math.abs(next - previous) >= FORGE_TIMER_JITTER_MS) {
                el.dataset[key] = String(next);
            }
        };
        remember('forgeReadyAt', eta.estimatedWaitMinMs);
        remember('forgeSlowAt', eta.estimatedWaitMaxMs);
    }

    /* A result chip still forging; not the two cards held while a fusion is
       admitted, which wear the loading look too. */
    isForgingChip(el) {
        return Boolean(el?.classList?.contains('loading'))
            && (el.dataset.cardId === 'loading' || Boolean(el.dataset.pendingForgeId));
    }

    forgeTimerReading(el, now = Date.now()) {
        const readyAt = Number(el.dataset.forgeReadyAt);
        const slowAt = Number(el.dataset.forgeSlowAt);
        if (el.dataset.forgeOverdue === 'true' || (Number.isFinite(slowAt) && now > slowAt)) {
            return { text: '…', title: uiText('foundry.forgeTimer.longer', 'Taking longer than usual') };
        }
        // Waiting in line: its exact place, which the queue knows; the time
        // it adds is in the countdown the title carries.
        const position = Number(el.dataset.forgeQueuePosition);
        if (Number.isSafeInteger(position) && position > 0) {
            const inLine = uiText('foundry.forgeTimer.inLine', '#{{position}} in line', { position });
            const readyIn = Number.isFinite(readyAt) && readyAt > now
                ? uiText('foundry.forgeTimer.readyIn', 'Ready in about {{time}}', {
                    time: formatForgeCountdown(readyAt - now),
                })
                : '';
            return { text: `#${position}`, title: readyIn ? `${inLine} · ${readyIn}` : inLine };
        }
        if (!Number.isFinite(readyAt)) return { text: '…', title: '' };
        if (readyAt > now) {
            const time = formatForgeCountdown(readyAt - now);
            return {
                text: forgeEtaText(time),
                title: uiText('foundry.forgeTimer.readyIn', 'Ready in about {{time}}', { time }),
            };
        }
        return { text: '…', title: uiText('foundry.forgeTimer.almostReady', 'Almost ready') };
    }

    /* A forging chip carries its countdown from its first paint, in a slot as
       wide as "ETA 0:00" in the player's language (foundry.css): the chip
       keeps its size while it counts. */
    syncForgeTimer(el) {
        if (!el?.querySelector) return;
        let timer = el.querySelector(':scope > .foundry-forge-timer');
        if (!this.isForgingChip(el)) {
            timer?.remove();
            return;
        }
        if (!timer) {
            timer = document.createElement('span');
            timer.className = 'foundry-forge-timer';
            timer.setAttribute('aria-hidden', 'true');
            el.insertBefore(timer, el.querySelector(':scope > .foundry-inspect-glyph'));
        }
        const { text, title } = this.forgeTimerReading(el);
        const widthOf = forgeEtaText('0:00');
        if (timer.dataset.widthOf !== widthOf) timer.dataset.widthOf = widthOf;
        if (timer.textContent !== text) timer.textContent = text;
        if (timer.title !== title) timer.title = title;
        this.ensureForgeTimerTicking();
    }

    ensureForgeTimerTicking() {
        if (this.forgeTimerInterval || !this.surfaceActive) return;
        this.forgeTimerInterval = setInterval(() => this.tickForgeTimers(), FORGE_TIMER_TICK_MS);
    }

    stopForgeTimerTicking() {
        if (!this.forgeTimerInterval) return;
        clearInterval(this.forgeTimerInterval);
        this.forgeTimerInterval = null;
    }

    tickForgeTimers() {
        let counting = 0;
        for (const { el } of this.elements) {
            if (!el?.querySelector?.(':scope > .foundry-forge-timer')) continue;
            counting += 1;
            this.syncForgeTimer(el);
        }
        if (counting === 0) this.stopForgeTimerTicking();
    }

    isElementLocked(el) {
        return el?.classList?.contains('loading') || Boolean(el?.dataset?.foundryOperationId);
    }

    setElementLoadingByCardId(cardId, isLoading) {
        if (!cardId) return;
        const entry = this.elements.find((item) =>
            item?.el?.dataset?.cardId === cardId || item?.card?.cardId === cardId
        );
        if (entry?.el) {
            this.setElementLoading(entry.el, isLoading);
        }
    }

    showCreationCooldown(data) {
        const now = Date.now();
        if (now - this.lastCreationCooldownNoticeAt < 5000) return;
        this.lastCreationCooldownNoticeAt = now;
        this.showAchievementBanner(
            uiText('foundry.creationPausedTitle', 'Creation paused'),
            buildCreationCooldownMessage(data)
        );
    }

    failPendingForge(element, cardId, name = 'Failed') {
        if (!element?.isConnected) return;
        this.setElementLoading(element, false);
        this.updateElementCard(element, { cardId, name, type: 'forged', forgeStatus: 'failed' });
        delete element.dataset.pendingForgeId;
    }

    capturePointerForDrag(el, e) {
        this.dragPointerId = e.pointerId !== undefined ? e.pointerId : null;
        this.dragPointerCaptureEl = el || null;
        if (!el || e.pointerId === undefined) return;
        try {
            el.setPointerCapture?.(e.pointerId);
        } catch (err) {
            // jsdom and older engines lack pointer capture — dragging still works
            // through the document-level pointermove/pointerup listeners.
        }
    }

    releaseDragPointer() {
        const el = this.dragPointerCaptureEl;
        const pointerId = this.dragPointerId;
        this.dragPointerCaptureEl = null;
        this.dragPointerId = null;
        if (!el || pointerId === null || pointerId === undefined) return;
        try {
            el.releasePointerCapture?.(pointerId);
        } catch (err) {
            // Capture may already be gone (pointercancel, element removed).
        }
    }

    isForeignDragPointer(e) {
        return this.dragPointerId !== null
            && e.pointerId !== undefined
            && e.pointerId !== this.dragPointerId;
    }

    pointerDragSlop(pointerType = 'mouse') {
        const mobilePointer = pointerType === 'touch' || pointerType === 'pen';
        if (!mobilePointer) return uiPx(4);
        const shared = Number(CardDrag?.touchSlopPx?.());
        return Number.isFinite(shared) && shared > 0 ? shared : 12;
    }

    onPointerDown(e, el) {
        if (e.button !== 0) return;
        if (!this.canEditWorkspace()) return;
        // A second concurrent pointer must not steal the active drag — it
        // would overwrite the capture and strand the first element in a
        // permanent 'dragging' state. Joining a press that has not become a
        // drag yet, it makes the gesture a pinch, which is neither a drag nor
        // a tap: the press is dropped.
        if (this.isForeignDragPointer(e)) {
            if (!this.draggedEl && this.pendingCanvasDrag) {
                this.pendingCanvasDrag = null;
                this.releaseDragPointer();
            }
            return;
        }
        this.canvasPointerOrigin = { x: e.clientX, y: e.clientY };
        this.capturePointerForDrag(el, e);
        // EVERY pointer type defers the grab until real movement. A press that
        // never moves is an inspection, not a drag — and it must not be able to
        // reach the drop path, because a drop on the canvas SPENDS STARDUST.
        // Mouse used to grab instantly, so a plain click on a chip overlapping
        // a neighbour ran a discovery merge with no drag and no confirmation.
        this.pendingCanvasDrag = {
            el,
            startX: e.clientX,
            startY: e.clientY,
            pointerType: e.pointerType,
        };
    }

    /** Actually grab a canvas element after its pointer moves past tap slop. */
    startCanvasDrag(clientX, clientY, el) {
        // Once the player moves a chip, its current visual location becomes the
        // new semantic location. Until then, responsive clamping remains purely
        // presentational and retains the exact normalized server position.
        this.clearCanonicalCanvasPosition(el);
        this.markCanvasMutation();
        this.pendingCanvasDrag = null;
        this.draggedEl = el;
        el.classList.add('dragging');
        this.clearHoverTimer(el);
        this.dismissHoverPreview();

        const rect = el.getBoundingClientRect();
        this.offsetX = clientX - rect.left;
        this.offsetY = clientY - rect.top;
        // Measured once per drag rather than per move: it cannot change while
        // one finger is down, and `getComputedStyle` in a pointermove is a
        // layout read on every frame of the gesture.
        this.dropFloorPx = this.readCanvasDropFloor();

        // Bring to front
        el.style.zIndex = 100;
    }

    onPointerMove(e) {
        if (!this.canEditWorkspace()) return;
        if (this.isForeignDragPointer(e)) return;
        if (!this.draggedEl && this.pendingCanvasDrag) {
            const delta = Math.hypot(
                e.clientX - this.pendingCanvasDrag.startX,
                e.clientY - this.pendingCanvasDrag.startY,
            );
            if (delta >= this.pointerDragSlop(this.pendingCanvasDrag.pointerType)) {
                const { el, startX, startY } = this.pendingCanvasDrag;
                if (!el.isConnected) {
                    this.pendingCanvasDrag = null;
                } else {
                    // Grab from the ORIGINAL touch point so the element does
                    // not jump when the deferred grab kicks in.
                    this.startCanvasDrag(startX, startY, el);
                }
            }
        }
        if (!this.draggedEl && this.sidebarDrag && !this.sidebarDrag.created) {
            const delta = Math.hypot(
                e.clientX - this.sidebarDrag.startX,
                e.clientY - this.sidebarDrag.startY,
            );
            if (delta >= this.pointerDragSlop(this.sidebarDrag.pointerType)) {
                const playRect = this.playArea.getBoundingClientRect();
                const inside = e.clientX >= playRect.left
                    && e.clientX <= playRect.right
                    && e.clientY >= playRect.top
                    && e.clientY <= playRect.bottom;
                if (inside) {
                    if (!this.canAddWorkspaceElement()) {
                        this.sidebarDrag = null;
                        return;
                    }
                    const el = this.spawnElement(this.sidebarDrag.card, 0, 0);
                    this.sidebarDrag.created = true;
                    this.draggedEl = el;
                    el.classList.add('dragging');
                    this.clearHoverTimer(el);
                    this.offsetX = el.offsetWidth / 2;
                    this.offsetY = el.offsetHeight / 2;
                    el.style.zIndex = 100;
                }
            }
        }

        if (!this.draggedEl) return;

        const playRect = this.playArea.getBoundingClientRect();
        let x = e.clientX - playRect.left - this.offsetX;
        let y = e.clientY - playRect.top - this.offsetY;

        // Clamp to play area, less the floor the device's edge gesture owns.
        // `Math.max(0, ...)` on the limit keeps a canvas shorter than the
        // reservation from inverting into a negative ceiling.
        const floor = this.dropFloorPx || 0;
        /* `offsetHeight` is ROUNDED, and the chip's box is not: measured at
           932x300 a 10px reservation came out 8.875px, because the clamp let
           the chip sit the fractional remainder lower than intended. Where a
           band is reserved the limit is computed from the real height, so the
           reservation means the number it says. The fine-pointer path keeps
           the integer it has always used — there is no band to miss there, and
           a subpixel shift to a proven desktop clamp is not worth buying. */
        const chipHeight = floor > 0
            ? this.draggedEl.getBoundingClientRect().height || this.draggedEl.offsetHeight
            : this.draggedEl.offsetHeight;
        const lowest = Math.max(0, playRect.height - chipHeight - floor);
        x = Math.max(0, Math.min(x, playRect.width - this.draggedEl.offsetWidth));
        y = Math.max(0, Math.min(y, lowest));

        this.draggedEl.style.left = x + 'px';
        this.draggedEl.style.top = y + 'px';

        if (this.trashZone) {
            this.setTrashZoneActive(this.isOverTrash(this.draggedEl));
        }
    }

    onPointerUp(e) {
        if (this.isForeignDragPointer(e)) return;
        this.releaseDragPointer();
        // A press that never moved is a tap, not a drag — on any pointer type.
        const releasedHold = this.pendingCanvasDrag;
        this.pendingCanvasDrag = null;
        if (releasedHold?.el) {
            this.handleCanvasChipTap(releasedHold.el, e);
        }
        if (!this.canEditWorkspace()) {
            /* A TAP-OPENED PREVIEW MUST SURVIVE ITS OWN POINTERUP.
               This surface hosts the Moon Dream tab, where the workspace is
               never editable, so every release landed here and wiped the shared
               hover preview. On a mouse that is invisible — there is no
               pointerup while hovering. On TOUCH the tap opens the preview
               through TouchPreview's latch and then this very gesture's
               `pointerup` destroys it, so a tapped Moon Dream spell card
               appeared never to enlarge at all. Traced by stack-capturing the
               node removal: dismissHoverPreview <- clearSurfaceTransientState
               <- onPointerUp.
               While TouchPreview holds the tapped card, dismissal belongs to
               the latch — a second tap, or a tap elsewhere. Every other caller
               of clearSurfaceTransientState (drag end, teardown) is unchanged. */
            const tappedCard = typeof e.target?.closest === 'function'
                ? e.target.closest('.card')
                : null;
            if (!(tappedCard && TouchPreview?.isLatched?.(tappedCard))) {
                this.clearSurfaceTransientState();
            }
            return;
        }
        if (!this.draggedEl && this.sidebarDrag) {
            const { card } = this.sidebarDrag;
            this.sidebarDrag = null;
            const playRect = this.playArea.getBoundingClientRect();
            const inside = e.clientX >= playRect.left
                && e.clientX <= playRect.right
                && e.clientY >= playRect.top
                && e.clientY <= playRect.bottom;
            if (inside) {
                if (!this.canAddWorkspaceElement()) return;
                const el = this.spawnElement(card, 0, 0, { skipSessionSave: true });
                const rect = el.getBoundingClientRect();
                let x = e.clientX - playRect.left - rect.width / 2;
                let y = e.clientY - playRect.top - rect.height / 2;
                x = Math.max(0, Math.min(x, playRect.width - rect.width));
                y = Math.max(0, Math.min(y, playRect.height - rect.height));
                el.style.left = `${x}px`;
                el.style.top = `${y}px`;
                this.settleChipClearOfOthers(el);
                this.scheduleWorkspaceSave();
            } else {
                this.activateInventoryCard(card);
            }
            return;
        }

        if (!this.draggedEl) return;

        this.draggedEl.classList.remove('dragging');
        this.draggedEl.style.zIndex = 10;

        if (this.trashZone && this.isOverTrash(this.draggedEl)) {
            const toRemove = this.draggedEl;
            this.draggedEl = null;
            this.setTrashZoneActive(false);
            if (this.isElementLocked(toRemove)) {
                this.notifyUser(uiText('foundry.forgingLocked', 'Some elements are still forging and cannot be removed yet.'));
                this.scheduleWorkspaceSave();
                return;
            }
            this.removeElement(toRemove);
            return;
        }
        this.setTrashZoneActive(false);

        // Check for collision with another element
        const collision = this.findCollision(this.draggedEl);
        if (collision) {
            this.mergeElements(this.draggedEl, collision);
        } else {
            this.settleChipClearOfOthers(this.draggedEl);
            this.maybeReopenArtSelectionFromTap(this.draggedEl, e);
        }

        this.draggedEl = null;
        this.sidebarDrag = null;
        this.scheduleWorkspaceSave();
    }

    onPointerCancel(e) {
        if (this.isForeignDragPointer(e)) return;
        this.releaseDragPointer();
        this.pendingCanvasDrag = null;
        if (!this.draggedEl && !this.sidebarDrag) return;
        const moved = this.draggedEl;
        // Abort without trashing or merging: settle the element where it is.
        this.clearSurfaceTransientState();
        if (moved) {
            this.settleChipClearOfOthers(moved);
            this.scheduleWorkspaceSave();
        }
    }

    /**
     * The chip this one was actually DROPPED ON.
     *
     * A merge spends Stardust, so it has to be an unambiguous act. This used to
     * accept any box overlap, which meant two chips resting a pixel apart on a
     * crowded canvas would fuse the moment either was nudged — and, because the
     * inspect path is only reached when nothing collides, overlapping chips
     * could not be opened at all.
     *
     * The test is now the dragged chip's CENTRE landing inside a target: the
     * same "where did you put it" question a person answers by eye. Ties go to
     * the topmost chip, which is the one under the cursor.
     */
    findCollision(el) {
        const rect1 = el.getBoundingClientRect();
        const cx = rect1.left + rect1.width / 2;
        const cy = rect1.top + rect1.height / 2;

        let hit = null;
        for (const item of this.elements) {
            if (item.el === el) continue;
            const rect2 = item.el.getBoundingClientRect();
            if (cx < rect2.left || cx > rect2.right || cy < rect2.top || cy > rect2.bottom) continue;
            if (!hit || this.chipStackOrder(item.el) >= this.chipStackOrder(hit)) hit = item.el;
        }
        return hit;
    }

    /** Paint order for tie-breaking a drop onto stacked chips. */
    chipStackOrder(el) {
        const z = Number.parseInt(el?.style?.zIndex, 10);
        if (Number.isFinite(z)) return z;
        return this.elements.findIndex((item) => item.el === el);
    }

    async fetchDiscoveryOperation(operationId, logPrefix = 'Discovery operation', {
        abortOnHide = true,
        timeoutMs = this.operationRequestTimeoutMs,
    } = {}) {
        const url = `/api/foundry/discoveries/${encodeURIComponent(operationId)}`;
        return this.runBoundedRequest(async (signal) => {
            const headers = {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                // A draft the fusion also wrote in the player's language reads in it.
                'X-Moonfall-Language': i18n?.getLanguage?.() || 'en',
            };
            if (this.user?.getIdToken) {
                const token = await awaitFoundryRequestStage(
                    this.user.getIdToken(),
                    signal,
                );
                if (token) headers.Authorization = `Bearer ${token}`;
            }
            const response = await awaitFoundryRequestStage(fetch(url, {
                cache: 'no-store',
                headers,
                signal,
            }), signal);
            let data = null;
            try {
                data = await awaitFoundryRequestStage(response.json(), signal);
            } catch (error) {
                if (signal.aborted) throw error;
                console.error(`${logPrefix} parse error:`, error);
            }
            return { response, data };
        }, { abortOnHide, timeoutMs });
    }

    buildMergePairKey(card1, card2) {
        const a = String(card1?.cardId || '');
        const b = String(card2?.cardId || '');
        return a < b ? `${a}|${b}` : `${b}|${a}`;
    }

    async checkMergeStardust(card1, card2) {
        // Re-fusing a personally known recipe and resuming a paid discovery
        // are free. Resolve that distinction before showing any forging state.
        try {
            const quote = await this.runBoundedRequest((signal) => awaitFoundryRequestStage(
                MoonfallCloud.convex.query('foundryDiscoveryOperations:getMergeStardustCost', {
                    parent1Id: card1.cardId,
                    parent2Id: card2.cardId,
                }), signal,
            ));
            if (this.destroyed || !this.surfaceActive) return false;
            if (!Number.isSafeInteger(quote?.cost) || quote.cost < 0
                || !Number.isSafeInteger(quote?.stardust) || quote.stardust < 0) {
                throw new Error('foundry_merge_pricing_invalid');
            }
            this.updateStardustDisplay(quote.stardust);
            announceStardust(quote.stardust);
            if (quote.stardust >= quote.cost) return true;
            this.notifyUser(uiText(
                'foundry.notEnoughStardust',
                'Not enough Stardust. You need {{cost}} ✦ to fuse.',
                { cost: quote.cost },
            ));
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.warn('Failed to check fusion pricing:', err);
                this.notifyUser(uiText('foundry.pricingUnavailable', 'Fusion pricing unavailable.'));
            }
        }
        return false;
    }

    async mergeElements(el1, el2) {
        if (!this.canEditWorkspace()) return;
        const card1 = this.elements.find(e => e.el === el1)?.card;
        const card2 = this.elements.find(e => e.el === el2)?.card;

        if (!card1 || !card2) return;
        if (this.isElementLocked(el1) || this.isElementLocked(el2)) return;

        const pairKey = this.buildMergePairKey(card1, card2);
        if (this.pendingMergePairs.has(pairKey)) return;
        this.pendingMergePairs.add(pairKey);
        let started = false;

        try {
            // A spell already known as one is answered before the price.
            const spellRefusal = this.getKnownSpellRefusal(card1) || this.getKnownSpellRefusal(card2);
            if (spellRefusal) {
                this.notifyUser(spellRefusal);
                return;
            }
            const cost = this.forgeCosts?.DISCOVERY;
            const balance = this.lastStardustBalance;
            if (Number.isSafeInteger(cost) && cost > 0
                && Number.isSafeInteger(balance) && balance < cost
                && !await this.checkMergeStardust(card1, card2)) return;
            if (!this.canEditWorkspace()) return;
            if (this.isElementLocked(el1) || this.isElementLocked(el2)) return;
            if (!this.elements.some((entry) => entry.el === el1)
                || !this.elements.some((entry) => entry.el === el2)) return;
            started = true;
            this.setElementLoading(el1, true, { skipSessionSave: true });
            this.setElementLoading(el2, true, { skipSessionSave: true });
            if (!await this.flushWorkspaceBeforeMerge()) return;
            if (
                this.destroyed
                || !this.elements.some((entry) => entry.el === el1)
                || !this.elements.some((entry) => entry.el === el2)
            ) return;
            return await this.runMergeElements(el1, el2, card1, card2);
        } catch (err) {
            console.error('Merge failed before completion:', err);
        } finally {
            this.pendingMergePairs.delete(pairKey);
            if (!started && this.canEditWorkspace() && !this.isElementLocked(el1)
                && this.elements.some((entry) => entry.el === el1)) {
                this.settleChipClearOfOthers(el1);
                this.scheduleWorkspaceSave();
            }
            if (started && !this.destroyed && this.elements.some((entry) => entry.el === el1)) {
                this.setElementLoading(el1, false, { skipSessionSave: true });
            }
            if (started && !this.destroyed && this.elements.some((entry) => entry.el === el2)) {
                this.setElementLoading(el2, false, { skipSessionSave: true });
            }
        }
    }

    async flushWorkspaceBeforeMerge() {
        if (this.workspaceSaveTimer) {
            clearTimeout(this.workspaceSaveTimer);
            this.workspaceSaveTimer = null;
        }
        await this.persistWorkspaceState({ force: true, allowPendingMerge: true });
        return this.canEditWorkspace()
            && !this.workspaceSaveBlockedByConflict
            && this.lastSavedWorkspaceHash === JSON.stringify(this.buildWorkspaceElements());
    }

    /** Why this card cannot be a parent now, in the player's words; null when it can. */
    async getInvalidMergeReason(card) {
        const cardId = typeof card?.cardId === 'string' ? card.cardId : '';
        if (!cardId) return 'Invalid card selection.';
        if (cardId === 'loading' || cardId.startsWith('pending_')) {
            return 'That card is still forging. Please wait for it to finish.';
        }
        if (card?.forgeStatus === 'failed' || card?.name === 'Failed') {
            return 'That card failed to forge and cannot be merged.';
        }
        const inventoryCard = this.inventoryById.get(cardId);
        const fetchedDetails = !inventoryCard ? await this.fetchCardDetails(cardId) : null;
        const resolvedCard = inventoryCard || fetchedDetails || card;
        const resolvedName = resolvedCard?.name || card?.name;
        if (resolvedName === 'Unknown') {
            return 'That card is still forging. Please wait for it to finish.';
        }
        const spellRefusal = this.spellRefusalFor(resolvedCard, resolvedName || cardId);
        if (spellRefusal) return spellRefusal;
        const localForgeStatus = resolvedCard?.forgeStatus || card?.forgeStatus;
        if (resolvedCard?.type === 'forged' && localForgeStatus !== 'complete') {
            return 'That forged card is still finalizing its art. Please wait.';
        }
        return null;
    }

    /* A SPELL ON THE CANVAS IS NAMED, NOT SENT. A recipe can discover a
       classic spell, and its chip stays on the canvas like any other; the
       server fuses Character cards only. One player dropped cards on Entropy
       three times and was told only "Moon Foundry only supports Character
       cards." (replay 01a175f7, 26 Oct 2026). */
    spellRefusalFor(card, cardName) {
        if (card?.type !== 'Spell') return null;
        return uiText(
            'foundry.discovery.spellCannotFuse',
            '{{cardName}} is a spell. Moon Fuse fuses Character cards only; craft {{cardName}} from your Collection.',
            { cardName: cardName || card.name || card.cardId },
        );
    }

    /** The refusal for a card already known to be a spell, without waiting:
        the drop's own lock stays synchronous (the spam guard). */
    getKnownSpellRefusal(card) {
        const cardId = typeof card?.cardId === 'string' ? card.cardId : '';
        if (!cardId) return null;
        const lang = i18n?.getLanguage?.() || 'en';
        const known = this.inventoryById.get(cardId)
            || this.cardDetailCache.get(this.getCardDetailCacheKey(cardId, lang));
        return this.spellRefusalFor(known, known?.name || card.name);
    }

    async getMergeRefusal(card1, card2) {
        const [reason1, reason2] = await Promise.all([
            this.getInvalidMergeReason(card1),
            this.getInvalidMergeReason(card2),
        ]);
        return reason1 || reason2;
    }

    async runMergeElements(el1, el2, card1, card2) {
        const invalidReason = await this.getMergeRefusal(card1, card2);
        if (this.destroyed) return;
        if (invalidReason) {
            this.notifyUser(invalidReason);
            return;
        }

        // Get midpoint
        const rect1 = el1.getBoundingClientRect();
        const rect2 = el2.getBoundingClientRect();
        const playRect = this.playArea.getBoundingClientRect();

        const midX = ((rect1.left + rect2.left) / 2) - playRect.left;
        const midY = ((rect1.top + rect2.top) / 2) - playRect.top;
        const sourceSnapshots = [
            { card: card1, ...this.getElementCanvasCoordinates(el1), elementId: el1.dataset.foundryElementId },
            { card: card2, ...this.getElementCanvasCoordinates(el2), elementId: el2.dataset.foundryElementId },
        ];

        // Remove both elements
        this.removeElement(el1, { skipSessionSave: true });
        this.removeElement(el2, { skipSessionSave: true });

        // Show loading element
        const loadingEl = this.spawnElement(
            { cardId: 'loading', name: uiText('collection.detail.forging', 'Forging...') },
            Math.round(midX),
            Math.round(midY),
            { skipSessionSave: true }
        );
        // The forge comes to rest like any chip, and its result rests there.
        this.settleChipClearOfOthers(loadingEl);
        this.setElementLoading(loadingEl, true, { skipSessionSave: true });
        const projectionGeneration = this.workspaceProjectionGeneration;
        let rolledBack = false;
        const restoreSources = ({ scheduleSave = true } = {}) => {
            if (rolledBack || this.destroyed) return;
            rolledBack = true;
            this.removeElement(loadingEl, { skipSessionSave: true });
            sourceSnapshots.forEach((source) => {
                this.spawnElement(source.card, source.x, source.y, {
                    elementId: source.elementId,
                    skipSessionSave: true,
                });
            });
            if (scheduleSave) this.scheduleWorkspaceSave();
        };

        this.playSoundEffect('draw');

        try {
            const { res, result, token } = await this.runBoundedRequest(async (signal) => {
                const token = await awaitFoundryRequestStage(this.user.getIdToken(), signal);
                const res = await awaitFoundryRequestStage(fetch('/api/foundry/discover', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        parent1Id: card1.cardId,
                        parent2Id: card2.cardId,
                        language: i18n?.getLanguage?.() || 'en',
                        leaseToken: this.workspaceLeaseToken,
                        baseRevision: this.workspaceRevision,
                        sourceElementIds: sourceSnapshots.map(({ elementId }) => elementId),
                        resultElementId: loadingEl.dataset.foundryElementId,
                    }),
                    signal,
                }), signal);
                const result = await awaitFoundryRequestStage(
                    res.json().catch(() => ({})),
                    signal,
                );
                return { res, result, token };
            });
            if (this.destroyed || !this.surfaceActive) return;
            if (!this.isCurrentMergeAdmission(loadingEl, projectionGeneration, result?.workspace?.revision)) {
                await this.openWorkspace({ authToken: token });
                return;
            }
            void this.loadStardust();

            if ([
                'workspace_conflict',
                'workspace_lease_lost',
                'workspace_lease_expired',
                'workspace_not_found',
            ].includes(result.outcome)) {
                restoreSources({ scheduleSave: false });
                await this.openWorkspace({ authToken: token });
                return;
            }

            if (result.outcome === 'refund_pending') {
                this.notifyUser(uiText(
                    'foundry.discovery.refundPending',
                    'Your previous discovery is still being refunded. No Stardust was charged. Please try again after the refund completes.'
                ));
                restoreSources();
                return;
            }

            if (!res.ok || result.error) {
                const isCooldown = result.code === 'creation_rate_limited'
                    || result.code === 'foundry_discover_throttled';
                // The opt-out refusal is a player's own setting talking back at
                // Moonfall's own refusal — a pause, a busy or silent service —
                // in plain words and the player's language (service-pause.js).
                const serviceLine = creationRefusalLine(result, uiText, {
                    locale: document.documentElement.lang || undefined,
                });
                this.notifyUser(isCooldown
                    ? buildCreationCooldownMessage(result)
                    : (serviceLine || result.error || uiText(
                        'foundry.discovery.startFailed',
                        'Failed to start this discovery.'
                    )));
                restoreSources();
                return;
            }

            const applied = await this.applyAtomicDiscoverResult(result, loadingEl, projectionGeneration);
            if (!applied) {
                await this.openWorkspace({ authToken: token });
                return;
            }
            void this.consumeAndShowCloudAchievementBanners();

        } catch (err) {
            if (this.destroyed) return;
            if (err?.name !== 'AbortError') console.error('Merge failed:', err);
            restoreSources({ scheduleSave: false });
            if (this.surfaceActive) await this.openWorkspace();
        }
    }

    isCurrentMergeAdmission(loadingEl, projectionGeneration, responseRevision = null) {
        if (this.destroyed || !loadingEl?.isConnected) return false;
        if (this.workspaceProjectionGeneration !== projectionGeneration) return false;
        if (!this.elements.some((entry) => entry.el === loadingEl)) return false;
        return !Number.isSafeInteger(responseRevision) || responseRevision >= this.workspaceRevision;
    }

    async applyAtomicDiscoverResult(result, loadingEl, projectionGeneration = this.workspaceProjectionGeneration) {
        const workspace = result?.workspace;
        const target = this.normalizeWorkspaceElements([workspace?.resultElement])[0];
        if (
            !workspace
            || !['applied', 'already_applied'].includes(workspace.outcome)
            || !Number.isSafeInteger(workspace.revision)
            || !target
        ) {
            throw new Error('Foundry discovery response omitted its atomic workspace result');
        }
        if (!this.isCurrentMergeAdmission(loadingEl, projectionGeneration, workspace.revision)) {
            return false;
        }

        let finalCard = null;
        if (target.kind !== 'discovery_operation') {
            finalCard = await this.fetchCardDetails(target.cardId);
            if (!this.isCurrentMergeAdmission(loadingEl, projectionGeneration, workspace.revision)) {
                return false;
            }
            if (!finalCard?.cardId) finalCard = { cardId: target.cardId, name: target.cardId };
        }

        const { width, height } = this.captureWorkspaceCanvasMetrics();
        loadingEl.dataset.foundryElementId = target.elementId;
        // The server's canvas, for the saved hash below; the chip itself stays
        // where its forge shows until it comes to rest (the end of this).
        this.setCanonicalCanvasPosition(loadingEl, { x: target.x, y: target.y });
        this.moveWorkspaceElementToOrder(loadingEl, workspace.resultElement.order);

        let pending = null;
        if (target.kind === 'discovery_operation') {
            const operationId = target.operationId;
            loadingEl.dataset.foundryOperationId = operationId;
            loadingEl.dataset.pendingForgeId = operationId;
            pending = {
                element: loadingEl,
                inventoryUpdated: false,
                mergeCompleteSoundPlayed: false,
            };
            this.pendingForges.set(operationId, pending);
            this.materializedOperationIds.add(operationId);
        } else {
            this.updateElementCard(loadingEl, { ...finalCard, type: 'standard' }, { skipSessionSave: true });
            this.setElementLoading(loadingEl, false, { skipSessionSave: true });
            delete loadingEl.dataset.pendingForgeId;
            delete loadingEl.dataset.foundryOperationId;
            if (target.sourceOperationId) {
                loadingEl.dataset.foundrySourceOperationId = target.sourceOperationId;
                this.materializedOperationIds.add(target.sourceOperationId);
            }
        }

        this.workspaceRevision = workspace.revision;
        if (Number.isFinite(Number(workspace.updatedAt)) && Number(workspace.updatedAt) > 0) {
            this.workspaceUpdatedAt = Number(workspace.updatedAt);
        }
        this.workspaceLeaseExpiresAt = Number(workspace.leaseExpiresAt) || this.workspaceLeaseExpiresAt;
        this.workspaceSaveBlockedByConflict = false;
        this.workspaceProjectionGeneration += 1;
        this.lastSavedWorkspaceHash = JSON.stringify(this.buildWorkspaceElements());
        this.clearCrashJournal();
        this.setWorkspaceMode('writer', null, 'save');
        this.scheduleWorkspaceLeaseRenewal();
        /* The server saves the result where its sources' SAVED spots average,
           which need not be where they showed (a chip spread off another
           shows away from its saved spot) nor clear of the chips around it:
           fusing two chips of a stack saved the result on the stack. It rests
           where its forge showed, clear of its neighbours, and is saved there. */
        this.settleChipClearOfOthers(loadingEl);
        const restsAt = this.getElementCanvasCoordinates(loadingEl);
        const serverSpot = this.projectCanvasSpot(loadingEl, { x: target.x, y: target.y }, width, height);
        if (restsAt.x !== serverSpot.x || restsAt.y !== serverSpot.y) {
            this.clearCanonicalCanvasPosition(loadingEl);
            this.scheduleWorkspaceSave();
        }

        if (pending) {
            if (MoonfallAnalytics) MoonfallAnalytics.capture('foundry_generation_started');
            void this.pollForResult(target.operationId, pending);
        } else {
            // A first personal discovery is charged and grants a copy; a
            // finished discovery claimed here grants its copy. Re-fusing a
            // recipe the player already knows grants nothing.
            if (result?.charged === true || result?.granted === true) announceHoldingsChanged();
            this.playSoundEffect('magic');
            void this.loadInventory({
                preserveScroll: true,
                preserveView: true,
                preserveOnError: true,
                showLoading: false,
            });
        }
        return true;
    }

    moveWorkspaceElementToOrder(el, rawOrder) {
        const currentIndex = this.elements.findIndex((entry) => entry.el === el);
        const order = Number(rawOrder);
        if (currentIndex < 0 || !Number.isSafeInteger(order)) return;
        const [entry] = this.elements.splice(currentIndex, 1);
        const targetIndex = Math.min(this.elements.length, Math.max(0, order));
        this.elements.splice(targetIndex, 0, entry);
        const nextEl = this.elements[targetIndex + 1]?.el || null;
        this.canvasElements.insertBefore(el, nextEl);
    }

    isPendingForgeContextCurrent(operationId, context) {
        if (!context?.element?.isConnected) return false;
        const mapped = this.pendingForges.get(operationId);
        if (mapped && mapped !== context) return false;
        return this.elements.some((entry) => entry.el === context.element);
    }

    deletePendingForgeIfOwned(operationId, context) {
        if (this.pendingForges.get(operationId) !== context) return false;
        this.pendingForges.delete(operationId);
        return true;
    }

    schedulePollForResult(operationId, context, delayMs) {
        if (context) context.resumePollId = operationId;
        const pollKey = context || operationId;
        const existingTimer = this.forgePollTimers.get(pollKey);
        if (existingTimer) clearTimeout(existingTimer);
        this.forgePollTimers.delete(pollKey);
        if (!this.surfaceActive) return;

        const timer = setTimeout(() => {
            if (this.forgePollTimers.get(pollKey) !== timer) return;
            this.forgePollTimers.delete(pollKey);
            void this.pollForResult(operationId, context);
        }, Math.max(0, Number(delayMs) || 0));
        this.forgePollTimers.set(pollKey, timer);
    }

    /**
     * Reads, at once, the state of each discovery a snapshot lists that no
     * chip on the canvas shows yet. Off the visible surface nothing is polled,
     * so nothing is read.
     */
    readSnapshotDiscoveries(rawElements) {
        const reads = new Map();
        if (!this.surfaceActive) return reads;
        this.normalizeWorkspaceElements(rawElements).forEach((saved) => {
            if (saved.kind !== 'discovery_operation') return;
            const { operationId } = /** @type {{ operationId: string }} */ (saved);
            if (reads.has(operationId) || this.pendingForges.get(operationId)?.element?.isConnected) return;
            reads.set(operationId, this.fetchDiscoveryOperation(operationId, 'Discovery poll', {
                abortOnHide: true,
            }));
        });
        return reads;
    }

    /**
     * `answered` is a read already in hand, which the chip takes without
     * waiting; `read` is one still out, which the poll waits for instead of
     * asking again.
     */
    async pollForResult(operationId, context, { answered = null, read = null } = {}) {
        context = context || this.pendingForges.get(operationId);
        if (!this.isPendingForgeContextCurrent(operationId, context)) return;
        if (context) context.resumePollId = operationId;
        if (!this.surfaceActive) return;
        if (context?.pollInFlight) {
            context.resumePollRequested = true;
            return;
        }
        if (context) context.pollInFlight = true;
        try {
            const element = context?.element;
            const { response, data } = answered || await (read || this.fetchDiscoveryOperation(operationId, 'Discovery poll', {
                abortOnHide: true,
            }));
            if (!this.surfaceActive || !this.isPendingForgeContextCurrent(operationId, context)) return;
            if ([403, 404, 422].includes(response?.status)) {
                this.removeElement(element, { skipSessionSave: true, triggerRecovery: true });
                this.deletePendingForgeIfOwned(operationId, context);
                this.materializedOperationIds.delete(operationId);
                this.dismissedArtOperations.delete(operationId);
                this.artReadySoundPlayed.delete(operationId);
                this.scheduleWorkspaceSave();
                return;
            }
            if (!response.ok || !data || data.error || !data.state) {
                this.schedulePollForResult(operationId, context, 2500);
                return;
            }

            const draft = data.draft && typeof data.draft === 'object' ? data.draft : null;
            this.rememberForgeEta(element, data.eta);
            if (draft?.name) {
                this.updateElementCard(element, {
                    cardId: draft.cardId || 'loading',
                    name: draft.name,
                    emoji: draft.emoji,
                    // Carried so the interim hover preview can render the full
                    // draft text before the card document exists.
                    description: draft.description || '',
                    manaCost: Number.isFinite(draft.manaCost) ? draft.manaCost : 0,
                    type: 'forged',
                    forgeStatus: data.state,
                }, { skipSessionSave: true });
                element.dataset.foundryOperationId = operationId;
                element.dataset.pendingForgeId = operationId;
                if (context && !context.mergeCompleteSoundPlayed) {
                    context.mergeCompleteSoundPlayed = true;
                    this.playSoundEffect('magic');
                }
            }

            if (data.state === 'failed') {
                /* A PAID FUSION THAT FAILED WAS REMOVED WITHOUT A WORD. Its
                   Stardust comes back (foundryDiscoveryOperationsData.js), and
                   the player is told so, plainly, before the card goes. */
                this.notifyUser(data.refunded === true
                    ? uiText(
                        'foundry.discovery.failedRefunded',
                        'Moon Fuse couldn\'t finish this card because of a problem on our side. Your Stardust was refunded.'
                    )
                    : uiText(
                        'foundry.discovery.failedRefunding',
                        'Moon Fuse couldn\'t finish this card because of a problem on our side. Your Stardust is being refunded.'
                    ));
                this.removeElement(element, { skipSessionSave: true, triggerRecovery: true });
                this.deletePendingForgeIfOwned(operationId, context);
                this.materializedOperationIds.delete(operationId);
                this.artReadySoundPlayed.delete(operationId);
                this.scheduleWorkspaceSave();
                return;
            }

            if (data.state === 'awaiting_art_selection') {
                this.setElementLoading(element, false, { skipSessionSave: true });
                element.classList.add('awaiting-art-selection');
                element.title = uiText(
                    'foundry.discovery.awaitingArtSelection',
                    'Waiting for an eligible discoverer to choose the final art. Tap this card to choose now — it stays locked until then.'
                );
                const hasSelectableArt = Array.isArray(data.artVariations)
                    && data.artVariations.some((art) => (
                        art?.status === 'complete' && Boolean(normalizeFoundryArtSource(art?.url))
                    ));
                this.setAwaitingArtSelectionAccessibility(
                    element,
                    operationId,
                    Boolean(data.canSelect && hasSelectableArt),
                );
                if (data.canSelect && hasSelectableArt && !this.dismissedArtOperations.has(operationId)) {
                    this.queueArtSelection({
                        data,
                        operationId,
                        x: context?.x,
                        y: context?.y,
                    });
                }
                this.schedulePollForResult(operationId, context, 5000);
                return;
            }

            if (data.state === 'complete' && data.finalCardId) {
                let finalCard = await this.fetchCardDetails(data.finalCardId);
                if (!this.surfaceActive || !this.isPendingForgeContextCurrent(operationId, context)) return;
                if (!finalCard) {
                    finalCard = { cardId: data.finalCardId, name: data.draft?.name || data.finalCardId };
                }
                this.dismissedArtOperations.delete(operationId);
                // The claim settles this player's copy (granted now, or
                // already by the selection); announced once it has.
                void this.claimCompletedDiscovery(operationId).then(announceHoldingsChanged);
                element.classList.remove('awaiting-art-selection');
                this.setAwaitingArtSelectionAccessibility(element, operationId, false);
                element.removeAttribute('title');
                element.dataset.foundrySourceOperationId = operationId;
                delete element.dataset.foundryOperationId;
                delete element.dataset.pendingForgeId;
                this.updateElementCard(element, { ...finalCard, type: 'standard', forgeStatus: 'complete' });
                this.setElementLoading(element, false);
                this.deletePendingForgeIfOwned(operationId, context);
                this.artReadySoundPlayed.delete(operationId);
                if (context && !context.inventoryUpdated) {
                    context.inventoryUpdated = true;
                    void this.loadInventory({
                        preserveScroll: true,
                        preserveView: true,
                        preserveOnError: true,
                        showLoading: false,
                    });
                }
                void this.consumeAndShowCloudAchievementBanners();
                this.scheduleWorkspaceSave();
                return;
            }

            this.setElementLoading(element, true, { skipSessionSave: true });
            if (!draft?.name) {
                this.updateElementCard(element, {
                    cardId: 'loading',
                    name: data.state === 'generating_art'
                        ? uiText('foundry.queue.artUnderway', 'Art is underway...')
                        : uiText('foundry.discovery.discovering', 'Discovering...'),
                    type: 'forged',
                    forgeStatus: data.state,
                }, { skipSessionSave: true });
            }
            this.schedulePollForResult(operationId, context, 2000);
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.error('Poll error:', err);
            }
            if (this.isPendingForgeContextCurrent(operationId, context)) {
                this.schedulePollForResult(operationId, context, 2500);
            }
        } finally {
            if (context) {
                context.pollInFlight = false;
                if (context.resumePollRequested) {
                    context.resumePollRequested = false;
                    if (this.pendingForges.get(operationId) === context && this.surfaceActive) {
                        this.schedulePollForResult(context.resumePollId || operationId, context, 0);
                    }
                }
            }
        }
    }

    async claimCompletedDiscovery(operationId) {
        try {
            await this.runBoundedRequest(async (signal) => {
                const token = await awaitFoundryRequestStage(this.user.getIdToken(), signal);
                await awaitFoundryRequestStage(fetch(
                    `/api/foundry/discoveries/${encodeURIComponent(operationId)}/claim`,
                    {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                        },
                        signal,
                    },
                ), signal);
            });
        } catch (_) { }
    }

    queueArtSelection(payload) {
        const operationId = String(payload?.operationId || '').trim();
        if (!operationId || this.dismissedArtOperations.has(operationId)) return;
        const artSources = Array.isArray(payload?.data?.artVariations)
            ? payload.data.artVariations
                .map((art) => ({
                    ...art,
                    normalizedUrl: normalizeFoundryArtSource(art?.url),
                }))
                .filter((art) => art.status === 'complete' && art.normalizedUrl)
                .map((art) => ({ imageUrl: art.normalizedUrl }))
            : [];
        if (artSources.length > 0) {
            // Polling exposes these URLs before the modal has to paint them.
            // Start the shared decode/LRU warm immediately and let the strict
            // final-size gate below remain the reveal authority.
            void Promise.resolve(AssetPreloader?.warmCards?.(
                artSources,
                { priority: 'high' },
            )).catch(() => undefined);
        }
        if (!this.artReadySoundPlayed.has(operationId)) {
            this.playSoundEffect('magic');
            this.artReadySoundPlayed.add(operationId);
        }
        if (this.activeArtModalOperationId === operationId) return;
        const existingIndex = this.artQueue.findIndex((item) => item?.operationId === operationId);
        if (existingIndex >= 0) {
            this.artQueue[existingIndex] = payload;
            return;
        }
        this.artQueue.push(payload);
        if (!this.activeArtModal) {
            this.showNextArtModal();
        }
    }

    dismissActiveArtModal() {
        if (!this.activeArtModal || !this.activeArtModalOperationId) return;
        const now = Date.now();
        if (now < this.artDismissLatchUntil) return;
        this.artDismissLatchUntil = now + ART_DISMISS_LATCH_MS;
        this.dismissedArtOperations.add(this.activeArtModalOperationId);
        this.finishArtModal(undefined, { restoreToAwaitingChip: true });
    }

    /** Is the discovery modal on screen at all, loading or choosing? */
    isDiscoveryModalOpen() {
        const modal = this.getElementById('discovery-modal');
        return Boolean(modal && !modal.classList.contains('hidden'));
    }

    /* THE CHOOSER OWNS THE SCREEN, SO IT CLOSES EVERY CARD ENLARGEMENT
       (17 Oct 2026). An enlargement sits at the top of the page (z-index
       2147483646) and this modal at 1000. A fusion lands its new card under
       the pointer that dropped it, so the new card's enlargement was usually
       already open when the chooser arrived: it covered the first choices and
       took their clicks. Whatever is open or pending goes when the modal
       appears; showHoverPreview refuses to reveal one while the modal is up. */
    closeCardInspection() {
        this.hoverTimers.forEach((timer) => clearTimeout(timer));
        this.hoverTimers.clear();
        try {
            TouchPreview?.cancelActivePress?.();
        } catch (error) {
            console.warn('[Foundry] Failed to release a tap inspection:', error);
        }
        this.dismissHoverPreview();
        Cards?.clearHoverPreview?.();
    }

    /** Is the appearance chooser currently the thing the player is looking at? */
    isArtChooserOpen() {
        const modal = this.getElementById('discovery-modal');
        if (!modal || modal.classList.contains('hidden')) return false;
        const artOptions = this.getElementById('art-options');
        return Boolean(artOptions && !artOptions.classList.contains('hidden'));
    }

    reopenArtSelection(operationId) {
        const normalized = String(operationId || '').trim();
        if (!normalized) return;
        this.dismissedArtOperations.delete(normalized);
        /* Show the modal on THIS tap, in its loading state, rather than after
           the poll returns. Reopening used to look like the tap had missed —
           nothing happened for a round-trip and then the chooser appeared. The
           poll still owns what fills it in. */
        const modal = this.getElementById('discovery-modal');
        const loading = this.getElementById('modal-loading');
        const result = this.getElementById('modal-result');
        this.closeCardInspection();
        if (modal?.classList.contains('hidden')) {
            modal.classList.remove('hidden');
            modal.setAttribute('aria-hidden', 'false');
            loading?.classList.remove('hidden');
            result?.classList.add('hidden');
        }
        void this.pollForResult(normalized, this.pendingForges.get(normalized));
    }

    /** A motionless pointer release on an awaiting chip is a tap that reopens
     *  its art chooser; any real drag distance keeps normal drag semantics. */
    /**
     * A canvas chip that was pressed and released without moving.
     *
     * Tap inspects; a labelled control acts — the same rule the battle board
     * follows. A chip still waiting on an art choice has a more specific answer
     * (its modal), so that wins; everything else opens the card at full size,
     * which is the affordance players could not find because an overlapping
     * neighbour used to swallow the press into a paid merge.
     */
    handleCanvasChipTap(el, event) {
        if (!el) return;
        const origin = this.canvasPointerOrigin;
        const moved = origin ? Math.hypot(
            Number(event?.clientX ?? origin.x) - origin.x,
            Number(event?.clientY ?? origin.y) - origin.y,
        ) : 0;
        if (moved >= this.pointerDragSlop(event?.pointerType)) return;
        if (el.dataset?.foundryOperationId && el.classList?.contains('awaiting-art-selection')) {
            this.maybeReopenArtSelectionFromTap(el, event);
            return;
        }
        this.openCanvasChipPreview(el);
    }

    /**
     * Open a canvas chip's enlarged card. Touch already has tap inspection
     * through TouchPreview, so its compatibility click must not open a second
     * popup on top of the latched one.
     */
    openCanvasChipPreview(el) {
        if (!el?.isConnected) return;
        if (TouchPreview?.isSuppressingMouse?.()) return;
        const opener = this.chipPreviewOpeners?.get(el);
        if (typeof opener === 'function') opener();
    }

    maybeReopenArtSelectionFromTap(el, event) {
        const operationId = el?.dataset?.foundryOperationId;
        if (!operationId || !el.classList?.contains('awaiting-art-selection')) return;
        const origin = this.canvasPointerOrigin;
        if (!origin) return;
        const distance = Math.hypot(
            Number(event?.clientX ?? origin.x) - origin.x,
            Number(event?.clientY ?? origin.y) - origin.y,
        );
        if (distance >= this.pointerDragSlop(event?.pointerType)) return;
        this.reopenArtSelection(operationId);
    }

    showNextArtModal() {
        if (this.artQueue.length === 0) {
            this.activeArtModal = false;
            this.activeArtModalOperationId = null;
            this.activeArtModalComplete = null;
            return;
        }

        this.activeArtModal = true;
        const next = this.artQueue.shift();
        this.activeArtModalOperationId = next?.operationId || null;
        let completed = false;
        const complete = () => {
            if (completed) return;
            completed = true;
            this.activeArtModal = false;
            this.activeArtModalOperationId = null;
            this.activeArtModalComplete = null;
            this.showNextArtModal();
        };
        this.activeArtModalComplete = complete;
        this.showArtSelection(next.data, next.operationId, next.x, next.y, complete);
    }

    releaseArtConfirmGuard() {
        const confirmBtn = this.getElementById('confirm-btn');
        try {
            MoonfallClickGuard?.forceRelease?.(confirmBtn);
        } catch (_) { }
    }

    focusWithoutScroll(element) {
        if (!element || typeof element.focus !== 'function') return;
        try {
            element.focus({ preventScroll: true });
        } catch (_) {
            element.focus();
        }
    }

    getArtModalFocusableElements() {
        const modal = this.getElementById('discovery-modal');
        if (!modal || modal.classList.contains('hidden')) return [];
        return Array.from(modal.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), '
            + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )).filter((element) => (
            !element.closest('[hidden], .hidden, [aria-hidden="true"]')
            && element.inert !== true
        ));
    }

    focusActiveArtModal() {
        if (!this.activeArtModal || OrientationGuard?.isActive?.()) return;
        const modal = this.getElementById('discovery-modal');
        if (!modal || modal.classList.contains('hidden')) return;
        if (modal.contains(document.activeElement)) return;
        const firstChoice = modal.querySelector('.art-choice[tabindex="0"]');
        const target = firstChoice
            || this.getArtModalFocusableElements()[0]
            || modal.querySelector('.modal-box');
        this.focusWithoutScroll(target);
    }

    activateArtModalAccessibility(operationId) {
        const modal = this.getElementById('discovery-modal');
        if (!modal) return;
        this.releaseArtModalAccessibility();
        this.artModalFocusOperationId = String(operationId || '');
        const focused = document.activeElement;
        this.artModalPreviousFocus = focused && focused !== document.body && !modal.contains(focused)
            ? focused
            : null;
        this.artModalInertRecords = Array.from(modal.parentElement?.children || [])
            .filter((element) => element !== modal)
            .map((element) => ({
                element,
                inert: element.inert === true,
                hadInertAttribute: element.hasAttribute('inert'),
                ariaHidden: element.getAttribute('aria-hidden'),
            }));
        this.artModalInertRecords.forEach(({ element }) => {
            element.inert = true;
            element.setAttribute('inert', '');
            element.setAttribute('aria-hidden', 'true');
        });
        modal.inert = false;
        modal.removeAttribute('inert');
        modal.setAttribute('aria-hidden', 'false');
        this.focusActiveArtModal();
    }

    releaseArtModalAccessibility({ restoreToAwaitingChip = false } = {}) {
        const operationId = this.artModalFocusOperationId;
        this.artModalInertRecords.forEach((record) => {
            const { element } = record;
            if (!element?.isConnected) return;
            element.inert = record.inert;
            if (record.hadInertAttribute) element.setAttribute('inert', '');
            else element.removeAttribute('inert');
            if (record.ariaHidden === null) element.removeAttribute('aria-hidden');
            else element.setAttribute('aria-hidden', record.ariaHidden);
        });
        this.artModalInertRecords = [];

        let target = null;
        if (restoreToAwaitingChip && operationId) {
            target = Array.from(this.root.querySelectorAll('.element.awaiting-art-selection'))
                .find((element) => element.dataset.foundryOperationId === operationId
                    && element.tabIndex >= 0);
        }
        if (!target && this.artModalPreviousFocus?.isConnected) {
            target = this.artModalPreviousFocus;
        }
        this.artModalPreviousFocus = null;
        this.artModalFocusOperationId = null;
        if (target && !OrientationGuard?.isActive?.()) this.focusWithoutScroll(target);
    }

    trapArtModalFocus(event) {
        if (!this.activeArtModal || event?.key !== 'Tab') return;
        if (OrientationGuard?.isActive?.()) return;
        const modal = this.getElementById('discovery-modal');
        if (!modal || modal.classList.contains('hidden')) return;
        const focusable = this.getArtModalFocusableElements();
        if (focusable.length === 0) {
            event.preventDefault();
            this.focusWithoutScroll(modal.querySelector('.modal-box'));
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (!modal.contains(active)) {
            event.preventDefault();
            this.focusWithoutScroll(event.shiftKey ? last : first);
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            this.focusWithoutScroll(first);
        } else if (event.shiftKey && active === first) {
            event.preventDefault();
            this.focusWithoutScroll(last);
        }
    }

    setAwaitingArtSelectionAccessibility(element, operationId, enabled) {
        if (!element) return;
        const existing = this.awaitingArtSelectionA11y.get(element);
        if (existing) {
            element.removeEventListener('keydown', existing.onKeyDown);
            if (!enabled) {
                if (existing.role === null) element.removeAttribute('role');
                else element.setAttribute('role', existing.role);
                if (existing.tabindex === null) element.removeAttribute('tabindex');
                else element.setAttribute('tabindex', existing.tabindex);
                if (existing.ariaLabel === null) element.removeAttribute('aria-label');
                else element.setAttribute('aria-label', existing.ariaLabel);
                this.awaitingArtSelectionA11y.delete(element);
                return;
            }
        }
        if (!enabled) return;
        const normalizedOperationId = String(operationId || '').trim();
        if (!normalizedOperationId) return;
        const record = existing || {
            role: element.getAttribute('role'),
            tabindex: element.getAttribute('tabindex'),
            ariaLabel: element.getAttribute('aria-label'),
        };
        record.onKeyDown = (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.reopenArtSelection(normalizedOperationId);
        };
        element.setAttribute('role', 'button');
        element.tabIndex = 0;
        element.setAttribute('aria-label', uiText(
            'foundry.discovery.awaitingArtSelection',
            'Waiting for an eligible discoverer to choose the final art. Tap this card to choose now — it stays locked until then.',
        ));
        element.addEventListener('keydown', record.onKeyDown);
        this.awaitingArtSelectionA11y.set(element, record);
    }

    abortArtSelectionRequest(operationId) {
        const controller = this.artSelectionRequestControllers.get(operationId);
        if (!controller) return;
        this.artSelectionRequestControllers.delete(operationId);
        this.surfaceRequestControllers.delete(controller);
        controller.abort();
    }

    async submitArtSelection(operationId, artIndex) {
        this.abortArtSelectionRequest(operationId);
        if (!this.surfaceActive || this.destroyed) throw createFoundryAbortError();
        const controller = new AbortController();
        this.artSelectionRequestControllers.set(operationId, controller);
        this.surfaceRequestControllers.add(controller);
        const timeout = setTimeout(
            () => controller.abort(),
            Math.max(1, Number(this.operationRequestTimeoutMs) || 1),
        );
        try {
            const token = await awaitFoundryRequestStage(
                this.user.getIdToken(),
                controller.signal,
            );
            const response = await awaitFoundryRequestStage(fetch(
                `/api/foundry/discoveries/${encodeURIComponent(operationId)}/select-art`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ artIndex }),
                    signal: controller.signal,
                },
            ), controller.signal);
            const result = await awaitFoundryRequestStage(
                response.json().catch(() => ({})),
                controller.signal,
            );
            return { response, result };
        } finally {
            clearTimeout(timeout);
            this.surfaceRequestControllers.delete(controller);
            if (this.artSelectionRequestControllers.get(operationId) === controller) {
                this.artSelectionRequestControllers.delete(operationId);
            }
        }
    }

    finishArtModal(onComplete, { restoreToAwaitingChip = false } = {}) {
        const operationId = this.activeArtModalOperationId;
        if (operationId) this.abortArtSelectionRequest(operationId);
        this.releaseArtConfirmGuard();
        disposeFoundryArtImages(this.getElementById('art-grid'));
        const modal = this.getElementById('discovery-modal');
        modal?.classList.add('hidden');
        modal?.setAttribute('aria-hidden', 'true');
        this.releaseArtModalAccessibility({ restoreToAwaitingChip });
        if (this.artModalReconcileTimer) {
            clearTimeout(this.artModalReconcileTimer);
            this.artModalReconcileTimer = null;
        }
        const complete = typeof onComplete === 'function'
            ? onComplete
            : this.activeArtModalComplete;
        this.activeArtModal = false;
        this.activeArtModalOperationId = null;
        if (this.activeArtModalComplete === complete) {
            this.activeArtModalComplete = null;
        }
        if (complete) complete();
    }

    scheduleArtModalReconciliation(delayMs = 2000) {
        if (this.artModalReconcileTimer) clearTimeout(this.artModalReconcileTimer);
        if (!this.surfaceActive || !this.activeArtModalOperationId) return;
        this.artModalReconcileTimer = setTimeout(() => {
            this.artModalReconcileTimer = null;
            void this.reconcileActiveArtModal();
        }, delayMs);
    }

    /**
     * Read the operation behind the open chooser. `lockConfirm` is for the
     * confirm path's own reads (under the click guard): the background check
     * every 2 s must never touch the button — disabling it for the read made
     * Confirm blink to its disabled look and swallow a tap every 2 s.
     */
    reconcileActiveArtModal(operationId = this.activeArtModalOperationId, { lockConfirm = false } = {}) {
        if (!operationId || this.activeArtModalOperationId !== operationId) {
            return Promise.resolve(false);
        }
        if (
            this.artModalReconcileInFlight
            && this.artModalReconcileOperationId === operationId
        ) {
            /* A background read in flight was taken before the confirm's
               submit: the confirm path waits for it and then asks again, so
               it never answers "not finalized" from a stale read. */
            if (lockConfirm && !this.artModalReconcileLocked) {
                // A read that already finished the chooser (another discoverer
                // chose first) is the answer: asking again would find no
                // chooser and read as "not finalized".
                return this.artModalReconcileInFlight.then((done) => (
                    done === true
                        ? true
                        : this.reconcileActiveArtModal(operationId, { lockConfirm: true })
                ));
            }
            return this.artModalReconcileInFlight;
        }

        const confirmBtn = lockConfirm ? this.getElementById('confirm-btn') : null;
        if (confirmBtn) confirmBtn.disabled = true;
        const reconcile = (async () => {
            try {
                const { response, data } = await this.fetchDiscoveryOperation(operationId, 'Art selection reconciliation', {
                    abortOnHide: true,
                });
                if (!this.surfaceActive || this.activeArtModalOperationId !== operationId) return false;
                if (!response?.ok || !data) {
                    this.scheduleArtModalReconciliation();
                    return false;
                }

                const finalized = data.state === 'complete'
                    || data.state === 'failed'
                    || Boolean(data.finalCardId);
                if (!finalized) {
                    this.scheduleArtModalReconciliation();
                    return false;
                }

                const complete = this.activeArtModalComplete;
                this.finishArtModal(complete);
                const context = this.pendingForges.get(operationId);
                if (context) this.schedulePollForResult(operationId, context, 0);
                return true;
            } catch (err) {
                console.warn('[Foundry] Failed to reconcile art selection:', err);
                if (this.surfaceActive && this.activeArtModalOperationId === operationId) {
                    this.scheduleArtModalReconciliation();
                }
                return false;
            } finally {
                if (this.activeArtModalOperationId === operationId && confirmBtn) {
                    confirmBtn.disabled = !this.root.querySelector('#art-grid .art-choice.selected');
                }
            }
        })();

        const trackedReconcile = reconcile.finally(() => {
            if (this.artModalReconcileInFlight !== trackedReconcile) return;
            this.artModalReconcileInFlight = null;
            this.artModalReconcileOperationId = null;
            this.artModalReconcileLocked = false;
        });
        this.artModalReconcileInFlight = trackedReconcile;
        this.artModalReconcileOperationId = operationId;
        this.artModalReconcileLocked = lockConfirm;
        return trackedReconcile;
    }

    showArtSelection(data, operationId, x, y, onComplete) {
        const artOptions = this.getElementById('art-options');
        const artGrid = this.getElementById('art-grid');
        const confirmBtn = this.getElementById('confirm-btn');
        const retryBtn = this.getElementById('retry-btn');
        const variations = Array.isArray(data.artVariations) ? data.artVariations : [];
        const draft = data.draft && typeof data.draft === 'object' ? data.draft : data;
        const prompt = this.getElementById('art-instructions');
        const modal = this.getElementById('discovery-modal');
        const result = this.getElementById('modal-result');
        const loading = this.getElementById('modal-loading');
        const nameEl = this.getElementById('result-name');
        const descEl = this.getElementById('result-desc');

        this.closeCardInspection();
        modal.classList.remove('hidden');
        modal.setAttribute('aria-hidden', 'false');
        loading.classList.add('hidden');
        result.classList.remove('hidden');
        nameEl.textContent = draft.name || uiText('foundry.discovery.newCard', 'New Card');
        descEl.textContent = draft.description || '';

        disposeFoundryArtImages(artGrid);
        artGrid.innerHTML = '';
        this.releaseArtConfirmGuard();
        confirmBtn.classList.remove('hidden');
        confirmBtn.disabled = true;
        let selectedIndex = null;
        const artChoices = [];

        variations.forEach((art, idx) => {
            const url = normalizeFoundryArtSource(art?.url);
            if (url && art?.status === 'complete') {
                artChoices.push({ art: { ...art, url }, index: idx, source: 'current' });
            }
        });
        artGrid.style.setProperty('--art-choice-count', String(Math.max(1, artChoices.length)));
        artGrid.style.setProperty('--art-choice-columns', String(Math.max(1, Math.min(2, artChoices.length))));

        artChoices.forEach((choice, choiceIndex) => {
            const option = document.createElement('button');
            option.type = 'button';
            option.className = 'art-choice';
            option.dataset.artIndex = `${choice.index}`;
            option.dataset.artSource = choice.source;
            option.setAttribute('role', 'radio');
            option.setAttribute('aria-checked', 'false');
            option.tabIndex = choiceIndex === 0 ? 0 : -1;
            option.setAttribute('aria-label', uiText(
                'foundry.discovery.artChoiceLabel',
                'Art option {{number}}',
                { number: choiceIndex + 1 },
            ));
            const img = document.createElement('img');
            img.draggable = false;
            img.alt = '';
            option.onclick = () => {
                artGrid.querySelectorAll('.art-choice').forEach((item) => {
                    item.classList.remove('selected');
                    item.setAttribute('aria-checked', 'false');
                    item.tabIndex = item === option ? 0 : -1;
                });
                option.classList.add('selected');
                option.setAttribute('aria-checked', 'true');
                selectedIndex = choice.index;
                confirmBtn.disabled = false;
            };
            option.addEventListener('keydown', (event) => {
                const choices = Array.from(artGrid.querySelectorAll('.art-choice'));
                const current = choices.indexOf(option);
                let next = current;
                if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (current + 1) % choices.length;
                else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (current - 1 + choices.length) % choices.length;
                else if (event.key === 'Home') next = 0;
                else if (event.key === 'End') next = choices.length - 1;
                else return;
                event.preventDefault();
                choices[next]?.click();
                choices[next]?.focus({ preventScroll: true });
            });
            option.appendChild(img);
            artGrid.appendChild(option);
            loadFoundryArtImage(img, choice.art.url, {
                fit: draft.type === 'Character' ? 'cover' : 'contain',
            });
        });

        if (artChoices.length > 0) {
            artOptions.classList.remove('hidden');
            if (prompt) {
                const expectedCount = Math.max(
                    variations.length,
                    Number(data.artVariationCount) || 0,
                );
                prompt.textContent = artChoices.length < expectedCount
                    ? uiText(
                        'foundry.discovery.partialVariations',
                        '{{completed}} of {{total}} variations were generated. Choose one of the available results.',
                        { completed: artChoices.length, total: expectedCount },
                    )
                    : uiText(
                        'foundry.discovery.cleanRenderPrompt',
                        'Choose the art you prefer. Confirm Art saves it to the card right away.'
                    );
            }
        } else {
            artOptions.classList.add('hidden');
            confirmBtn.classList.add('hidden');
            this.finishArtModal(onComplete);
            return;
        }

        if (retryBtn) {
            retryBtn.classList.add('hidden');
            retryBtn.disabled = true;
            retryBtn.onclick = null;
        }

        this.scheduleArtModalReconciliation();

        const selectArtAction = async () => {
            if (selectedIndex === null || this.activeArtModalOperationId !== operationId) return;

            try {
                const { response, result } = await this.submitArtSelection(
                    operationId,
                    selectedIndex,
                );
                // Accepted: the selection finalized the card into this
                // player's inventory, whatever the chooser does next.
                if (response.ok && !result.error) announceHoldingsChanged();
                if (!this.surfaceActive || this.activeArtModalOperationId !== operationId) return;
                if (!response.ok || result.error) {
                    const reconciled = await this.reconcileActiveArtModal(operationId, { lockConfirm: true });
                    if (response.status === 409 && reconciled) {
                        this.notifyUser(uiText(
                            'foundry.discovery.finalizedByAnother',
                            'Another discoverer finalized this card. Showing the shared final art.'
                        ));
                        return;
                    }
                    if (!reconciled) {
                        this.notifyUser(result.error || uiText('foundry.discovery.selectArtFailed', 'Failed to select art.'));
                    }
                    return;
                }
            } catch (error) {
                if (!this.surfaceActive || this.activeArtModalOperationId !== operationId) return;
                console.error('Failed to select art:', error);
                const reconciled = await this.reconcileActiveArtModal(operationId, { lockConfirm: true });
                if (!reconciled) {
                    this.notifyUser(uiText('foundry.discovery.selectArtFailed', 'Failed to select art.'));
                }
                return;
            }

            if (!this.surfaceActive || this.activeArtModalOperationId !== operationId) return;
            const reconciled = await this.reconcileActiveArtModal(operationId, { lockConfirm: true });
            if (!reconciled) this.scheduleArtModalReconciliation(250);
            void this.loadStardust();
            void this.consumeAndShowCloudAchievementBanners();
        };

        confirmBtn.onclick = () => {
            const guard = MoonfallClickGuard;
            if (guard && typeof guard.guard === 'function') {
                return guard.guard(confirmBtn, selectArtAction, { cooldownMs: 800 }).catch((err) => {
                    console.error('[Foundry] select-art guarded action failed:', err);
                });
            }
            return selectArtAction().catch((err) => console.error('[Foundry] select-art action failed:', err));
        };
        this.activateArtModalAccessibility(operationId);
    }

    destroy({ persist = true } = {}) {
        if (this.destroyed) return;
        this.destroying = true;
        const finalSave = persist
            ? this.persistWorkspaceState({ force: true, allowDestroying: true })
            : this.workspaceSaveDrainPromise;
        // An unseen canvas read still out hands its own lease back (runCanvasPeek).
        const pendingPeek = this.canvasPeekInFlight?.whole || null;
        const afterSave = Promise.resolve(finalSave).catch(() => undefined);
        const release = (pendingPeek ? afterSave.then(() => pendingPeek).catch(() => undefined) : afterSave)
            .then(() => this.releaseWorkspaceLease());
        FoundryWorkspaceReleasePromise = release;
        void release.finally(() => {
            if (FoundryWorkspaceReleasePromise === release) {
                FoundryWorkspaceReleasePromise = null;
            }
        });
        this.destroyed = true;
        this.inventoryLoadSeq += 1;
        this.settleFirstProjection();
        this.settleFirstView();
        this.hiddenReadControllers.forEach((controller) => controller.abort());
        this.hiddenReadControllers.clear();
        this.markPanelPreparing(false);
        this.handleSurfaceVisibility(false);
        this.eventController.abort();
        this.unsubscribeSurfaceLifecycle?.();
        this.unsubscribeTabLifecycle?.();
        this.unsubscribeSurfaceLifecycle = null;
        this.unsubscribeTabLifecycle = null;
        this.unsubscribeStardustAnnounced?.();
        this.unsubscribeStardustAnnounced = null;
        if (typeof this.authUnsubscribe === 'function') this.authUnsubscribe();
        this.authUnsubscribe = null;
        if (this.authRetryTimer) clearTimeout(this.authRetryTimer);
        if (this.inventorySearchTimer) clearTimeout(this.inventorySearchTimer);
        if (this.workspaceSaveTimer) clearTimeout(this.workspaceSaveTimer);
        if (this.workspaceOpenRetryTimer) clearTimeout(this.workspaceOpenRetryTimer);
        if (this.workspaceRenewTimer) clearTimeout(this.workspaceRenewTimer);
        if (this.workspaceRecoveryTimer) clearTimeout(this.workspaceRecoveryTimer);
        if (this.artModalReconcileTimer) clearTimeout(this.artModalReconcileTimer);
        this.authRetryTimer = null;
        this.workspaceSaveTimer = null;
        this.workspaceOpenRetryTimer = null;
        this.workspaceRenewTimer = null;
        this.workspaceRecoveryTimer = null;
        this.artModalReconcileTimer = null;
        this.surfaceTimers.forEach((timer) => clearTimeout(timer));
        this.surfaceTimers.clear();
        this.artQueue = [];
        this.artReadySoundPlayed.clear();
        disposeFoundryArtImages(this.getElementById('art-grid'));
        this.getElementById('discovery-modal')?.setAttribute('aria-hidden', 'true');
        this.releaseArtModalAccessibility();
        this.activeArtModal = false;
        this.activeArtModalOperationId = null;
        this.activeArtModalComplete = null;
        this.pendingForges.clear();
        this.clearSurfaceTransientState();
    }
}


function autoMountLegacyFoundry() {
    if (!document.getElementById('play-area')) return;
    getFoundry()?.destroy?.({ persist: false });
    setFoundry(new MoonFoundry());
}

if (document.getElementById('play-area')) {
    autoMountLegacyFoundry();
}
document.addEventListener('DOMContentLoaded', autoMountLegacyFoundry);


// Same as moon-exchange: these four were reachable only through the
// classic-script `module.exports` block, and removing that block took their
// only export path with it. Real named exports now.
export {
  FoundryWorkspaceReleasePromise,
  MoonFoundry,
  formatEtaWindow,
  buildFoundryEtaLabel,
  hasRecoverableFoundryProgress,
  calculateInventoryPageCapacity,
};
